import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";

import api from "../../api/axios";
import { isNativeApp, openExternalUrl } from "../../native/runtime";
import { useIsDesktop } from "../../hooks/useIsDesktop";
import { useBodyLock } from "../../hooks/useDisableBodyScroll";
import CreateEventBanner from "./CreateEventBanner";
import EventCard from "./EventCard";
import { buildEventPath } from "../../utils/eventUrl";
import { isLocalDevelopmentHost } from "../../utils/localDev";
import VaultModalCloseButton from "../vault/VaultModalCloseButton";
import SegmentedModeToggle from "../common/SegmentedModeToggle";
import AppModalShell, { AppModalButton } from "../common/AppModalShell";
import { AUTH_GRADIENT_BACKGROUND } from "../../constants/authVisuals";
import { resolveUserAvatarSrc } from "../../utils/userAvatar";
import { useHostFeedbackUnread } from "../../context/HostFeedbackUnreadContext";
import {
  PRESS_HOVER,
  PRESS_TAP,
  PRESS_TRANSITION,
  SUBTLE_PRESS_TAP,
} from "../../utils/motionInteractions";

const EVENT_CREATE_INTRO_TYPES = ["event", "concept"];
const HOST_PANEL_TAB_VALUES = ["overview", "events", "analytics"];
const EMBEDDED_ORGANIZER_CONTROL_WIDTH_CLASS =
  "w-[var(--vg-mobile-content-width)] max-w-[var(--vg-desktop-feed-content-max)] lg:w-full";
const DEFAULT_STRIPE_CONNECT_COUNTRY = "SK";
const ORGANIZER_PAYMENTS_RETURN_TO = "/events/search";
const ORGANIZER_PAYMENTS_RETURN_STATE = {
  audienceMode: "host",
  hostPanelTab: "payments",
};
const FALLBACK_STRIPE_CONNECT_COUNTRIES = [
  { code: "AT", name: "Austria" },
  { code: "SK", name: "Slovakia" },
];

const EVENT_CREATE_INTRO_BACKGROUND_STYLE = {
  backgroundColor: "#050812",
  backgroundImage: AUTH_GRADIENT_BACKGROUND,
  backgroundSize: "100% 100%",
  backgroundPosition: "center top",
  backgroundRepeat: "no-repeat",
};

function AutoLoadMoreSentinel({ onIntersect }) {
  const sentinelRef = useRef(null);

  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) onIntersect();
      },
      { rootMargin: "0px 0px 280px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [onIntersect]);

  return <div ref={sentinelRef} className="h-px" aria-hidden="true" />;
}

export function resolvePaidHostPaymentsFlow({
  requestStatus,
  accessEntitled = false,
  agreementAccepted,
  stripeReadyForOnlineTickets,
  paymentSetupReady,
  billingRequired = false,
  billingReady = false,
  paidEvents = [],
}) {
  const normalizedRequestStatus = String(requestStatus || "").trim().toLowerCase();
  const accessApproved = normalizedRequestStatus === "approved" || Boolean(accessEntitled);
  const hasOpenRequest = !accessApproved && ["open", "in_review"].includes(normalizedRequestStatus);
  const verificationComplete = hasOpenRequest || accessApproved;
  const agreementComplete = accessApproved && Boolean(agreementAccepted);
  const hasCashOnlyPaidEvents = paidEvents.length > 0 && paidEvents.every(
    (event) => String(event?.host_payment_collection_method || "").toLowerCase() === "cash"
  );
  const paymentSetupComplete =
    agreementComplete &&
    (Boolean(paymentSetupReady ?? stripeReadyForOnlineTickets) || hasCashOnlyPaidEvents);
  const billingComplete = paymentSetupComplete && (!billingRequired || Boolean(billingReady) || hasCashOnlyPaidEvents);
  const paidEventsComplete = billingComplete && paidEvents.length > 0;

  return {
    hasOpenRequest,
    accessApproved,
    verificationComplete,
    agreementComplete,
    paymentSetupComplete,
    billingComplete,
    paidEventsComplete,
    complete:
      verificationComplete &&
      agreementComplete &&
      paymentSetupComplete &&
      billingComplete &&
      paidEventsComplete,
  };
}

const COPY = {
  en: {
    title: "Organizer Panel",
    rulesCta: "Rules",
    guideCta: "Instructions",
    closeAria: "Close organizer panel",
    loading: "Updating organizer panel...",
    loadError: "We could not load organizer details right now. Basic host stats are still shown below.",
    tabs: {
      overview: "Overview",
      events: "Events",
      analytics: "Analytics",
    },
    analyticsPanel: {
      eventList: "Event analytics",
      open: "Show analytics",
      close: "Hide analytics",
      loading: "Loading analytics...",
      error: "Could not load analytics for this event.",
      retry: "Try again",
      noEvents: "No events to analyze yet.",
      previewError: "Could not load the card-open counts.",
    },
    hostScore: {
      label: "Host score",
      unrated: "No event ratings yet",
      basedOn: "Based on {{count}} ratings across {{events}} rated events",
      attendance: "Attendance confirmation rate",
      attendanceHint: "{{confirmed}} confirmed out of {{registered}} participants at event start",
    },
    sections: {
      performance: "Performance",
      attendance: "Attendance",
      analytics: "Reach",
      recentEvents: "Events",
      payments: "VaultGrid payments",
      paidEvents: "Paid events",
      commercial: "Commercial conditions",
      payouts: "Payout overview",
      limits: "Creation limits",
    },
    metrics: {
      slotsUsed: "Slots used",
      slotsAvailable: "Slots available",
      totalCreated: "Total created",
      activeEvents: "Active events",
      activeConcepts: "Active concepts",
      finished: "Successfully finished",
      canceled: "Canceled",
      postponed: "Postponed",
      registered: "Registered at start",
      confirmed: "Confirmed attendance",
      manualConfirmed: "Manual confirmations",
      attendanceRate: "Attendance rate",
      availableSlots: "Slots available",
      eventSlotsLeft: "Slots available",
      views: "Card views",
      opens: "Card opens",
      uniqueViewers: "Unique viewers",
      uniqueOpeners: "Unique openers",
      paidEventsCount: "Paid events",
      paidTicketsSold: "Paid tickets sold",
      depositPayments: "Deposits received",
      refundedTickets: "Refunded tickets",
      depositRevenue: "Deposit amount",
      grossRevenue: "Gross revenue",
      refundedAmount: "Refunds",
      buyerProcessingFees: "Earlier buyer processing fees",
      organizerProcessingTariff: "Organizer card-processing tariff",
      platformFeeRetained: "Platform commission",
      heldBalance: "Payout on hold",
      releasedBalance: "Released balance",
      pendingPayout: "Estimated payout",
      cashDepositReserved: "Cash deposits held",
      cashCommissionSettled: "Cash fees settled",
      cashDepositReleased: "Cash deposit released",
      cashOutstanding: "Cash outstanding",
    },
    eventCard: {
      paid: "Paid",
      free: "Free",
      activeOnly: "Active",
      finishedOnly: "Finished",
      conceptsOnly: "Concepts",
      currentParticipants: "Current participants",
      registeredAtStart: "Registered at start",
      confirmedAttendance: "Confirmed attendance",
      attendanceRate: "Attendance rate",
      eventRating: "Event rating",
      participantFeedback: "Participant feedback",
      noEvents: "No created events yet.",
      noEventsFiltered: "No events in this category yet.",
      ratingsSuffix: "ratings",
      showMore: "Show more",
    },
    feedbackModal: {
      title: "Participant feedback",
      close: "Close participant feedback",
      loading: "Loading anonymous feedback...",
      error: "We could not load participant feedback right now.",
      retry: "Try again",
      anonymousParticipant: "Anonymous participant",
      empty: "No anonymous feedback has been left for this event.",
    },
    safetyReports: {
      title: "Unregistered participant reports",
      countLabel: "Participant reports: {{count}}",
      body:
        "Participants reported that people who were not on the VaultGrid participant list attended this event.",
      guidance:
        "Please review how attendance was managed. Everyone attending an event must be registered on VaultGrid and joined to the event.",
      viewRules: "Review event organization rules",
      closeRules: "Close rules",
    },
    statuses: {
      active: "Active",
      finished: "Finished",
      cancelled: "Cancelled",
      postponed: "Postponed",
      pending: "Pending",
      concept: "Concept",
      event: "Event",
    },
    payments: {
      topTitle: "VaultGrid payments",
      hostGuideTitle: "Discover hosting on VaultGrid",
      hostGuideBody: "Explore the tools for organizers, from building a community to selling tickets.",
      hostGuideCta: "Open Host Guide",
      body:
        "Monetize your hosted events on VaultGrid and sell tickets directly through your event page.",
      stepsTitle: "Launch flow",
      steps: [
        "1. Send a verification request for paid host events.",
        "2. After approval, review and accept the paid host agreement.",
        "3. Set up how you will receive ticket payments.",
        "4. Confirm your invoice details.",
        "5. Launch ticket sales and start monetizing your hosted events on VaultGrid.",
      ],
      stripeStatusLabel: "Stripe connection",
      tbankStatusLabel: "T-Bank payouts",
      requestStatusLabel: "Host verification",
      commercialIntro:
        "Review how platform commissions, held balances, payout release rules, and paid host event conditions will work on VaultGrid.",
      commercialCta: "Commercial conditions",
      commercialComingSoon: "Coming soon",
      commercialTitle: "Commercial conditions",
      commercialPoints: [
        "Hosts will be able to sell tickets for hosted paid events through VaultGrid.",
        "VaultGrid will retain a platform commission from each successful sale.",
        "Funds will remain on hold until the event is completed and release conditions are met.",
        "Detailed percentages, payout timing, and dispute rules will be published here before launch.",
      ],
      paidEventsEmpty: "You do not have any paid events yet.",
      payoutStatusTitle: "Payout status",
      payoutStatuses: {
        scheduled: "Waiting for release",
        blocked: "Blocked",
        processing: "Bank payout initiated",
        released: "Paid to bank",
        failed: "Action required",
      },
      payoutReleaseAfter: "VaultGrid dispute window ends {{date}}",
      payoutStripeFundsAvailable: "Stripe currently expects the card funds to clear on {{date}}",
      payoutProcessingDetail: "Stripe and the receiving bank are processing the transfer.",
      payoutProcessingGeneric: "The payment provider and receiving bank are processing the transfer.",
      payoutReleasedAt: "Stripe confirmed the bank payout on {{date}}",
      payoutReleasedGeneric: "The bank payout was confirmed on {{date}}",
      payoutBlockedHint: "The payout is paused. Open disputes or payment account restrictions must be resolved first.",
      payoutStripeReference: "Stripe payout {{id}}",
      payoutProviderReference: "Payout reference {{id}}",
      payoutInfoAria: "Explain payout status",
      payoutInfoTitle: "How this payout works",
      payoutInfoIntro:
        "Your payout passes through separate VaultGrid and Stripe stages. The dates can differ: the 72-hour VaultGrid complaint period does not shorten Stripe's card settlement time or the bank's transfer time.",
      payoutBreakdownTitle: "Estimated payout",
      payoutGrossTickets: "Ticket revenue",
      payoutRefunds: "Refunded ticket revenue",
      payoutBuyerFees: "Processing fees on earlier purchases",
      payoutPlatformCommission: "VaultGrid commission",
      payoutOrganizerProcessingTariff: "Card-processing tariff",
      payoutEstimatedTotal: "Estimated payout to organizer",
      payoutStripeFeeNote:
        "For new purchases, buyers pay only the ticket price. VaultGrid invoices the organizer for the disclosed card-processing tariff; Stripe's actual charge to VaultGrid is recorded separately.",
      payoutHistoricalFeeNote:
        "Earlier buyers paid {{amount}} in separate processing fees. These amounts remain in the historical settlement and are tracked separately from Stripe's actual processing costs.",
      payoutTimelineTitle: "Payout timeline",
      payoutTimeline: [
        "Ticket paid. The card payment is recorded on your connected Stripe account. Stripe may still show the funds as pending until card settlement is complete.",
        "Event completed. VaultGrid starts a 72-hour complaint period. An open dispute pauses the payout until it is resolved.",
        "Stripe settlement. Even after the VaultGrid window ends, card funds may remain pending until Stripe's displayed availability date.",
        "Payout initiated. As soon as both conditions are met, VaultGrid automatically instructs Stripe to send the eligible amount to your bank. No action is required from you.",
        "Bank transfer. Stripe and your bank process the payout. The arrival date depends on Stripe, banking days, currency, country, and your bank.",
      ],
      payoutTimingNote:
        "Dates shown are current estimates, not guaranteed arrival dates. Stripe, banks, compliance checks, reserves, refunds, chargebacks, or disputes may extend the timeline.",
      payoutInfoClose: "Close",
      paymentTermsTitle: "Ticket terms",
      ticketPrice: "Ticket price",
      upfrontPayment: "Paid on join",
      remainingPayment: "Remaining balance",
      balanceDue: "Balance request",
      balanceDueDays: "{{days}} days before event",
      noBalanceDue: "No remaining balance",
      cancellationPolicy: "Cancellation policy",
      cancellationPolicies: {
        flexible: "Flexible",
        moderate: "Moderate",
        strict: "Strict",
      },
      paidByTitle: "Ticket records",
      noPaidParticipants: "No ticket records yet.",
      lastPayment: "Last payment",
      newDeposits: "{{count}} new",
      viewDetails: "Payment details",
      hideDetails: "Hide details",
      openEvent: "Open event",
      metricHelpTitle: "What these numbers mean",
      metricsTitle: "Ticket payments",
      chargesTitle: "Fees charged",
      metricHelpAria: "Explain ticket payment figures",
      metricHelp: {
        paidTicketsSold: "Number of tickets with a successful payment.",
        depositPayments: "Number of ticket payments made as a deposit.",
        refundedTickets: "Number of tickets with a full or partial refund.",
        refundedAmount: "Total amount returned to buyers.",
        grossRevenue: "Total ticket payments received before refunds and fees.",
        depositRevenue: "Amount collected as ticket deposits; this is included in gross revenue.",
        platformFeeRetained: "VaultGrid commission on eligible ticket revenue.",
        organizerProcessingTariff: "Card-processing tariff charged to the organizer for applicable purchases.",
        pendingPayout: "Current estimate of the amount still due to the organizer after refunds and applicable fees; it can change before release.",
        releasedBalance: "Amount already released for payout to the organizer.",
      },
      refundedValueLabel: "Refunded {{value}}",
      hostBalanceValueLabel: "Host balance {{value}}",
      rowStatuses: {
        paid: "Paid",
        deposit_received: "Deposit received",
        refunded: "Refunded",
        partially_refunded: "Partially refunded",
        cancelled: "Cancelled",
        cash_deposit: "Cash deposit",
      },
      connectCta: "Connect payment account",
      stripeNewAccountNotice: "This creates a new Stripe Standard account for VaultGrid. An existing Stripe account outside VaultGrid cannot be linked here.",
      stripeCountryModalTitle: "Business country",
      stripeCountryModalBody:
        "Choose where your company or sole proprietorship is registered. Stripe uses this country to open the correct onboarding form.",
      stripeCountryLabel: "Registered business country",
      stripeCountryLockedHint:
        "This country cannot be changed after the Stripe connected account is created.",
      stripeCountryReviewedHint:
        "This is the legal entity country approved in your host application. Contact VaultGrid before continuing if it needs correction.",
      stripeCountryContinue: "Continue to Stripe",
      stripeCountryCancel: "Cancel",
      agreementCta: "Accept paid host agreement",
      reviewAgreementCta: "Read paid host agreement",
      billingCta: "Confirm invoice details",
      billingHelp: "Stripe is connected. Confirm your business details before selling card tickets.",
      agreementAcceptedCta: "Paid host agreement accepted",
      verificationTermsCta: "Verification terms",
      continueCta: "Continue Stripe setup",
      dashboardCta: "Open Stripe Dashboard",
      refreshCta: "Refresh status",
      tbankReadyHelp:
        "T-Bank payouts are connected. Create your first paid host event to launch RUB ticket sales.",
      tbankDirectReadyHelp:
        "T-Bank acquiring is connected. Ticket payments are collected by VOLGRID VOSTOK in rubles; organizer payouts are configured separately.",
      tbankPendingHelp:
        "The T-Bank recipient profile is being configured. Online ticket sales will become available after payout details are verified.",
      tbankMissingHelp:
        "A T-Bank recipient profile is required for online ticket sales. Contact VaultGrid support to start verification.",
      tbankRecipientTypeLabel: "Recipient type",
      tbankRecipientTypes: {
        sole_proprietor: "Sole proprietor",
        legal_entity: "Legal entity",
      },
      tbankCreateProfileCta: "Create recipient profile",
      tbankBindCardCta: "Link payout card",
      tbankSyncCardCta: "Check linked card",
      tbankProfileCreated: "Recipient profile created. VaultGrid will verify it with T-Bank.",
      tbankBindingStarted: "Complete card linking in the T-Bank page that opened.",
      managementHelp:
        "Track every payout stage in the Payments tab. Stripe Dashboard shows payment and balance data, but VaultGrid controls when event funds are released to your bank.",
      stripeConnectedHelp:
        "Your payment account is connected. Create your first paid host event to launch ticket sales on VaultGrid.",
      stripeOnboardingHelp:
        "Your payment account has been created. Finish Stripe setup to enable dashboard access and online ticket sales.",
      billingSavedHelp: "Your invoice details are saved.",
      stripeReviewHelp:
        "Stripe is reviewing your payment account. Payouts are not enabled yet. Check Stripe Dashboard for any requests; if there are none, contact VaultGrid support.",
      requestAccessCta: "Request host verification",
      requestPendingCta: "Waiting for review",
      opening: "Opening Stripe...",
      refreshing: "Refreshing...",
      requestSubmitting: "Sending request...",
      requestSent: "Your request has been sent. We will review it before enabling paid host events.",
      requestAlreadyOpen: "You already have an open verification request for paid host events.",
      requestOpenLabel: "Open",
      requestInReviewLabel: "In review",
      requestApprovedLabel: "Approved",
      requestRejectedLabel: "Rejected",
      requestNoneLabel: "Not requested yet",
      requestHelp:
        "To start monetizing events on VaultGrid, first submit your organizer verification request. After approval, paid host events and payment setup will become available in this section.",
      approvedHelp:
        "Your organizer verification is approved. Before enabling paid host flows, please review and accept the paid host agreement.",
      agreementHelp:
        "After you accept the paid host agreement, you can continue with Stripe setup for online ticket sales or proceed with cash collection for host-paid events.",
      agreementUpdatedHelp:
        "The paid host agreement has been updated. Review and accept the current version to continue.",
      formTitle: "Request paid host verification",
      formBody:
        "Tell us about your organizer profile, the kinds of events you want to monetize, and how you plan to accept payments.",
      formLegalHint:
        "Before sending your request, review the verification terms and the commercial conditions for paid host access.",
      requestConsentLabel:
        "I confirm that the information in this request is accurate and complete, and I agree to the Paid Host Verification Terms.",
      socialLabel: "Main social profile",
      socialPlaceholder: "Instagram, TikTok, Telegram channel, Facebook page, or another public profile",
      businessSocialLabel: "Business social profile",
      businessSocialPlaceholder: "Optional business page, studio account, brand profile, or website",
      businessCountryLabel: "Country where your business is registered *",
      businessCountryPlaceholder: "Select a country",
      businessTypeLabel: "Business type *",
      businessTypePlaceholder: "Select a business type",
      businessTypeSoleTrader: "Sole trader",
      businessTypeCompany: "Company",
      businessLegalNameLabel: "Registered business name *",
      businessLegalNamePlaceholder: "Legal name on your business registration",
      ageLabel: "Your age",
      agePlaceholder: "Enter your age",
      phoneLabel: "Phone number",
      phonePlaceholder: "Enter your contact phone number",
      eventTypesLabel: "What types of events do you want to host?",
      eventTypesPlaceholder: "For example: workshops, sports sessions, networking events, private clubs...",
      paymentMethodLabel: "How do you want to accept payments?",
      paymentMethodCash: "Cash",
      paymentMethodCard: "Card only",
      paymentMethodBoth: "Cash and card",
      notesLabel: "Additional notes",
      notesPlaceholder: "Optional details that help us understand your hosting model.",
      submitRequestCta: "Send request",
      closeRequestCta: "Close",
      statuses: {
        not_connected: "Not connected",
        onboarding_required: "Onboarding required",
        restricted: "Restricted",
        connected: "Connected",
        error: "Connection error",
      },
    },
    limits: {
      cardTitle: "Unlock unlimited creation",
      cardBody:
        "You can keep up to 3 active creations in parallel. Switch to Premium to unlock unlimited events and concepts on VaultGrid.",
      cardButtonLabel: "Open Premium",
      modalTitleSlots: "All host creation slots are used",
      modalBodySlots:
        "Switch to Premium to unlock unlimited events and concepts on VaultGrid.",
      modalCapLabel: "Current creation limit",
      modalParallelLimit: "Maximum 3 active creations in parallel",
      debugTitle: "Local debug",
      debugLive: "Live",
      debugSlotsFull: "Slots full",
    },
    buttons: {
      backToCreation: "Event Creation",
      openPremium: "Open VaultGrid Premium",
      maybeLater: "Maybe later",
    },
  },
  ru: {
    title: "Панель организатора",
    rulesCta: "Правила",
    guideCta: "Инструкции",
    closeAria: "Закрыть панель организатора",
    loading: "Обновляем панель организатора...",
    loadError:
      "Сейчас не удалось загрузить детали панели организатора. Ниже всё равно показана базовая статистика хоста.",
    tabs: {
      overview: "Обзор",
      events: "События",
      analytics: "Аналитика",
    },
    analyticsPanel: {
      eventList: "Аналитика событий",
      open: "Показать аналитику",
      close: "Скрыть аналитику",
      loading: "Загружаем аналитику...",
      error: "Не удалось загрузить аналитику события.",
      retry: "Повторить",
      noEvents: "Пока нет событий для анализа.",
      previewError: "Не удалось загрузить число открытий карточек.",
    },
    hostScore: {
      label: "Рейтинг организатора",
      unrated: "Оценок мероприятий пока нет",
      basedOn: "Основано на {{count}} оценках по {{events}} оценённым мероприятиям",
      attendance: "Процент подтверждения участия",
      attendanceHint: "{{confirmed}} подтверждений из {{registered}} участников на момент старта",
    },
    sections: {
      performance: "Результаты",
      attendance: "Подтверждение участия",
      analytics: "Охват",
      recentEvents: "События",
      payments: "Платежи VaultGrid",
      paidEvents: "Платные события",
      commercial: "Коммерческие условия",
      payouts: "Выплаты",
      limits: "Лимиты создания",
    },
    metrics: {
      slotsUsed: "Слотов занято",
      slotsAvailable: "Слотов доступно",
      totalCreated: "Всего создано",
      activeEvents: "Активные ивенты",
      activeConcepts: "Активные концепты",
      finished: "Успешно завершено",
      canceled: "Отменено",
      postponed: "Перенесено",
      registered: "Участников на старте",
      confirmed: "Подтвердили участие",
      manualConfirmed: "Ручные подтверждения",
      attendanceRate: "Подтверждено",
      availableSlots: "Слотов доступно",
      eventSlotsLeft: "Слотов доступно",
      views: "Просмотры карточек",
      opens: "Открытия карточек",
      uniqueViewers: "Уникальные зрители",
      uniqueOpeners: "Уникальные открытия",
      paidEventsCount: "Платных событий",
      paidTicketsSold: "Продано билетов",
      depositPayments: "Получено депозитов",
      refundedTickets: "Возвращено билетов",
      depositRevenue: "Сумма депозитов",
      grossRevenue: "Общий оборот",
      refundedAmount: "Возвраты",
      buyerProcessingFees: "Сборы с покупателей по прежним покупкам",
      organizerProcessingTariff: "Сбор за обработку карт с организатора",
      platformFeeRetained: "Комиссия платформы",
      heldBalance: "Выплата удерживается",
      releasedBalance: "Выплачено",
      pendingPayout: "Расчётная выплата",
      cashDepositReserved: "Cash-депозиты",
      cashCommissionSettled: "Cash-комиссии",
      cashDepositReleased: "Возвращено с cash-депозита",
      cashOutstanding: "Недоплата cash",
    },
    eventCard: {
      paid: "Платный",
      free: "Бесплатный",
      activeOnly: "Активные",
      finishedOnly: "Завершённые",
      conceptsOnly: "Концепты",
      currentParticipants: "Сейчас участников",
      registeredAtStart: "Было на старте",
      confirmedAttendance: "Подтвердили участие",
      attendanceRate: "Подтверждено",
      eventRating: "Оценка события",
      participantFeedback: "Отзывы участников",
      noEvents: "Созданных событий пока нет.",
      noEventsFiltered: "В этой категории событий пока нет.",
      ratingsSuffix: "оценок",
      showMore: "Показать ещё",
    },
    feedbackModal: {
      title: "Отзывы участников",
      close: "Закрыть отзывы участников",
      loading: "Загружаем анонимные отзывы...",
      error: "Сейчас не удалось загрузить отзывы участников.",
      retry: "Повторить",
      anonymousParticipant: "Анонимный участник",
      empty: "К этому событию пока нет анонимных отзывов.",
    },
    safetyReports: {
      title: "Репорты о незарегистрированных участниках",
      countLabel: "Репортов участников: {{count}}",
      body:
        "Участники сообщили, что на этом ивенте присутствовали люди, которых не было в списке участников VaultGrid.",
      guidance:
        "Пожалуйста, проверьте, как контролировалось участие. Каждый присутствующий должен быть зарегистрирован на VaultGrid и присоединён к ивенту.",
      viewRules: "Просмотреть правила организации ивентов",
      closeRules: "Закрыть правила",
    },
    statuses: {
      active: "Активно",
      finished: "Завершено",
      cancelled: "Отменено",
      postponed: "Перенесено",
      pending: "В ожидании",
      concept: "Концепт",
      event: "Ивент",
    },
    payments: {
      topTitle: "Платежи VaultGrid",
      hostGuideTitle: "Возможности для организаторов",
      hostGuideBody: "Узнайте, как развивать сообщество и продавать билеты через VaultGrid.",
      hostGuideCta: "Открыть гайд организатора",
      body:
        "Монетизируйте свои события в VaultGrid и продавайте билеты прямо через страницу события.",
      stepsTitle: "Как это запускается",
      steps: [
        "1. Отправьте запрос на верификацию для платных hosted events.",
        "2. После одобрения прочитайте и примите paid host agreement.",
        "3. Настройте способ получения оплаты за билеты.",
        "4. Подтвердите данные для фактуры.",
        "5. Запускайте продажи билетов и начинайте монетизацию своих событий в VaultGrid.",
      ],
      stripeStatusLabel: "Подключение Stripe",
      tbankStatusLabel: "Выплаты через Т‑Банк",
      requestStatusLabel: "Верификация хоста",
      commercialIntro:
        "Здесь будут опубликованы коммерческие условия: комиссия платформы, правила удержания средств, release выплат и базовые условия платных hosted events.",
      commercialCta: "Коммерческие условия",
      commercialComingSoon: "Скоро",
      commercialTitle: "Коммерческие условия",
      commercialPoints: [
        "Хосты смогут продавать билеты на платные hosted events через VaultGrid.",
        "VaultGrid будет удерживать платформенную комиссию с каждой успешной продажи.",
        "Средства будут удерживаться до завершения события и выполнения условий release.",
        "Точные проценты, сроки выплат и правила dispute/refund будут опубликованы здесь до запуска.",
      ],
      paidEventsEmpty: "У вас пока нет платных ивентов.",
      payoutStatusTitle: "Статус выплаты",
      payoutStatuses: {
        scheduled: "Ожидает выплаты",
        blocked: "Заблокирована",
        processing: "Перевод в банк начат",
        released: "Выплачена в банк",
        failed: "Требует внимания",
      },
      payoutReleaseAfter: "Окно для споров VaultGrid завершится {{date}}",
      payoutStripeFundsAvailable: "Сейчас Stripe ожидает завершение обработки карточных средств {{date}}",
      payoutProcessingDetail: "Stripe и банк-получатель обрабатывают перевод.",
      payoutProcessingGeneric: "Платёжный провайдер и банк-получатель обрабатывают перевод.",
      payoutReleasedAt: "Stripe подтвердил выплату в банк {{date}}",
      payoutReleasedGeneric: "Выплата в банк подтверждена {{date}}",
      payoutBlockedHint: "Выплата приостановлена. Сначала нужно разрешить открытые споры или ограничения платёжного аккаунта.",
      payoutStripeReference: "Stripe payout {{id}}",
      payoutProviderReference: "Идентификатор выплаты {{id}}",
      payoutInfoAria: "Объяснить статус выплаты",
      payoutInfoTitle: "Как работает эта выплата",
      payoutInfoIntro:
        "Выплата проходит отдельные этапы VaultGrid и Stripe. Эти сроки могут не совпадать: 72-часовой срок VaultGrid для жалоб не сокращает срок обработки карточного платежа Stripe и срок банковского перевода.",
      payoutBreakdownTitle: "Расчёт выплаты",
      payoutGrossTickets: "Выручка от билетов",
      payoutRefunds: "Возвраты по билетам",
      payoutBuyerFees: "Сборы за обработку по прежним покупкам",
      payoutPlatformCommission: "Комиссия VaultGrid",
      payoutOrganizerProcessingTariff: "Сбор за обработку карт",
      payoutEstimatedTotal: "Ожидаемая выплата организатору",
      payoutStripeFeeNote:
        "При новых покупках покупатели платят только цену билета. VaultGrid выставляет организатору счёт за объявленный сбор по карточным платежам; фактический расход Stripe учитывается отдельно.",
      payoutHistoricalFeeNote:
        "По прежним покупкам покупатели заплатили отдельные сборы за обработку на {{amount}}. Эти суммы учитываются в прежних расчётах отдельно от фактических расходов Stripe.",
      payoutTimelineTitle: "Этапы выплаты",
      payoutTimeline: [
        "Билет оплачен. Карточный платёж записан на вашем подключённом аккаунте Stripe. До завершения обработки Stripe средства могут отображаться как pending.",
        "Событие завершено. VaultGrid запускает 72-часовой срок для жалоб. Открытый спор приостанавливает выплату до его разрешения.",
        "Обработка Stripe. Даже после окончания окна VaultGrid карточные средства могут оставаться pending до даты доступности, указанной Stripe.",
        "Выплата запущена. Как только оба условия выполнены, VaultGrid автоматически поручает Stripe отправить доступную сумму в ваш банк. От вас ничего не требуется.",
        "Банковский перевод. Stripe и банк обрабатывают выплату. Срок поступления зависит от Stripe, банковских дней, валюты, страны и вашего банка.",
      ],
      payoutTimingNote:
        "Показанные даты являются текущей оценкой, а не гарантированной датой поступления. Stripe, банк, compliance-проверки, резервы, возвраты, chargeback или спор могут увеличить срок.",
      payoutInfoClose: "Закрыть",
      paymentTermsTitle: "Условия билета",
      ticketPrice: "Стоимость билета",
      upfrontPayment: "Оплата при входе",
      remainingPayment: "Остаток",
      balanceDue: "Запрос остатка",
      balanceDueDays: "за {{days}} дня до события",
      noBalanceDue: "Остатка к оплате нет",
      cancellationPolicy: "Условия сторно",
      cancellationPolicies: {
        flexible: "Гибкие",
        moderate: "Умеренные",
        strict: "Строгие",
      },
      paidByTitle: "Билетные записи",
      noPaidParticipants: "Записей по билетам пока нет.",
      lastPayment: "Последняя оплата",
      newDeposits: "{{count}} новых",
      viewDetails: "Детали оплат",
      hideDetails: "Скрыть детали",
      openEvent: "Открыть ивент",
      metricHelpTitle: "Что означают показатели",
      metricsTitle: "Платежи за билеты",
      chargesTitle: "Начисленные комиссии",
      metricHelpAria: "Пояснить показатели продаж билетов",
      metricHelp: {
        paidTicketsSold: "Количество билетов с успешной оплатой.",
        depositPayments: "Количество билетов, по которым внесён депозит.",
        refundedTickets: "Количество билетов с полным или частичным возвратом.",
        refundedAmount: "Общая сумма, возвращённая покупателям.",
        grossRevenue: "Все полученные платежи за билеты до возвратов и комиссий.",
        depositRevenue: "Сумма внесённых депозитов; она уже входит в общий оборот.",
        platformFeeRetained: "Комиссия VaultGrid с подходящей выручки от билетов.",
        organizerProcessingTariff: "Сбор за обработку карточных платежей, начисленный организатору.",
        pendingPayout: "Текущая оценка суммы к выплате организатору после возвратов и применимых сборов; до отправки она может измениться.",
        releasedBalance: "Сумма, уже отправленная на выплату организатору.",
      },
      refundedValueLabel: "Возврат {{value}}",
      hostBalanceValueLabel: "Баланс хоста {{value}}",
      rowStatuses: {
        paid: "Оплачен",
        deposit_received: "Получен депозит",
        refunded: "Возвращён",
        partially_refunded: "Частичный возврат",
        cancelled: "Отменён",
        cash_deposit: "Cash-депозит",
      },
      connectCta: "Подключить платёжный аккаунт",
      stripeNewAccountNotice: "Здесь создаётся новый аккаунт Stripe Standard для VaultGrid. Уже существующий аккаунт Stripe вне VaultGrid этим способом подключить нельзя.",
      stripeCountryModalTitle: "Страна бизнеса",
      stripeCountryModalBody:
        "Выберите страну, где зарегистрирована ваша компания или предпринимательство. Stripe использует эту страну, чтобы открыть правильную форму онбординга.",
      stripeCountryLabel: "Страна регистрации бизнеса",
      stripeCountryLockedHint:
        "После создания Stripe connected account эту страну уже нельзя изменить.",
      stripeCountryReviewedHint:
        "Это страна регистрации, проверенная в вашей заявке хоста. Если её нужно исправить, обратитесь в VaultGrid до продолжения.",
      stripeCountryContinue: "Продолжить в Stripe",
      stripeCountryCancel: "Отмена",
      agreementCta: "Принять соглашение организатора платных событий",
      reviewAgreementCta: "Прочитать соглашение организатора",
      billingCta: "Подтвердить данные для фактуры",
      billingHelp: "Stripe подключён. Подтвердите данные предпринимателя перед продажей билетов с оплатой картой.",
      agreementAcceptedCta: "Paid host agreement принят",
      verificationTermsCta: "Условия верификации",
      continueCta: "Продолжить настройку Stripe",
      dashboardCta: "Открыть Stripe Dashboard",
      refreshCta: "Обновить статус",
      tbankReadyHelp:
        "Выплаты через Т‑Банк подключены. Создайте первое платное событие и запустите продажу билетов в рублях.",
      tbankDirectReadyHelp:
        "Интернет-эквайринг Т‑Банка подключён. Оплату билетов в рублях принимает ООО «ВОЛГРИД ВОСТОК»; выплаты организаторам настраиваются отдельно.",
      tbankPendingHelp:
        "Профиль получателя Т‑Банка настраивается. Онлайн-продажа билетов станет доступна после проверки реквизитов для выплат.",
      tbankMissingHelp:
        "Для онлайн-продажи билетов нужен профиль получателя Т‑Банка. Обратитесь в поддержку VaultGrid, чтобы начать проверку.",
      tbankRecipientTypeLabel: "Тип получателя",
      tbankRecipientTypes: {
        sole_proprietor: "ИП",
        legal_entity: "Юридическое лицо",
      },
      tbankCreateProfileCta: "Создать профиль получателя",
      tbankBindCardCta: "Привязать карту для выплат",
      tbankSyncCardCta: "Проверить привязанную карту",
      tbankProfileCreated: "Профиль получателя создан. VaultGrid проверит его вместе с Т‑Банком.",
      tbankBindingStarted: "Завершите привязку карты на открывшейся странице Т‑Банка.",
      managementHelp:
        "Отслеживайте этапы выплаты во вкладке «Платежи». Stripe Dashboard показывает платежи и баланс, но момент перевода средств за событие в банк контролирует VaultGrid.",
      stripeConnectedHelp:
        "Ваш платёжный аккаунт подключён. Создайте первое paid host событие, чтобы запустить продажу билетов в VaultGrid.",
      stripeOnboardingHelp:
        "Ваш платёжный аккаунт создан. Завершите настройку Stripe, чтобы открыть dashboard и включить онлайн-продажу билетов.",
      billingSavedHelp: "Данные для фактуры сохранены.",
      stripeReviewHelp:
        "Stripe проверяет ваш платёжный аккаунт. Выплаты пока недоступны. Проверьте запросы в Stripe Dashboard; если их нет, обратитесь в поддержку VaultGrid.",
      requestAccessCta: "Запросить верификацию хоста",
      requestPendingCta: "Ожидает проверки",
      opening: "Открываем Stripe...",
      refreshing: "Обновляем...",
      requestSubmitting: "Отправляем запрос...",
      requestSent: "Запрос отправлен. Мы проверим его перед включением платных hosted events.",
      requestAlreadyOpen: "У вас уже есть открытый запрос на верификацию для платных hosted events.",
      requestOpenLabel: "Открыт",
      requestInReviewLabel: "На проверке",
      requestApprovedLabel: "Одобрено",
      requestRejectedLabel: "Отклонено",
      requestNoneLabel: "Запрос ещё не отправлен",
      requestHelp:
        "Чтобы начать монетизировать события в VaultGrid, сначала отправьте запрос на верификацию профиля организатора. После одобрения в этом разделе откроются платные host_paid события и настройка приёма платежей.",
      approvedHelp:
        "Верификация организатора одобрена. Перед запуском paid host flow, пожалуйста, прочитайте и примите paid host agreement.",
      agreementHelp:
        "После принятия paid host agreement вы сможете продолжить настройку Stripe для онлайн-продажи билетов или перейти к модели с наличными для host-paid событий.",
      agreementUpdatedHelp:
        "Соглашение для организаторов платных событий обновилось. Прочитайте и примите актуальную версию, чтобы продолжить.",
      formTitle: "Запрос на верификацию хоста",
      formBody:
        "Расскажите о своём профиле организатора, какие события хотите монетизировать и как планируете принимать оплату.",
      formLegalHint:
        "Перед отправкой заявки ознакомьтесь с условиями верификации и коммерческими условиями для paid host доступа.",
      requestConsentLabel:
        "Подтверждаю, что информация в этой заявке точная и полная, и я согласен с условиями Paid Host Verification Terms.",
      socialLabel: "Основной социальный профиль",
      socialPlaceholder: "Instagram, TikTok, Telegram-канал, Facebook-страница или другой публичный профиль",
      businessSocialLabel: "Социальный профиль бизнеса",
      businessSocialPlaceholder: "Необязательная страница бизнеса, студии, бренда или сайт",
      businessCountryLabel: "Страна регистрации бизнеса *",
      businessCountryPlaceholder: "Выберите страну",
      businessTypeLabel: "Форма деятельности *",
      businessTypePlaceholder: "Выберите форму деятельности",
      businessTypeSoleTrader: "Предприниматель",
      businessTypeCompany: "Компания",
      businessLegalNameLabel: "Зарегистрированное название или имя *",
      businessLegalNamePlaceholder: "Как указано в регистрации бизнеса",
      ageLabel: "Ваш возраст",
      agePlaceholder: "Укажите ваш возраст",
      phoneLabel: "Телефон",
      phonePlaceholder: "Укажите контактный номер телефона",
      eventTypesLabel: "Какие мероприятия вы хотите проводить?",
      eventTypesPlaceholder: "Например: мастер-классы, спортивные занятия, нетворкинг, приватные клубы...",
      paymentMethodLabel: "Как вы хотите принимать оплаты?",
      paymentMethodCash: "Наличными",
      paymentMethodCard: "Только картой",
      paymentMethodBoth: "Наличные и карта",
      notesLabel: "Дополнительная информация",
      notesPlaceholder: "Необязательные детали, которые помогут нам понять вашу модель хостинга.",
      submitRequestCta: "Отправить запрос",
      closeRequestCta: "Закрыть",
      statuses: {
        not_connected: "Не подключено",
        onboarding_required: "Нужно пройти онбординг",
        restricted: "Есть ограничения",
        connected: "Подключено",
        error: "Ошибка подключения",
      },
    },
    limits: {
      cardTitle: "Открыть безлимитное создание",
      cardBody:
        "Параллельно можно держать до 3 активных созданий. Перейдите на Premium, чтобы открыть безлимитные ивенты и концепты на VaultGrid.",
      cardButtonLabel: "Перейти на премиум",
      modalTitleSlots: "Все host-слоты заняты",
      modalBodySlots:
        "Перейдите на Premium, чтобы открыть неограниченное количество ивентов и концептов на VaultGrid.",
      modalCapLabel: "Текущий лимит создания",
      modalParallelLimit: "Максимум 3 активных создания параллельно",
      debugTitle: "Local debug",
      debugLive: "Live",
      debugSlotsFull: "Slots full",
    },
    buttons: {
      backToCreation: "Создание события",
      openPremium: "Перейти на VaultGrid Premium",
      maybeLater: "Позже",
    },
  },
  sk: {
    title: "Panel organizátora",
    rulesCta: "Pravidlá",
    guideCta: "Inštrukcie",
    closeAria: "Zavrieť panel organizátora",
    loading: "Aktualizujem panel organizátora...",
    loadError:
      "Detaily panelu organizátora sa teraz nepodarilo načítať. Základné host štatistiky sú však stále zobrazené nižšie.",
    tabs: {
      overview: "Prehľad",
      events: "Eventy",
      analytics: "Analytika",
    },
    analyticsPanel: {
      eventList: "Analytika eventov",
      open: "Zobraziť analytiku",
      close: "Skryť analytiku",
      loading: "Načítavam analytiku...",
      error: "Analytiku eventu sa nepodarilo načítať.",
      retry: "Skúsiť znova",
      noEvents: "Zatiaľ nemáte eventy na analýzu.",
      previewError: "Počet otvorení kariet sa nepodarilo načítať.",
    },
    hostScore: {
      label: "Rating organizátora",
      unrated: "Zatiaľ bez hodnotení eventov",
      basedOn: "Vychádza z {{count}} hodnotení v rámci {{events}} ohodnotených eventov",
      attendance: "Miera potvrdenia účasti",
      attendanceHint: "{{confirmed}} potvrdení z {{registered}} účastníkov pripojených na začiatku podujatia",
    },
    sections: {
      performance: "Výsledky",
      attendance: "Potvrdenie účasti",
      analytics: "Dosah",
      recentEvents: "Eventy",
      payments: "VaultGrid platby",
      paidEvents: "Platené eventy",
      commercial: "Komerčné podmienky",
      payouts: "Prehľad výplat",
      limits: "Limity tvorby",
    },
    metrics: {
      slotsUsed: "Obsadené sloty",
      slotsAvailable: "Voľné sloty",
      totalCreated: "Celkovo vytvorené",
      activeEvents: "Aktívne eventy",
      activeConcepts: "Aktívne koncepty",
      finished: "Úspešne dokončené",
      canceled: "Zrušené",
      postponed: "Presunuté",
      registered: "Pripojení na začiatku",
      confirmed: "Potvrdili účasť",
      manualConfirmed: "Manuálne potvrdenia",
      attendanceRate: "Miera potvrdenia",
      availableSlots: "Voľné sloty",
      eventSlotsLeft: "Voľné sloty",
      views: "Zobrazenia kariet",
      opens: "Otvorenia kariet",
      uniqueViewers: "Unikátni diváci",
      uniqueOpeners: "Unikátne otvorenia",
      paidEventsCount: "Platené eventy",
      paidTicketsSold: "Predané lístky",
      depositPayments: "Prijaté depozity",
      refundedTickets: "Refundované lístky",
      depositRevenue: "Suma depozitov",
      grossRevenue: "Hrubé tržby",
      refundedAmount: "Refundy",
      buyerProcessingFees: "Poplatky kupujúcich pri starších nákupoch",
      organizerProcessingTariff: "Poplatok organizátora za spracovanie kariet",
      platformFeeRetained: "Provízia platformy",
      heldBalance: "Výplata je zadržaná",
      releasedBalance: "Uvoľnené výplaty",
      pendingPayout: "Odhadovaná výplata",
      cashDepositReserved: "Cash depozity",
      cashCommissionSettled: "Cash provízie",
      cashDepositReleased: "Vrátený cash depozit",
      cashOutstanding: "Cash nedoplatok",
    },
    eventCard: {
      paid: "Platený",
      free: "Bezplatný",
      activeOnly: "Aktívne",
      finishedOnly: "Ukončené",
      conceptsOnly: "Koncepty",
      currentParticipants: "Aktuálni účastníci",
      registeredAtStart: "Pripojení na začiatku",
      confirmedAttendance: "Potvrdili účasť",
      attendanceRate: "Miera potvrdenia",
      eventRating: "Hodnotenie eventu",
      participantFeedback: "Spätná väzba účastníkov",
      noEvents: "Zatiaľ nemáš vytvorené eventy.",
      noEventsFiltered: "V tejto kategórii zatiaľ nie sú eventy.",
      ratingsSuffix: "hodnotení",
      showMore: "Zobraziť viac",
    },
    feedbackModal: {
      title: "Spätná väzba účastníkov",
      close: "Zavrieť spätnú väzbu účastníkov",
      loading: "Načítavam anonymnú spätnú väzbu...",
      error: "Spätnú väzbu účastníkov sa teraz nepodarilo načítať.",
      retry: "Skúsiť znova",
      anonymousParticipant: "Anonymný účastník",
      empty: "K tomuto eventu zatiaľ nebola pridaná anonymná spätná väzba.",
    },
    safetyReports: {
      title: "Hlásenia neregistrovaných účastníkov",
      countLabel: "Hlásenia účastníkov: {{count}}",
      body:
        "Účastníci uviedli, že sa tohto eventu zúčastnili ľudia, ktorí neboli v zozname účastníkov VaultGrid.",
      guidance:
        "Skontroluj, prosím, ako bola účasť riadená. Každý prítomný musí byť registrovaný na VaultGrid a pripojený k eventu.",
      viewRules: "Pozrieť pravidlá organizovania eventov",
      closeRules: "Zavrieť pravidlá",
    },
    statuses: {
      active: "Aktívny",
      finished: "Dokončený",
      cancelled: "Zrušený",
      postponed: "Presunutý",
      pending: "Čakajúci",
      concept: "Koncept",
      event: "Event",
    },
    payments: {
      topTitle: "VaultGrid platby",
      hostGuideTitle: "Objav možnosti pre hostiteľov",
      hostGuideBody: "Pozri si nástroje pre organizátorov od budovania komunity až po predaj lístkov.",
      hostGuideCta: "Otvoriť sprievodcu hostiteľa",
      body:
        "Monetizuj svoje podujatia na VaultGrid a predávaj lístky priamo cez stránku podujatia.",
      stepsTitle: "Ako prebieha spustenie",
      steps: [
        "1. Pošli žiadosť o overenie organizátora platených podujatí.",
        "2. Po schválení si prečítaj a prijmi dohodu pre organizátorov platených podujatí.",
        "3. Nastav spôsob prijímania platieb za lístky.",
        "4. Potvrď fakturačné údaje.",
        "5. Spusti predaj lístkov a začni monetizovať svoje podujatia na VaultGrid.",
      ],
      stripeStatusLabel: "Pripojenie Stripe",
      tbankStatusLabel: "Výplaty cez T-Bank",
      requestStatusLabel: "Overenie organizátora",
      commercialIntro:
        "Tu nájdeš komerčné podmienky: províziu platformy, pravidlá zadržania platieb, uvoľnenie výplat a podmienky platených podujatí.",
      commercialCta: "Komerčné podmienky",
      commercialComingSoon: "Čoskoro",
      commercialTitle: "Komerčné podmienky",
      commercialPoints: [
        "Organizátori môžu cez VaultGrid predávať lístky na platené podujatia.",
        "VaultGrid si z každej úspešnej platby ponechá platformovú províziu.",
        "Prostriedky zostanú zadržané až do ukončenia podujatia a splnenia podmienok uvoľnenia.",
        "Presné percentá, termíny výplat a pravidlá sporov a vrátenia platieb sú uvedené v komerčných podmienkach.",
      ],
      paidEventsEmpty: "Zatiaľ nemáš žiadne platené podujatia.",
      payoutStatusTitle: "Stav výplaty",
      payoutStatuses: {
        scheduled: "Čaká na výplatu",
        blocked: "Zablokovaná",
        processing: "Banková výplata spustená",
        released: "Vyplatená do banky",
        failed: "Vyžaduje pozornosť",
      },
      payoutReleaseAfter: "Lehota VaultGridu na otvorenie sporu sa skončí {{date}}",
      payoutStripeFundsAvailable: "Stripe aktuálne očakáva spracovanie kartových prostriedkov {{date}}",
      payoutProcessingDetail: "Stripe a prijímajúca banka spracúvajú prevod.",
      payoutProcessingGeneric: "Poskytovateľ platieb a prijímajúca banka spracúvajú prevod.",
      payoutReleasedAt: "Stripe potvrdil výplatu do banky {{date}}",
      payoutReleasedGeneric: "Výplata do banky bola potvrdená {{date}}",
      payoutBlockedHint: "Výplata je pozastavená. Najskôr treba vyriešiť otvorené spory alebo obmedzenia platobného účtu.",
      payoutStripeReference: "Výplata Stripe {{id}}",
      payoutProviderReference: "Referencia výplaty {{id}}",
      payoutInfoAria: "Vysvetliť stav výplaty",
      payoutInfoTitle: "Ako funguje táto výplata",
      payoutInfoIntro:
        "Výplata prechádza samostatnými fázami VaultGridu a Stripe. Tieto lehoty sa nemusia zhodovať: 72-hodinová lehota VaultGridu na sťažnosti neskracuje čas spracovania kartovej platby v Stripe ani čas bankového prevodu.",
      payoutBreakdownTitle: "Odhad výplaty",
      payoutGrossTickets: "Tržby z lístkov",
      payoutRefunds: "Vrátené platby za lístky",
      payoutBuyerFees: "Poplatky za spracovanie pri starších nákupoch",
      payoutPlatformCommission: "Provízia VaultGrid",
      payoutOrganizerProcessingTariff: "Poplatok za spracovanie kariet",
      payoutEstimatedTotal: "Odhadovaná výplata organizátorovi",
      payoutStripeFeeNote:
        "Pri nových nákupoch kupujúci platia iba cenu lístka. VaultGrid fakturuje organizátorovi zverejnený poplatok za platby kartou; skutočný náklad Stripe sa eviduje osobitne.",
      payoutHistoricalFeeNote:
        "Pri starších nákupoch kupujúci zaplatili samostatné poplatky za spracovanie vo výške {{amount}}. Tieto sumy sa sledujú v historickom vyrovnaní oddelene od skutočných nákladov Stripe.",
      payoutTimelineTitle: "Priebeh výplaty",
      payoutTimeline: [
        "Lístok je zaplatený. Kartová platba je zaevidovaná na tvojom pripojenom účte Stripe. Kým Stripe nedokončí spracovanie, prostriedky môžu byť označené ako čakajúce.",
        "Podujatie je ukončené. VaultGrid spustí 72-hodinovú lehotu na sťažnosti. Otvorený spor pozastaví výplatu až do vyriešenia.",
        "Spracovanie v Stripe. Aj po skončení lehoty VaultGridu môžu kartové prostriedky zostať čakajúce do dátumu dostupnosti uvedeného Stripe.",
        "Výplata je spustená. Keď sú splnené obe podmienky, VaultGrid automaticky prikáže Stripe odoslať dostupnú sumu do tvojej banky. Nie je potrebná žiadna akcia.",
        "Bankový prevod. Stripe a banka spracujú výplatu. Čas pripísania závisí od Stripe, bankových dní, meny, krajiny a banky.",
      ],
      payoutTimingNote:
        "Zobrazené dátumy sú aktuálnym odhadom, nie zaručeným dátumom pripísania. Stripe, banka, kontroly súladu, rezervy, vrátenia platieb, spätné zúčtovanie alebo spor môžu lehotu predĺžiť.",
      payoutInfoClose: "Zavrieť",
      paymentTermsTitle: "Podmienky lístka",
      ticketPrice: "Cena lístka",
      upfrontPayment: "Platba pri vstupe",
      remainingPayment: "Zvyšok",
      balanceDue: "Výzva na doplatok",
      balanceDueDays: "{{days}} dní pred podujatím",
      noBalanceDue: "Bez doplatku",
      cancellationPolicy: "Storno podmienky",
      cancellationPolicies: {
        flexible: "Flexibilné",
        moderate: "Stredné",
        strict: "Prísne",
      },
      paidByTitle: "Záznamy lístkov",
      noPaidParticipants: "Zatiaľ tu nie sú žiadne záznamy lístkov.",
      lastPayment: "Posledná platba",
      newDeposits: "{{count}} nové",
      viewDetails: "Detaily platieb",
      hideDetails: "Skryť detaily",
      openEvent: "Otvoriť event",
      metricHelpTitle: "Čo znamenajú tieto údaje",
      metricsTitle: "Platby za lístky",
      chargesTitle: "Účtované poplatky",
      metricHelpAria: "Vysvetliť údaje o predaji lístkov",
      metricHelp: {
        paidTicketsSold: "Počet lístkov s úspešnou platbou.",
        depositPayments: "Počet lístkov, za ktoré bol zaplatený depozit.",
        refundedTickets: "Počet lístkov s úplnou alebo čiastočnou refundáciou.",
        refundedAmount: "Celková suma vrátená kupujúcim.",
        grossRevenue: "Všetky prijaté platby za lístky pred refundáciami a poplatkami.",
        depositRevenue: "Suma prijatých depozitov; je už zahrnutá v hrubých tržbách.",
        platformFeeRetained: "Provízia VaultGrid z príslušných tržieb za lístky.",
        organizerProcessingTariff: "Poplatok za spracovanie kartových platieb účtovaný organizátorovi.",
        pendingPayout: "Aktuálny odhad sumy, ktorá ešte patrí organizátorovi po refundáciách a príslušných poplatkoch; pred uvoľnením sa môže zmeniť.",
        releasedBalance: "Suma už uvoľnená na výplatu organizátorovi.",
      },
      refundedValueLabel: "Vrátené {{value}}",
      hostBalanceValueLabel: "Zostatok organizátora {{value}}",
      rowStatuses: {
        paid: "Uhradené",
        deposit_received: "Prijatý depozit",
        refunded: "Vrátené",
        partially_refunded: "Čiastočne vrátené",
        cancelled: "Zrušené",
        cash_deposit: "Hotovostný depozit",
      },
      connectCta: "Pripojiť platobný účet",
      stripeNewAccountNotice: "Týmto vytvoríš nový účet Stripe Standard pre VaultGrid. Existujúci účet Stripe mimo VaultGrid sa tu nedá pripojiť.",
      stripeCountryModalTitle: "Krajina podnikania",
      stripeCountryModalBody:
        "Vyber krajinu, v ktorej je registrovaná tvoja firma alebo podnikanie. Stripe podľa nej otvorí správny registračný formulár.",
      stripeCountryLabel: "Krajina registrácie podnikania",
      stripeCountryLockedHint:
        "Po vytvorení prepojeného účtu Stripe sa táto krajina už nedá zmeniť.",
      stripeCountryReviewedHint:
        "Toto je krajina registrácie schválená vo vašej žiadosti organizátora. Ak ju treba opraviť, pred pokračovaním kontaktujte VaultGrid.",
      stripeCountryContinue: "Pokračovať do Stripe",
      stripeCountryCancel: "Zrušiť",
      agreementCta: "Prijať dohodu pre platené podujatia",
      reviewAgreementCta: "Prečítať dohodu pre platené podujatia",
      billingCta: "Potvrdiť fakturačné údaje",
      billingHelp: "Stripe je pripojený. Pred predajom lístkov kartou potvrď svoje podnikateľské údaje.",
      agreementAcceptedCta: "Dohoda pre platené podujatia prijatá",
      verificationTermsCta: "Podmienky overenia",
      continueCta: "Dokončiť nastavenie Stripe",
      dashboardCta: "Otvoriť ovládací panel Stripe",
      refreshCta: "Obnoviť stav",
      tbankReadyHelp:
        "Výplaty cez T-Bank sú pripojené. Vytvor prvé platené podujatie a spusti predaj lístkov v rubľoch.",
      tbankDirectReadyHelp:
        "Internetový acquiring T-Bank je pripojený. Platby za lístky v rubľoch prijíma VOLGRID VOSTOK; výplaty organizátorom sa nastavujú samostatne.",
      tbankPendingHelp:
        "Profil príjemcu T-Bank sa nastavuje. Online predaj lístkov bude dostupný po overení údajov pre výplaty.",
      tbankMissingHelp:
        "Online predaj lístkov vyžaduje profil príjemcu T-Bank. Kontaktuj podporu VaultGrid a začni overenie.",
      tbankRecipientTypeLabel: "Typ príjemcu",
      tbankRecipientTypes: {
        sole_proprietor: "Živnostník",
        legal_entity: "Právnická osoba",
      },
      tbankCreateProfileCta: "Vytvoriť profil príjemcu",
      tbankBindCardCta: "Prepojiť kartu na výplaty",
      tbankSyncCardCta: "Skontrolovať prepojenú kartu",
      tbankProfileCreated: "Profil príjemcu bol vytvorený. VaultGrid ho overí s T-Bank.",
      tbankBindingStarted: "Dokonči prepojenie karty na otvorenej stránke T-Bank.",
      managementHelp:
        "Jednotlivé fázy výplaty sleduj v záložke Platby. Ovládací panel Stripe zobrazuje platby a zostatok, ale čas uvoľnenia prostriedkov z podujatia do banky riadi VaultGrid.",
      stripeConnectedHelp:
        "Tvoj platobný účet je pripojený. Vytvor prvé platené podujatie a spusti predaj lístkov na VaultGrid.",
      stripeOnboardingHelp:
        "Tvoj platobný účet je vytvorený. Dokonči nastavenie Stripe, aby sa sprístupnil ovládací panel a online predaj lístkov.",
      billingSavedHelp: "Fakturačné údaje sú uložené.",
      stripeReviewHelp:
        "Stripe kontroluje tvoj platobný účet. Výplaty zatiaľ nie sú povolené. Skontroluj požiadavky v Stripe Dashboard; ak tam žiadne nie sú, kontaktuj podporu VaultGrid.",
      requestAccessCta: "Požiadať o overenie organizátora",
      requestPendingCta: "Čaká na kontrolu",
      opening: "Otváram Stripe...",
      refreshing: "Obnovujem...",
      requestSubmitting: "Odosielam žiadosť...",
      requestSent: "Žiadosť bola odoslaná. Skontrolujeme ju pred sprístupnením platených podujatí.",
      requestAlreadyOpen: "Už máš otvorenú žiadosť o overenie organizátora platených podujatí.",
      requestOpenLabel: "Otvorená",
      requestInReviewLabel: "V kontrole",
      requestApprovedLabel: "Schválená",
      requestRejectedLabel: "Zamietnutá",
      requestNoneLabel: "Žiadosť ešte nebola odoslaná",
      requestHelp:
        "Ak chceš začať monetizovať podujatia na VaultGrid, najprv odošli žiadosť o overenie organizátora. Po schválení sa v tejto sekcii sprístupnia platené podujatia aj nastavenie platieb.",
      approvedHelp:
        "Overenie organizátora je schválené. Pred spustením platených podujatí si prečítaj a prijmi dohodu pre organizátorov platených podujatí.",
      agreementHelp:
        "Po prijatí dohody môžeš nastaviť Stripe pre online predaj lístkov alebo prijímať platby v hotovosti na platených podujatiach.",
      agreementUpdatedHelp:
        "Dohoda pre organizátorov platených podujatí bola aktualizovaná. Prečítaj si a prijmi aktuálnu verziu, aby si mohol pokračovať.",
      formTitle: "Žiadosť o overenie organizátora",
      formBody:
        "Povedz nám viac o svojom profile organizátora, aké podujatia chceš monetizovať a ako plánuješ prijímať platby.",
      formLegalHint:
        "Pred odoslaním žiadosti si pozri podmienky overenia a komerčné podmienky pre organizátorov platených podujatí.",
      requestConsentLabel:
        "Potvrdzujem, že informácie v tejto žiadosti sú presné a úplné a súhlasím s podmienkami overenia organizátora platených podujatí.",
      socialLabel: "Hlavný sociálny profil",
      socialPlaceholder: "Instagram, TikTok, Telegram kanál, Facebook stránka alebo iný verejný profil",
      businessSocialLabel: "Sociálny profil biznisu",
      businessSocialPlaceholder: "Voliteľná firemná stránka, štúdio, značka alebo web",
      businessCountryLabel: "Krajina registrácie podnikania *",
      businessCountryPlaceholder: "Vyber krajinu",
      businessTypeLabel: "Forma podnikania *",
      businessTypePlaceholder: "Vyber formu podnikania",
      businessTypeSoleTrader: "Živnostník",
      businessTypeCompany: "Spoločnosť",
      businessLegalNameLabel: "Registrované obchodné meno *",
      businessLegalNamePlaceholder: "Presne podľa registrácie podnikania",
      ageLabel: "Tvoj vek",
      agePlaceholder: "Zadaj svoj vek",
      phoneLabel: "Telefónne číslo",
      phonePlaceholder: "Zadaj svoje kontaktné telefónne číslo",
      eventTypesLabel: "Aké typy podujatí chceš organizovať?",
      eventTypesPlaceholder: "Napríklad: workshopy, športové stretnutia, networking, súkromné kluby...",
      paymentMethodLabel: "Ako chceš prijímať platby?",
      paymentMethodCash: "V hotovosti",
      paymentMethodCard: "Len kartou",
      paymentMethodBoth: "Hotovosť aj karta",
      notesLabel: "Doplňujúce informácie",
      notesPlaceholder: "Nepovinné detaily, ktoré nám pomôžu pochopiť tvoj hosting model.",
      submitRequestCta: "Odoslať žiadosť",
      closeRequestCta: "Zavrieť",
      statuses: {
        not_connected: "Nepripojené",
        onboarding_required: "Treba dokončiť onboarding",
        restricted: "Obmedzené",
        connected: "Pripojené",
        error: "Chyba pripojenia",
      },
    },
    limits: {
      cardTitle: "Odomkni neobmedzené vytváranie",
      cardBody:
        "Naraz môžeš mať najviac 3 aktívne eventy alebo koncepty. Prejdi na Premium a odomkni ich neobmedzené vytváranie na VaultGrid.",
      cardButtonLabel: "Prejsť na Premium",
      modalTitleSlots: "Všetky miesta na vytváranie sú obsadené",
      modalBodySlots:
        "Prejdi na Premium a odomkni neobmedzené eventy a koncepty na VaultGrid.",
      modalCapLabel: "Aktuálny limit tvorby",
      modalParallelLimit: "Maximálne 3 aktívne eventy alebo koncepty naraz",
      debugTitle: "Lokálny debug",
      debugLive: "Live",
      debugSlotsFull: "Slots full",
    },
    buttons: {
      backToCreation: "Vytváranie eventu",
      openPremium: "Prejsť na VaultGrid Premium",
      maybeLater: "Neskôr",
    },
  },
  cs: {
    "title": "Panel organizátora",
    "rulesCta": "Pravidla",
    "guideCta": "Instrukce",
    "closeAria": "Zavřít panel organizátora",
    "loading": "Aktualizuji panel organizátora...",
    "loadError": "Detaily panelu organizátora se nyní nepodařilo načíst. Základní hostitelské statistiky jsou však stále zobrazeny níže.",
    "tabs": {
      "overview": "Přehled",
      "events": "Eventy",
      "analytics": "Analytika"
    },
    "analyticsPanel": {
      "eventList": "Analytika eventů",
      "open": "Zobrazit analytiku",
      "close": "Skrýt analytiku",
      "loading": "Načítám analytiku...",
      "error": "Analytiku eventu se nepodařilo načíst.",
      "retry": "Zkusit znovu",
      "noEvents": "Zatím nemáte eventy k analýze.",
      "previewError": "Počet otevření karet se nepodařilo načíst."
    },
    "hostScore": {
      "label": "Hodnocení organizátora",
      "unrated": "Zatím bez hodnocení eventů",
      "basedOn": "Vyplývá z {{count}} hodnocení v rámci {{events}} hodnocených eventů",
      "attendance": "Míra potvrzení účasti",
      "attendanceHint": "{{confirmed}} potvrzených z {{registered}} účastníků při startu eventu"
    },
    "sections": {
      "performance": "Výsledky",
      "attendance": "Potvrzení účasti",
      "analytics": "Dosah",
      "recentEvents": "Eventy",
      "payments": "VaultGrid platby",
      "paidEvents": "Placené eventy",
      "commercial": "Komerční podmínky",
      "payouts": "Přehled výplat",
      "limits": "Limity tvorby"
    },
    "metrics": {
      "slotsUsed": "Obsazené sloty",
      "slotsAvailable": "Volné sloty",
      "totalCreated": "Celkem vytvořeno",
      "activeEvents": "Aktivní eventy",
      "activeConcepts": "Aktivní koncepty",
      "finished": "Úspěšně dokončeno",
      "canceled": "Zrušeno",
      "postponed": "Přesunuto",
      "registered": "Při startu přihlášeno",
      "confirmed": "Potvrdili účast",
      "manualConfirmed": "Manuální potvrzení",
      "attendanceRate": "Míra potvrzení",
      "availableSlots": "Volné sloty",
      "eventSlotsLeft": "Volné sloty",
      "views": "Zobrazení karet",
      "opens": "Otevření karet",
      "uniqueViewers": "Unikátní diváci",
      "uniqueOpeners": "Unikátní otevření",
      "paidEventsCount": "Placené eventy",
      "paidTicketsSold": "Prodáno lístků",
      "depositPayments": "Přijaté zálohy",
      "refundedTickets": "Vrácené lístky",
      "depositRevenue": "Částka záloh",
      "grossRevenue": "Hrubé tržby",
      "refundedAmount": "Refundace",
      "buyerProcessingFees": "Poplatky kupujících u starších nákupů",
      "organizerProcessingTariff": "Poplatek pořadatele za zpracování karet",
      "platformFeeRetained": "Provize platformy",
      "heldBalance": "Výplata zadržena",
      "releasedBalance": "Uvolněné výplaty",
      "pendingPayout": "Odhadovaná výplata",
      "cashDepositReserved": "Hotovostní zálohy",
      "cashCommissionSettled": "Hotovostní provize",
      "cashDepositReleased": "Vrácená hotovostní záloha",
      "cashOutstanding": "Hotovostní nedoplatek"
    },
    "eventCard": {
      "paid": "Placený",
      "free": "Zdarma",
      "activeOnly": "Aktivní",
      "finishedOnly": "Ukončené",
      "conceptsOnly": "Koncepty",
      "currentParticipants": "Aktuální účastníci",
      "registeredAtStart": "Přihlášeni na startu",
      "confirmedAttendance": "Potvrzená účast",
      "attendanceRate": "Míra potvrzení",
      "eventRating": "Hodnocení akce",
      "participantFeedback": "Zpětná vazba účastníků",
      "noEvents": "Zatím nemáš vytvořené akce.",
      "noEventsFiltered": "V této kategorii zatím nejsou žádné akce.",
      "ratingsSuffix": "hodnocení",
      "showMore": "Zobrazit více"
    },
    "feedbackModal": {
      "title": "Zpětná vazba účastníků",
      "close": "Zavřít zpětnou vazbu účastníků",
      "loading": "Načítám anonymní zpětnou vazbu...",
      "error": "Zpětnou vazbu účastníků se nyní nepodařilo načíst.",
      "retry": "Zkusit znovu",
      "anonymousParticipant": "Anonymní účastník",
      "empty": "K této akci zatím nebyla přidána anonymní zpětná vazba."
    },
    "safetyReports": {
      "title": "Hlášení neregistrovaných účastníků",
      "countLabel": "Hlášení účastníků: {{count}}",
      "body": "Účastníci uvedli, že se této akce zúčastnili lidé, kteří nebyli v seznamu účastníků VaultGrid.",
      "guidance": "Zkontroluj, prosím, jak byla účast řízena. Každý přítomný musí být registrovaný na VaultGrid a připojený k eventu.",
      "viewRules": "Zobrazit pravidla pořádání eventů",
      "closeRules": "Zavřít pravidla"
    },
    "statuses": {
      "active": "Aktivní",
      "finished": "Dokončený",
      "cancelled": "Zrušený",
      "postponed": "Přesunutý",
      "pending": "Čekající",
      "concept": "Koncept",
      "event": "Event"
    },
    "payments": {
      "hostGuideTitle": "Objev možnosti pro hostitele",
      "hostGuideBody": "Prohlédni si nástroje pro organizátory od budování komunity až po prodej lístků.",
      "hostGuideCta": "Otevřít průvodce hostitele",
      "topTitle": "VaultGrid platby",
      "body": "Monetizuj své akce na VaultGrid a prodávej vstupenky přímo přes stránku akce.",
      "stepsTitle": "Jak probíhá spuštění",
      "steps": [
        "1. Pošli žádost o ověření organizátora placených akcí.",
        "2. Po schválení si přečti a přijmi dohodu pro organizátory placených akcí.",
        "3. Nastav způsob přijímání plateb za vstupenky.",
        "4. Potvrď fakturační údaje.",
        "5. Spusť prodej vstupenek a začni monetizovat své akce na VaultGrid."
      ],
      "stripeStatusLabel": "Připojení Stripe",
      "tbankStatusLabel": "Výplaty přes T-Bank",
      "requestStatusLabel": "Ověření organizátora",
      "commercialIntro": "Zde najdeš obchodní podmínky: provizi platformy, pravidla zadržení plateb, uvolnění výplat a podmínky placených akcí.",
      "commercialCta": "Obchodní podmínky",
      "commercialComingSoon": "Brzy",
      "commercialTitle": "Obchodní podmínky",
      "commercialPoints": [
        "Organizátoři mohou přes VaultGrid prodávat vstupenky na placené akce.",
        "VaultGrid si z každé úspěšné platby ponechá platformní provizi.",
        "Prostředky zůstanou zadrženy až do ukončení akce a splnění podmínek uvolnění.",
        "Přesná procenta, termíny výplat a pravidla sporů a vrácení plateb jsou uvedena v obchodních podmínkách."
      ],
      "paidEventsEmpty": "Zatím nemáš žádné placené akce.",
      "payoutStatusTitle": "Stav výplaty",
      "payoutStatuses": {
        "scheduled": "Čeká na výplatu",
        "blocked": "Blokovaná",
        "processing": "Bankovní výplata spuštěna",
        "released": "Vyplacená do banky",
        "failed": "Vyžaduje pozornost"
      },
      "payoutReleaseAfter": "Lhůta VaultGridu pro otevření sporu končí {{date}}",
      "payoutStripeFundsAvailable": "Stripe aktuálně očekává zpracování kartových prostředků {{date}}",
      "payoutProcessingDetail": "Stripe a přijímající banka zpracovávají převod.",
      "payoutProcessingGeneric": "Poskytovatel plateb a přijímající banka zpracovávají převod.",
      "payoutReleasedAt": "Stripe potvrdil výplatu do banky {{date}}",
      "payoutReleasedGeneric": "Výplata do banky byla potvrzena {{date}}",
      "payoutBlockedHint": "Výplata je pozastavená. Nejprve je třeba vyřešit otevřené spory nebo omezení platebního účtu.",
      "payoutStripeReference": "Výplata Stripe {{id}}",
      "payoutProviderReference": "Reference výplaty {{id}}",
      "payoutInfoAria": "Vysvětlit stav výplaty",
      "payoutInfoTitle": "Jak funguje tato výplata",
      "payoutInfoIntro": "Výplata prochází samostatnými fázemi VaultGridu a Stripe. Tyto lhůty se nemusí shodovat: 72hodinová lhůta VaultGridu pro stížnosti nesnižuje čas zpracování kartové platby ve Stripe ani čas bankovního převodu.",
      "payoutBreakdownTitle": "Odhad výplaty",
      "payoutGrossTickets": "Tržby z lístků",
      "payoutRefunds": "Vrácené platby za lístky",
      "payoutBuyerFees": "Poplatky za zpracování u starších nákupů",
      "payoutPlatformCommission": "Provize VaultGrid",
      "payoutOrganizerProcessingTariff": "Poplatek za zpracování karet",
      "payoutEstimatedTotal": "Odhadovaná výplata organizátorovi",
      "payoutStripeFeeNote": "U nových nákupů kupující platí pouze cenu vstupenky. Skutečné náklady Stripe se evidují odděleně od poplatků kupujících.",
      "payoutHistoricalFeeNote": "U starších nákupů kupující zaplatili samostatné poplatky za zpracování ve výši {{amount}}. Tyto částky se sledují v historickém vyrovnání odděleně od skutečných nákladů Stripe.",
      "payoutTimelineTitle": "Průběh výplaty",
      "payoutTimeline": [
        "Lístek je zaplacen. Kartová platba je zaznamenána na tvém připojeném účtu Stripe. Dokud Stripe nedokončí zpracování, prostředky mohou být označeny jako čekající.",
        "Akce je ukončena. VaultGrid spustí 72hodinovou lhůtu pro stížnosti. Otevřený spor pozastaví výplatu až do vyřešení.",
        "Zpracování ve Stripe. I po skončení lhůty VaultGridu mohou kartové prostředky zůstat čekající do data dostupnosti uvedeného Stripe.",
        "Výplata je spuštěna. Když jsou splněny obě podmínky, VaultGrid automaticky nařídí Stripe odeslat dostupnou částku do tvé banky. Není potřeba žádná akce.",
        "Bankovní převod. Stripe a banka zpracují výplatu. Čas připsání závisí na Stripe, bankovních dnech, měně, zemi a bance."
      ],
      "payoutTimingNote": "Zobrazená data jsou aktuálním odhadem, nikoli zaručeným datem připsání. Stripe, banka, kontroly souladu, rezervy, vrácení plateb, zpětné zúčtování nebo spor mohou lhůtu prodloužit.",
      "payoutInfoClose": "Zavřít",
      "paymentTermsTitle": "Podmínky lístku",
      "ticketPrice": "Cena lístku",
      "upfrontPayment": "Platba při vstupu",
      "remainingPayment": "Zbytek",
      "balanceDue": "Výzva k doplatku",
      "balanceDueDays": "{{days}} dní před akcí",
      "noBalanceDue": "Bez doplatku",
      "cancellationPolicy": "Storno podmínky",
      "cancellationPolicies": {
        "flexible": "Flexibilní",
        "moderate": "Střední",
        "strict": "Přísné"
      },
      "paidByTitle": "Záznamy lístků",
      "noPaidParticipants": "Zatím zde nejsou žádné záznamy lístků.",
      "lastPayment": "Poslední platba",
      "newDeposits": "{{count}} nové",
      "viewDetails": "Detaily plateb",
      "hideDetails": "Skrýt detaily",
      "openEvent": "Otevřít událost",
      metricHelpTitle: "Co znamenají tyto údaje",
      metricsTitle: "Platby za lístky",
      chargesTitle: "Účtované poplatky",
      metricHelpAria: "Vysvětlit údaje o prodeji lístků",
      metricHelp: {
        paidTicketsSold: "Počet lístků s úspěšnou platbou.",
        depositPayments: "Počet lístků, u kterých byla zaplacena záloha.",
        refundedTickets: "Počet lístků s úplnou nebo částečnou refundací.",
        refundedAmount: "Celková částka vrácená kupujícím.",
        grossRevenue: "Všechny přijaté platby za lístky před refundacemi a poplatky.",
        depositRevenue: "Částka přijatých záloh; je již zahrnuta v hrubých tržbách.",
        platformFeeRetained: "Provize VaultGrid z příslušných tržeb za lístky.",
        organizerProcessingTariff: "Poplatek za zpracování plateb kartou účtovaný pořadateli.",
        pendingPayout: "Aktuální odhad částky, která ještě náleží pořadateli po refundacích a příslušných poplatcích; před uvolněním se může změnit.",
        releasedBalance: "Částka již uvolněná k výplatě pořadateli.",
      },
      "refundedValueLabel": "Vráceno {{value}}",
      "hostBalanceValueLabel": "Zůstatek organizátora {{value}}",
      "rowStatuses": {
        "paid": "Zaplaceno",
        "deposit_received": "Přijatá záloha",
        "refunded": "Vráceno",
        "partially_refunded": "Částečně vráceno",
        "cancelled": "Zrušeno",
        "cash_deposit": "Hotovostní záloha"
      },
      "connectCta": "Připojit platební účet",
      "stripeNewAccountNotice": "Tímto vytvoříš nový účet Stripe Standard pro VaultGrid. Stávající účet Stripe mimo VaultGrid tu nelze připojit.",
      "stripeCountryModalTitle": "Země podnikání",
      "stripeCountryModalBody": "Vyber zemi, ve které je registrována tvoje firma nebo podnikání. Stripe podle toho otevře správný registrační formulář.",
      "stripeCountryLabel": "Země registrace podnikání",
      "stripeCountryLockedHint": "Po vytvoření propojeného účtu Stripe už tuto zemi nelze změnit.",
      "stripeCountryReviewedHint": "Toto je země registrace schválená ve vaší žádosti pořadatele. Pokud ji potřebujete opravit, před pokračováním kontaktujte VaultGrid.",
      "stripeCountryContinue": "Pokračovat do Stripe",
      "stripeCountryCancel": "Zrušit",
      "agreementCta": "Přijmout dohodu pro placené akce",
      "reviewAgreementCta": "Přečíst dohodu pro placené akce",
      "billingCta": "Potvrdit fakturační údaje",
      "billingHelp": "Stripe je připojený. Před prodejem vstupenek kartou potvrď své podnikatelské údaje.",
      "agreementAcceptedCta": "Dohoda pro placené akce přijata",
      "verificationTermsCta": "Podmínky ověření",
      "continueCta": "Dokončit nastavení Stripe",
      "dashboardCta": "Otevřít ovládací panel Stripe",
      "refreshCta": "Obnovit stav",
      "tbankReadyHelp": "Výplaty přes T-Bank jsou připojené. Vytvoř první placenou akci a spusť prodej vstupenek v rublích.",
      "tbankDirectReadyHelp": "Internetový acquiring T-Bank je připojený. Platby za vstupenky v rublích přijímá VOLGRID VOSTOK; výplaty organizátorům se nastavují samostatně.",
      "tbankPendingHelp": "Profil příjemce T-Bank se nastavuje. Online prodej vstupenek bude dostupný po ověření údajů pro výplaty.",
      "tbankMissingHelp": "Online prodej vstupenek vyžaduje profil příjemce T-Bank. Kontaktuj podporu VaultGrid a začni ověření.",
      "tbankRecipientTypeLabel": "Typ příjemce",
      "tbankRecipientTypes": {
        "sole_proprietor": "Živnostník",
        "legal_entity": "Právnická osoba"
      },
      "tbankCreateProfileCta": "Vytvořit profil příjemce",
      "tbankBindCardCta": "Propojit kartu pro výplaty",
      "tbankSyncCardCta": "Zkontrolovat propojenou kartu",
      "tbankProfileCreated": "Profil příjemce byl vytvořen. VaultGrid ho ověří s T-Bank.",
      "tbankBindingStarted": "Dokonči propojení karty na otevřené stránce T-Bank.",
      "managementHelp": "Jednotlivé fáze výplaty sleduj v záložce Platby. Ovládací panel Stripe zobrazuje platby a zůstatek, ale čas uvolnění prostředků z akce do banky řídí VaultGrid.",
      "stripeConnectedHelp": "Tvůj platební účet je připojený. Vytvoř první placenou akci a spusť prodej vstupenek na VaultGrid.",
      "stripeOnboardingHelp": "Tvůj platební účet je vytvořený. Dokonči nastavení Stripe, aby se zpřístupnil ovládací panel a online prodej vstupenek.",
      "billingSavedHelp": "Fakturační údaje jsou uložené.",
      "stripeReviewHelp": "Stripe kontroluje tvůj platební účet. Výplaty zatím nejsou povolené. Zkontroluj požadavky ve Stripe Dashboard; pokud tam žádné nejsou, kontaktuj podporu VaultGrid.",
      "requestAccessCta": "Požádat o ověření organizátora",
      "requestPendingCta": "Čeká na kontrolu",
      "opening": "Otevírám Stripe...",
      "refreshing": "Obnovuji...",
      "requestSubmitting": "Odesílám žádost...",
      "requestSent": "Žádost byla odeslána. Zkontrolujeme ji před zpřístupněním placených akcí.",
      "requestAlreadyOpen": "Už máš otevřenou žádost o ověření organizátora placených akcí.",
      "requestOpenLabel": "Otevřená",
      "requestInReviewLabel": "V kontrole",
      "requestApprovedLabel": "Schválená",
      "requestRejectedLabel": "Zamítnutá",
      "requestNoneLabel": "Žádost ještě nebyla odeslána",
      "requestHelp": "Pokud chceš začít monetizovat akce na VaultGrid, nejdřív odešli žádost o ověření organizátora. Po schválení se v této sekci zpřístupní placené akce i nastavení plateb.",
      "approvedHelp": "Ověření organizátora je schválené. Před spuštěním placených akcí si přečti a přijmi dohodu pro organizátory placených akcí.",
      "agreementHelp": "Po přijetí dohody můžeš nastavit Stripe pro online prodej vstupenek nebo přijímat platby v hotovosti na placených akcích.",
      "agreementUpdatedHelp": "Dohoda pro organizátory placených akcí byla aktualizována. Přečti si a přijmi aktuální verzi, aby ses mohl posunout dál.",
      "formTitle": "Žádost o ověření organizátora",
      "formBody": "Řekni nám víc o svém profilu organizátora, jaké akce chceš monetizovat a jak plánuješ přijímat platby.",
      "formLegalHint": "Před odesláním žádosti si přečti podmínky ověření a obchodní podmínky pro organizátory placených akcí.",
      "requestConsentLabel": "Potvrzuji, že informace v této žádosti jsou přesné a úplné a souhlasím s podmínkami ověření organizátora placených akcí.",
      "socialLabel": "Hlavní sociální profil",
      "socialPlaceholder": "Instagram, TikTok, Telegram kanál, Facebook stránka nebo jiný veřejný profil",
      "businessSocialLabel": "Sociální profil firmy",
      "businessSocialPlaceholder": "Volitelná firemní stránka, studio, značka nebo web",
      "businessCountryLabel": "Země registrace podnikání *",
      "businessCountryPlaceholder": "Vyber zemi",
      "businessTypeLabel": "Forma podnikání *",
      "businessTypePlaceholder": "Vyber formu podnikání",
      "businessTypeSoleTrader": "Živnostník",
      "businessTypeCompany": "Společnost",
      "businessLegalNameLabel": "Registrované obchodní jméno *",
      "businessLegalNamePlaceholder": "Přesně podle registrace podnikání",
      "ageLabel": "Tvůj věk",
      "agePlaceholder": "Zadej svůj věk",
      "phoneLabel": "Telefonní číslo",
      "phonePlaceholder": "Zadej své kontaktní telefonní číslo",
      "eventTypesLabel": "Jaké typy akcí chceš pořádat?",
      "eventTypesPlaceholder": "Například: workshopy, sportovní setkání, networking, soukromé kluby...",
      "paymentMethodLabel": "Jak chceš přijímat platby?",
      "paymentMethodCash": "Hotově",
      "paymentMethodCard": "Pouze kartou",
      "paymentMethodBoth": "Hotově i kartou",
      "notesLabel": "Doplňující informace",
      "notesPlaceholder": "Nepovinné detaily, které nám pomohou pochopit tvůj hosting model.",
      "submitRequestCta": "Odeslat žádost",
      "closeRequestCta": "Zavřít",
      "statuses": {
        "not_connected": "Nepřipojeno",
        "onboarding_required": "Je třeba dokončit onboarding",
        "restricted": "Omezeno",
        "connected": "Připojeno",
        "error": "Chyba připojení"
      }
    },
    "limits": {
      "cardTitle": "Odemkni neomezené vytváření",
      "cardBody": "Najvíce můžeš mít současně 3 aktivní eventy nebo koncepty. Přejdi na Premium a odemkni neomezené vytváření na VaultGrid.",
      "cardButtonLabel": "Přejít na Premium",
      "modalTitleSlots": "Všechna místa pro vytváření jsou obsazená",
      "modalBodySlots": "Přejdi na Premium a odemkni neomezené eventy a koncepty na VaultGrid.",
      "modalCapLabel": "Aktuální limit tvorby",
      "modalParallelLimit": "Maximálně 3 aktivní eventy nebo koncepty současně",
      "debugTitle": "Lokální debug",
      "debugLive": "Live",
      "debugSlotsFull": "Místa plná"
    },
    "buttons": {
      "backToCreation": "Vytváření eventu",
      "openPremium": "Přejít na VaultGrid Premium",
      "maybeLater": "Později"
    }
  },
};

function resolveCopy(i18n) {
  const lang = String(i18n?.resolvedLanguage || i18n?.language || "en").toLowerCase();
  if (lang.startsWith("ru")) return COPY.ru;
  if (lang.startsWith("sk")) return COPY.sk;
  if (lang.startsWith("cs")) return COPY.cs;
  return COPY.en;
}

const SETTLEMENT_COPY = {
  en: {
    offerTitle: "Your approved payment terms",
    direct: "Payouts from your Stripe account",
    hold: "Payout after the event",
    directDetail: "Ticket revenue goes to your connected Stripe account without waiting for the event to end. Stripe sends available funds to your bank on your payout schedule; with manual payouts, you initiate them in Stripe Dashboard. You pay Stripe processing fees. Separately, VaultGrid calculates any platform commission after the event and its 72-hour complaint period, then sends an invoice. That invoice does not delay your ticket revenue in Stripe.",
    holdDetail: "VaultGrid initiates the eligible bank payout after the event and the complaint period, once Stripe funds are available.",
    freeUntil: "0% VaultGrid commission for events ending by {{date}} (inclusive). The current turnover-based commission applies to later events.",
    freeOpen: "0% VaultGrid commission until your approved terms change.",
    commission: "Any VaultGrid commission follows your approved payment terms and the linked Commercial Conditions; it is invoiced separately after the event.",
    manualScheduleNotice: "Your Stripe account currently has manual bank payouts. To receive automatic transfers, change the payout schedule in Stripe Dashboard. Ticket revenue still reaches your Stripe balance without waiting for the event.",
    eventDirect: "Available ticket revenue is paid to your bank according to your Stripe payout settings. VaultGrid does not hold it until the event ends or initiate the bank payout. Stripe processing fees are charged to you; any VaultGrid commission is invoiced after the event and complaint period.",
    eventHold: "This event uses the standard VaultGrid payout process after the event and complaint period.",
    invoiceUnpaid: "VaultGrid commission invoice due",
    invoicePaid: "VaultGrid commission invoice paid",
    invoiceVoided: "VaultGrid commission invoice voided · nothing to pay",
    invoiceAdjusting: "Earlier VaultGrid commission invoice under review · do not pay yet",
    invoiceAdjustingDetail: "This earlier invoice was flagged for manual review. VaultGrid will confirm the amount due and provide the appropriate document. Payment is unavailable during review.",
    invoiceDue: "Due {{date}}",
    payInvoice: "Pay invoice",
    payingInvoice: "Opening payment…",
    invoicePaymentError: "Could not open invoice payment. Please try again.",
    stripeFee: "Stripe processing fees",
    commissionEstimate: "VaultGrid commission (invoiced after the event)",
    commissionPending: "Calculated after event",
    metricCommission: "For direct events, commission is calculated after the event and complaint period, then invoiced separately. A valid 0% waiver makes it zero.",
    metricStripeFee: "Stripe charges its processing fees to your connected account. See Stripe Dashboard for the final amount.",
  },
  ru: {
    offerTitle: "Одобренные условия оплаты",
    direct: "Вывод с вашего Stripe-аккаунта",
    hold: "Выплата после события",
    directDetail: "Выручка от билетов поступает на ваш подключённый Stripe-аккаунт без ожидания конца события. Stripe перечисляет доступные средства в банк по вашему графику выплат; при ручном графике вы запускаете вывод в Stripe Dashboard. Комиссию Stripe оплачиваете вы. Отдельно VaultGrid рассчитывает свою комиссию после события и 72 часов для жалоб, затем выставляет счёт. Этот счёт не задерживает вашу выручку в Stripe.",
    holdDetail: "VaultGrid инициирует допустимую выплату после события и срока для жалоб, когда средства становятся доступны в Stripe.",
    freeUntil: "Комиссия VaultGrid 0% для событий, завершившихся не позднее {{date}} включительно. Для более поздних событий действует текущая шкала комиссии с оборота.",
    freeOpen: "Комиссия VaultGrid 0%, пока ваши одобренные условия не изменятся.",
    commission: "Комиссия VaultGrid определяется вашими одобренными условиями оплаты и связанными Коммерческими условиями; после события она выставляется отдельным счётом.",
    manualScheduleNotice: "Сейчас на вашем Stripe-аккаунте выбран ручной вывод в банк. Для автоматических переводов измените график выплат в Stripe Dashboard. Выручка от билетов всё равно поступает на баланс Stripe без ожидания конца события.",
    eventDirect: "Доступная выручка выводится в банк по настройкам вашего Stripe-аккаунта. VaultGrid не удерживает её до конца события и не запускает банковскую выплату. Комиссию Stripe оплачиваете вы; комиссию VaultGrid, если она есть, выставят счётом после события и срока для жалоб.",
    eventHold: "Для этого события действует стандартный порядок выплаты VaultGrid после события и срока для жалоб.",
    invoiceUnpaid: "Счёт на комиссию VaultGrid к оплате",
    invoicePaid: "Счёт на комиссию VaultGrid оплачен",
    invoiceVoided: "Счёт на комиссию VaultGrid аннулирован · платить не нужно",
    invoiceAdjusting: "Ранее выставленный счёт VaultGrid на проверке · пока не оплачивайте",
    invoiceAdjustingDetail: "Этот ранее выставленный счёт направлен на ручную проверку. VaultGrid подтвердит сумму к оплате и предоставит нужный документ. Во время проверки оплата недоступна.",
    invoiceDue: "Оплатить до {{date}}",
    payInvoice: "Оплатить счёт",
    payingInvoice: "Открываем оплату…",
    invoicePaymentError: "Не удалось открыть оплату счёта. Попробуйте ещё раз.",
    stripeFee: "Комиссия Stripe за обработку",
    commissionEstimate: "Комиссия VaultGrid (счёт после события)",
    commissionPending: "Расчёт после события",
    metricCommission: "Для прямых выплат комиссия рассчитывается после события и срока для жалоб, затем выставляется отдельным счётом. При действующей льготе 0% она равна нулю.",
    metricStripeFee: "Stripe списывает комиссию за обработку с вашего подключённого аккаунта. Итоговую сумму смотрите в Stripe Dashboard.",
  },
  sk: {
    offerTitle: "Schválené platobné podmienky",
    direct: "Výplaty z vášho účtu Stripe",
    hold: "Výplata po podujatí",
    directDetail: "Tržby za lístky idú na váš prepojený účet Stripe bez čakania na koniec podujatia. Stripe pošle dostupné prostriedky do banky podľa vášho harmonogramu výplat; pri manuálnom nastavení prevod spustíte v Stripe Dashboard. Poplatky Stripe hradíte vy. Osobitne VaultGrid vypočíta svoju províziu po podujatí a 72-hodinovej lehote na sťažnosti a vystaví faktúru. Táto faktúra neodkladá vaše tržby v Stripe.",
    holdDetail: "VaultGrid spustí oprávnenú bankovú výplatu po podujatí a lehote na sťažnosti, keď sú prostriedky dostupné v Stripe.",
    freeUntil: "Provízia VaultGrid je 0 % pre podujatia končiace najneskôr {{date}} vrátane. Pre neskoršie podujatia platí súčasná odstupňovaná provízia z obratu.",
    freeOpen: "Provízia VaultGrid je 0 %, kým sa vaše schválené podmienky nezmenia.",
    commission: "Provízia VaultGrid sa riadi vašimi schválenými platobnými podmienkami a súvisiacimi obchodnými podmienkami; po podujatí sa fakturuje osobitne.",
    manualScheduleNotice: "Na vašom účte Stripe sú teraz nastavené manuálne výplaty do banky. Ak chcete automatické prevody, zmeňte harmonogram v Stripe Dashboard. Tržby za lístky aj tak prichádzajú na zostatok Stripe bez čakania na koniec podujatia.",
    eventDirect: "Dostupné tržby za lístky sa vyplácajú do banky podľa nastavení vášho účtu Stripe. VaultGrid ich nezadržiava do skončenia podujatia ani nezačína bankovú výplatu. Poplatky Stripe hradíte vy; prípadná provízia VaultGrid sa fakturuje po podujatí a lehote na sťažnosti.",
    eventHold: "Na toto podujatie sa vzťahuje štandardný proces výplaty VaultGrid po podujatí a lehote na sťažnosti.",
    invoiceUnpaid: "Faktúra za províziu VaultGrid na úhradu",
    invoicePaid: "Faktúra za províziu VaultGrid uhradená",
    invoiceVoided: "Faktúra za províziu VaultGrid zrušená · nič netreba platiť",
    invoiceAdjusting: "Skôr vystavená faktúra VaultGrid sa preveruje · zatiaľ ju neplaťte",
    invoiceAdjustingDetail: "Táto skôr vystavená faktúra bola označená na manuálne preverenie. VaultGrid potvrdí splatnú sumu a poskytne príslušný doklad. Počas preverovania nie je možné faktúru uhradiť.",
    invoiceDue: "Splatná {{date}}",
    payInvoice: "Zaplatiť faktúru",
    payingInvoice: "Otváram platbu…",
    invoicePaymentError: "Platbu faktúry sa nepodarilo otvoriť. Skúste to znova.",
    stripeFee: "Poplatky Stripe za spracovanie",
    commissionEstimate: "Provízia VaultGrid (faktúra po podujatí)",
    commissionPending: "Výpočet po podujatí",
    metricCommission: "Pri priamych výplatách sa provízia vypočíta po podujatí a lehote na sťažnosti, potom sa osobitne fakturuje. Pri platnom zvýhodnení 0 % je nulová.",
    metricStripeFee: "Stripe účtuje poplatky za spracovanie vášmu prepojenému účtu. Konečnú sumu nájdete v Stripe Dashboard.",
  },
  cs: {
    offerTitle: "Schválené platební podmínky",
    direct: "Výplaty z vašeho účtu Stripe",
    hold: "Výplata po akci",
    directDetail: "Tržby ze vstupenek jdou na váš propojený účet Stripe bez čekání na konec akce. Stripe pošle dostupné prostředky do banky podle vašeho harmonogramu výplat; při ručním nastavení převod spustíte ve Stripe Dashboard. Poplatky Stripe hradíte vy. Samostatně VaultGrid vypočte svou provizi po akci a 72hodinové lhůtě pro stížnosti a vystaví fakturu. Tato faktura neodkládá vaše tržby ve Stripe.",
    holdDetail: "VaultGrid zahájí oprávněnou bankovní výplatu po akci a lhůtě pro stížnosti, jakmile jsou prostředky dostupné ve Stripe.",
    freeUntil: "Provize VaultGrid je 0 % pro akce končící nejpozději {{date}} včetně. Pro pozdější akce platí současná odstupňovaná provize z obratu.",
    freeOpen: "Provize VaultGrid je 0 %, dokud se vaše schválené podmínky nezmění.",
    commission: "Provize VaultGrid se řídí vašimi schválenými platebními podmínkami a souvisejícími obchodními podmínkami; po akci se fakturuje samostatně.",
    manualScheduleNotice: "Na vašem účtu Stripe jsou nyní nastavené ruční výplaty do banky. Pokud chcete automatické převody, změňte harmonogram ve Stripe Dashboard. Tržby ze vstupenek přesto přicházejí na zůstatek Stripe bez čekání na konec akce.",
    eventDirect: "Dostupné tržby ze vstupenek se vyplácejí do banky podle nastavení vašeho účtu Stripe. VaultGrid je nezadržuje do skončení akce ani nezahajuje bankovní výplatu. Poplatky Stripe hradíte vy; případná provize VaultGrid se fakturuje po akci a lhůtě pro stížnosti.",
    eventHold: "Na tuto akci se vztahuje standardní proces výplaty VaultGrid po akci a lhůtě pro stížnosti.",
    invoiceUnpaid: "Faktura za provizi VaultGrid k úhradě",
    invoicePaid: "Faktura za provizi VaultGrid uhrazena",
    invoiceVoided: "Faktura za provizi VaultGrid zrušena · nic není třeba platit",
    invoiceAdjusting: "Dříve vystavená faktura VaultGrid se prověřuje · zatím ji neplaťte",
    invoiceAdjustingDetail: "Tato dříve vystavená faktura byla označena k ruční kontrole. VaultGrid potvrdí splatnou částku a poskytne příslušný doklad. Během kontroly nelze fakturu uhradit.",
    invoiceDue: "Splatná {{date}}",
    payInvoice: "Zaplatit fakturu",
    payingInvoice: "Otevírám platbu…",
    invoicePaymentError: "Platbu faktury se nepodařilo otevřít. Zkuste to znovu.",
    stripeFee: "Poplatky Stripe za zpracování",
    commissionEstimate: "Provize VaultGrid (faktura po akci)",
    commissionPending: "Výpočet po akci",
    metricCommission: "Při přímých výplatách se provize vypočte po akci a lhůtě pro stížnosti, poté se samostatně fakturuje. Při platném zvýhodnění 0 % je nulová.",
    metricStripeFee: "Stripe účtuje poplatky za zpracování vašemu propojenému účtu. Konečnou částku najdete ve Stripe Dashboard.",
  },
};

function resolveSettlementCopy(i18n) {
  const language = String(i18n?.resolvedLanguage || i18n?.language || "en").slice(0, 2).toLowerCase();
  return SETTLEMENT_COPY[language] || SETTLEMENT_COPY.en;
}

function normalizeStripeCountryCode(value) {
  const code = String(value || "").trim().toUpperCase();
  return /^[A-Z]{2}$/.test(code) ? code : "";
}

function buildStripeCountryOptions(stripe) {
  const rawOptions = Array.isArray(stripe?.supported_countries)
    ? stripe.supported_countries
    : FALLBACK_STRIPE_CONNECT_COUNTRIES;
  const seen = new Set();
  const options = rawOptions
    .map((item) => {
      const code = normalizeStripeCountryCode(item?.code || item);
      if (!code || seen.has(code)) return null;
      seen.add(code);
      return {
        code,
        name: String(item?.name || item?.label || code).trim() || code,
      };
    })
    .filter(Boolean);
  return options.length > 0 ? options : FALLBACK_STRIPE_CONNECT_COUNTRIES;
}

function canUseTelegramExternalLink() {
  if (typeof window === "undefined") return false;
  return typeof window.Telegram?.WebApp?.openLink === "function";
}

function prepareExternalWindow() {
  if (typeof window === "undefined" || canUseTelegramExternalLink()) return null;
  try {
    const popup = window.open("about:blank", "_blank");
    if (popup) {
      try {
        popup.opener = null;
      } catch {}
    }
    return popup;
  } catch {
    return null;
  }
}

function closePreparedExternalWindow(popup) {
  try {
    popup?.close?.();
  } catch {}
}

function openExternalStripeUrl(url, popup = null) {
  const target = String(url || "").trim();
  if (!target || typeof window === "undefined") return false;

  if (popup) {
    try {
      popup.location.replace(target);
      popup.focus?.();
      return true;
    } catch {
      closePreparedExternalWindow(popup);
    }
  }

  try {
    const telegramWebApp = window.Telegram?.WebApp;
    if (typeof telegramWebApp?.openLink === "function") {
      telegramWebApp.openLink(target, { try_instant_view: false });
      return true;
    }
  } catch {}

  try {
    const opened = window.open(target, "_blank", "noopener,noreferrer");
    if (opened) {
      opened.focus?.();
      return true;
    }
  } catch {}

  window.location.assign(target);
  return true;
}

function numberValue(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function formatNumber(value) {
  return new Intl.NumberFormat().format(numberValue(value));
}

function formatAnalyticsEventDate(value, language) {
  const date = value ? new Date(value) : null;
  if (!date || Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(language || undefined, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(date);
}

export function formatCompactMetricNumber(value) {
  const numeric = numberValue(value);
  if (Math.abs(numeric) < 1000) return formatNumber(numeric);

  return new Intl.NumberFormat("en-US", {
    notation: "compact",
    compactDisplay: "short",
    maximumSignificantDigits: 3,
  }).format(numeric);
}

function formatMoney(value, currency = "EUR") {
  const parsed = Number(value);
  const safe = Number.isFinite(parsed) ? parsed : 0;
  const normalizedCurrency = String(currency || "EUR").toUpperCase();
  const symbol = normalizedCurrency === "EUR" ? "€" : normalizedCurrency === "RUB" ? "₽" : normalizedCurrency;
  return `${new Intl.NumberFormat(undefined, {
    minimumFractionDigits: safe % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(safe)} ${symbol}`.trim();
}

function formatPaymentDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function formatMetricValue(value, { unlimited = false } = {}) {
  if (unlimited) return "∞";
  return formatNumber(value);
}

function formatPercent(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return "0%";
  return `${parsed.toFixed(parsed % 1 === 0 ? 0 : 1)}%`;
}

function buildStars(value) {
  const safe = Math.max(0, Math.min(5, Number(value) || 0));
  return Array.from({ length: 5 }, (_, index) => ({
    filled: safe >= index + 1,
    half: !Number.isInteger(safe) && safe > index && safe < index + 1,
  }));
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative rounded-full px-4 py-2 text-[0.82rem] font-semibold transition ${
        active
          ? "border border-white/22 bg-white text-[#0A64FF] shadow-[0_10px_24px_rgba(0,0,0,0.18)]"
          : "border border-white/14 bg-white/[0.04] text-white/74"
      }`}
    >
      {children}
    </button>
  );
}

function CountBadge({ count, className = "" }) {
  const value = Number(count) || 0;
  if (value <= 0) return null;
  const label = value > 999 ? "999+" : String(value);
  const isWide = label.length >= 2;
  const isVeryWide = label.length >= 3;
  return (
    <span
      className={`pointer-events-none absolute flex items-center justify-center rounded-full bg-white text-[#0A64FF] shadow-[0_0_10px_rgba(255,255,255,0.42)] ${
        isVeryWide
          ? "h-5 min-w-5 px-[6px] text-[10px]"
          : isWide
          ? "h-5 min-w-5 px-[6px] text-[11px]"
          : "h-5 w-5 text-[11px]"
      } ${className}`}
      aria-hidden="true"
    >
      <span className="font-extrabold leading-none">{label}</span>
    </span>
  );
}

function HostPanelTabs({
  copy,
  activeTab,
  onChange,
  eventsCount = 0,
  compact = false,
}) {
  const options = HOST_PANEL_TAB_VALUES.map((value) => ({
    value,
    label: copy.tabs[value],
  }));

  return (
    <div
      className={`mx-auto ${
        compact
          ? `${EMBEDDED_ORGANIZER_CONTROL_WIDTH_CLASS} mt-0`
          : "mt-5 w-full max-w-[345px]"
      }`}
    >
      <SegmentedModeToggle
        value={activeTab}
        options={options}
        onChange={onChange}
        ariaLabel={copy.title}
        className={`vg-glass-control h-10 !overflow-visible !rounded-[99px] !border-white/10 ${
          compact ? "max-w-none" : "max-w-[345px]"
        }`}
        style={{ width: "100%" }}
        knobClassName="h-[34px] !rounded-[99px] !border-0"
        buttonClassName="min-w-0 px-2 !text-[13px] !font-medium !leading-5 !text-white"
        surfaceStyle={{
          backgroundImage:
            "linear-gradient(180deg,rgba(255,255,255,0.02)_0%,rgba(255,255,255,0.1)_100%)",
        }}
        activeStyle={{
          backgroundImage: "none",
          backgroundColor: "rgba(0,0,0,0.5)",
          boxShadow:
            "0 1px 0 rgba(0,0,0,0.05), 0 4px 4px rgba(0,0,0,0.05), 0 10px 10px rgba(0,0,0,0.1)",
        }}
        measureKnob
        knobInset={3}
        renderAccessory={(option) => {
          const count = option.value === "events" ? eventsCount : 0;
          return count > 0 ? (
            <CountBadge
              count={count}
              className="right-0 top-0 translate-x-[18%] -translate-y-[42%]"
            />
          ) : null;
        }}
      />
    </div>
  );
}

function formatFeedbackDate(value) {
  if (!value) return "";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
}

function HostFeedbackModal({
  open,
  event,
  copy,
  onClose,
  onSeen,
}) {
  const [feedback, setFeedback] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const eventId = Number(event?.event_id || 0);

  const loadFeedback = useCallback(async () => {
    if (!eventId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/ratings/events/${eventId}/host-feedback/`, {
        meta: { suppressGlobalError: true },
      });
      setFeedback(Array.isArray(data?.feedback) ? data.feedback : []);
      try {
        await api.post(`/ratings/events/${eventId}/host-feedback/read/`, {});
        onSeen?.(eventId);
      } catch (readError) {
        console.error("[HostFeedbackModal] failed to mark feedback read", readError);
      }
    } catch (loadError) {
      console.error("[HostFeedbackModal] failed to load feedback", loadError);
      setError(copy.feedbackModal.error);
    } finally {
      setLoading(false);
    }
  }, [copy.feedbackModal.error, eventId, onSeen]);

  useEffect(() => {
    if (!open || !eventId) return;
    setFeedback([]);
    void loadFeedback();
  }, [eventId, loadFeedback, open]);

  return (
    <AppModalShell
      open={Boolean(open && eventId)}
      onClose={onClose}
      closeAriaLabel={copy.feedbackModal.close}
      title={copy.feedbackModal.title}
      description={event?.name || ""}
      maxWidthClassName="max-w-[520px]"
      desktopMaxHeight="min(82vh, 720px)"
      bodyClassName="min-h-0"
      contentClassName="min-h-0"
    >
      {loading ? (
        <div className="space-y-3" aria-label={copy.feedbackModal.loading}>
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`feedback-loading-${index}`}
              className="vg-chat-loading-row rounded-[20px] border border-white/10 bg-white/[0.05] px-4 py-4"
            >
              <HostPanelSkeletonBlock className="h-4 w-[46%]" />
              <HostPanelSkeletonBlock className="mt-4 h-4 w-full" />
              <HostPanelSkeletonBlock className="mt-2 h-4 w-[76%]" />
            </div>
          ))}
        </div>
      ) : error ? (
        <div className="rounded-[20px] border border-amber-200/18 bg-amber-300/10 px-4 py-4">
          <p className="text-[14px] font-medium leading-6 text-amber-50/90">{error}</p>
          <button
            type="button"
            onClick={() => void loadFeedback()}
            className="mt-4 inline-flex h-10 items-center justify-center rounded-full bg-white px-5 text-[14px] font-bold text-[#1D2C54]"
          >
            {copy.feedbackModal.retry}
          </button>
        </div>
      ) : feedback.length === 0 ? (
        <div className="rounded-[20px] border border-white/10 bg-white/[0.05] px-4 py-5 text-[14px] font-medium leading-6 text-white/68">
          {copy.feedbackModal.empty}
        </div>
      ) : (
        <div className="space-y-3">
          {feedback.map((item, index) => (
            <article
              key={item?.id || `anonymous-feedback-${index}`}
              className="rounded-[20px] border border-white/12 bg-white/[0.06] px-4 py-4 shadow-[0_14px_26px_rgba(0,0,0,0.14)]"
            >
              <div className="flex items-center justify-between gap-3">
                <span className="text-[12px] font-extrabold uppercase tracking-[0.12em] text-white/68">
                  {copy.feedbackModal.anonymousParticipant}
                </span>
                <time className="shrink-0 text-[11px] font-medium text-white/42">
                  {formatFeedbackDate(item?.created_at)}
                </time>
              </div>
              <p className="mt-3 whitespace-pre-wrap break-words text-[15px] font-medium leading-6 text-white/92">
                {item?.text || ""}
              </p>
            </article>
          ))}
        </div>
      )}
    </AppModalShell>
  );
}

function HostPanelSkeletonBlock({ className = "" }) {
  return (
    <span
      className={`vg-chat-loading-shimmer block rounded-full bg-white/[0.16] ${className}`}
      aria-hidden="true"
    />
  );
}

function HostPanelOverviewSkeleton() {
  return (
    <motion.div
      className="mt-5 space-y-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      aria-hidden="true"
    >
      <div className="rounded-[26px] border border-white/18 bg-white/[0.045] px-5 py-5 shadow-[0_20px_40px_rgba(0,0,0,0.18)]">
        <HostPanelSkeletonBlock className="h-[18px] w-[112px] bg-white/[0.20]" />
        <div className="mt-8 flex flex-wrap items-end gap-5">
          <HostPanelSkeletonBlock className="h-[58px] w-[96px] bg-white/[0.22]" />
          <div className="flex gap-2 pb-2">
            {Array.from({ length: 5 }).map((_, index) => (
              <HostPanelSkeletonBlock
                key={`host-score-star-skeleton-${index}`}
                className="h-[28px] w-[28px] bg-yellow-200/[0.20]"
              />
            ))}
          </div>
        </div>
        <HostPanelSkeletonBlock className="mt-6 h-[22px] w-[84%] bg-white/[0.16]" />
        <div className="mt-6 rounded-[20px] border border-white/14 bg-black/[0.12] px-5 py-5">
          <HostPanelSkeletonBlock className="h-[20px] w-[78%] bg-white/[0.18]" />
          <HostPanelSkeletonBlock className="ml-auto mt-7 h-[46px] w-[128px] bg-white/[0.22]" />
          <HostPanelSkeletonBlock className="mx-auto mt-5 h-[20px] w-[72%] bg-white/[0.14]" />
        </div>
      </div>

      <div className="rounded-[26px] border border-white/18 bg-white/[0.045] px-5 py-5 shadow-[0_20px_40px_rgba(0,0,0,0.16)]">
        <HostPanelSkeletonBlock className="h-[18px] w-[150px] bg-white/[0.20]" />
        <div className="mt-5 grid grid-cols-2 gap-3">
          <HostPanelSkeletonBlock className="h-[112px] rounded-[22px] bg-white/[0.12]" />
          <HostPanelSkeletonBlock className="h-[112px] rounded-[22px] bg-white/[0.12]" />
        </div>
      </div>

      <div className="flex items-center gap-4 py-2">
        <HostPanelSkeletonBlock className="h-[3px] flex-1 bg-white/[0.10]" />
        <HostPanelSkeletonBlock className="h-[18px] w-[132px] bg-white/[0.18]" />
        <HostPanelSkeletonBlock className="h-[3px] flex-1 bg-white/[0.10]" />
      </div>

      <div className="grid grid-cols-2 gap-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <HostPanelSkeletonBlock
            key={`host-overview-metric-skeleton-${index}`}
            className="h-[112px] rounded-[22px] bg-white/[0.10]"
          />
        ))}
      </div>
    </motion.div>
  );
}

function HostPanelEventListSkeleton({ rows = 4, showFilters = false, showPaymentIntro = false } = {}) {
  return (
    <motion.div
      className="mt-5 space-y-4"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
      aria-hidden="true"
    >
      {showFilters ? (
        <div className="mb-4 flex flex-wrap justify-center gap-2">
          <HostPanelSkeletonBlock className="h-[36px] w-[70px] bg-white/[0.16]" />
          <HostPanelSkeletonBlock className="h-[36px] w-[86px] bg-white/[0.12]" />
          <HostPanelSkeletonBlock className="h-[36px] w-[104px] bg-white/[0.12]" />
        </div>
      ) : null}

      {showPaymentIntro ? (
        <div className="rounded-[26px] border border-white/18 bg-white/[0.045] px-5 py-5">
          <HostPanelSkeletonBlock className="h-[20px] w-[72%] bg-white/[0.16]" />
          <HostPanelSkeletonBlock className="mt-4 h-[20px] w-[58%] bg-white/[0.12]" />
          <div className="mt-5 grid gap-2">
            {Array.from({ length: 4 }).map((_, index) => (
              <HostPanelSkeletonBlock
                key={`host-payment-step-skeleton-${index}`}
                className="h-[44px] rounded-[16px] bg-white/[0.09]"
              />
            ))}
          </div>
        </div>
      ) : null}

      {Array.from({ length: rows }).map((_, index) => (
        <div
          key={`host-event-card-skeleton-${index}`}
          className="vg-chat-loading-row rounded-[24px] border border-white/16 bg-white/[0.045] px-5 py-5 shadow-[0_18px_34px_rgba(0,0,0,0.14)]"
          style={{ animationDelay: `${index * 80}ms` }}
        >
          <div className="flex items-start gap-4">
            <HostPanelSkeletonBlock className="h-[72px] w-[72px] shrink-0 rounded-[18px] bg-white/[0.14]" />
            <div className="min-w-0 flex-1">
              <div className="flex items-start gap-3">
                <HostPanelSkeletonBlock className="h-[24px] w-[62%] bg-white/[0.19]" />
                <HostPanelSkeletonBlock className="ml-auto h-[24px] w-[72px] bg-white/[0.12]" />
              </div>
              <div className="mt-4 flex gap-2">
                <HostPanelSkeletonBlock className="h-[26px] w-[26px] bg-white/[0.12]" />
                <HostPanelSkeletonBlock className="h-[26px] w-[26px] bg-white/[0.12]" />
                <HostPanelSkeletonBlock className="h-[26px] w-[26px] bg-white/[0.12]" />
                <HostPanelSkeletonBlock className="h-[26px] w-[26px] bg-white/[0.12]" />
              </div>
            </div>
          </div>
          <div className="mt-5 grid grid-cols-2 gap-3">
            <HostPanelSkeletonBlock className="h-[92px] rounded-[20px] bg-white/[0.09]" />
            <HostPanelSkeletonBlock className="h-[92px] rounded-[20px] bg-white/[0.09]" />
          </div>
        </div>
      ))}
    </motion.div>
  );
}

function HostPanelSkeleton({ activeTab = "overview" }) {
  if (activeTab === "events") {
    return <HostPanelEventListSkeleton showFilters />;
  }

  if (activeTab === "payments") {
    return <HostPanelEventListSkeleton rows={3} showPaymentIntro />;
  }

  if (activeTab === "analytics") {
    return <HostPanelEventListSkeleton rows={3} />;
  }

  return <HostPanelOverviewSkeleton />;
}

function MetricCard({ label, value, hint = "", className = "" }) {
  return (
    <div className={`flex min-h-[92px] flex-col rounded-[22px] border border-white/12 bg-white/[0.05] px-4 py-4 shadow-[0_18px_32px_rgba(0,0,0,0.18)] ${className}`}>
      <div className="min-h-[34px] text-[0.74rem] font-semibold uppercase tracking-[0.14em] text-white/58">
        {label}
      </div>
      <div className="mt-auto pt-3 text-[1.9rem] font-extrabold leading-none text-white">
        {value}
      </div>
      {hint ? (
        <div className="mt-2 text-[0.8rem] leading-[1.35] text-white/56">{hint}</div>
      ) : null}
    </div>
  );
}

function PaymentMetricsHelpModal({ open, onClose, copy, settlementCopy, settlementMode = "event_hold" }) {
  const metricKeys = settlementMode === "direct" ? [
    "paidTicketsSold", "depositPayments", "refundedTickets", "refundedAmount",
    "grossRevenue", "depositRevenue", "platformFeeRetained", "stripeFee",
  ] : [
    "paidTicketsSold", "depositPayments", "refundedTickets", "refundedAmount",
    "grossRevenue", "depositRevenue", "platformFeeRetained", "organizerProcessingTariff",
    "pendingPayout", "releasedBalance",
  ];
  return (
    <AppModalShell
      open={open}
      onClose={onClose}
      title={copy.payments.metricHelpTitle}
      closeAriaLabel={copy.payments.payoutInfoClose}
      maxWidthClassName="max-w-[560px]"
    >
      <div className="space-y-4">
        {metricKeys.map((key) => (
          <div key={key}>
            <div className="text-[0.78rem] font-bold uppercase tracking-[0.1em] text-[#a5ecff]">
              {settlementMode === "direct" && key === "platformFeeRetained"
                ? settlementCopy.commissionEstimate
                : key === "stripeFee"
                  ? settlementCopy.stripeFee
                  : copy.metrics[key]}
            </div>
            <p className="mt-1 text-[0.9rem] leading-[1.5] text-white/72">
              {settlementMode === "direct" && key === "platformFeeRetained"
                ? settlementCopy.metricCommission
                : key === "stripeFee"
                  ? settlementCopy.metricStripeFee
                  : copy.payments.metricHelp[key]}
            </p>
          </div>
        ))}
      </div>
    </AppModalShell>
  );
}

function AnalyticsMetricSkeleton() {
  return (
    <div className="flex min-h-[92px] flex-col rounded-[22px] border border-white/12 bg-white/[0.05] px-4 py-4 shadow-[0_18px_32px_rgba(0,0,0,0.18)]" aria-hidden="true">
      <div className="min-h-[34px] space-y-2">
        <HostPanelSkeletonBlock className="h-[10px] w-[76%]" />
        <HostPanelSkeletonBlock className="h-[10px] w-[52%]" />
      </div>
      <div className="mt-auto pt-3">
        <HostPanelSkeletonBlock className="h-[30px] w-[45%]" />
      </div>
    </div>
  );
}

function PaymentInfoPill({ label, value }) {
  return (
    <div className="rounded-[18px] border border-white/10 bg-black/10 px-4 py-3">
      <div className="text-[0.7rem] font-semibold uppercase tracking-[0.12em] text-white/48">
        {label}
      </div>
      <div className="mt-1 text-[0.98rem] font-bold leading-snug text-white">{value}</div>
    </div>
  );
}

export function PayoutInfoModal({ open, onClose, copy, paymentSummary }) {
  const grossRevenue = numberValue(paymentSummary?.gross_revenue);
  const refundedAmount = numberValue(paymentSummary?.refunded_amount);
  const buyerFees = numberValue(paymentSummary?.participant_service_fee_total);
  const platformCommission = numberValue(paymentSummary?.platform_commission_amount);
  const organizerProcessingTariff = numberValue(paymentSummary?.organizer_processing_fee_amount);
  const estimatedPayout = numberValue(paymentSummary?.pending_payout);
  const rows = [
    { label: copy.payments.payoutGrossTickets, value: grossRevenue, sign: "" },
    ...(refundedAmount > 0 ? [{ label: copy.payments.payoutRefunds, value: refundedAmount, sign: "−" }] : []),
    { label: copy.payments.payoutPlatformCommission, value: platformCommission, sign: "−" },
    ...(organizerProcessingTariff > 0 ? [{ label: copy.payments.payoutOrganizerProcessingTariff, value: organizerProcessingTariff, sign: "−" }] : []),
  ];

  return (
    <AppModalShell
      open={open}
      onClose={onClose}
      title={copy.payments.payoutInfoTitle}
      closeAriaLabel={copy.payments.payoutInfoClose}
      maxWidthClassName="max-w-[560px]"
    >
        <div className="text-[0.72rem] font-extrabold uppercase tracking-[0.16em] text-[#75d9ff]">
          {copy.payments.payoutStatusTitle}
        </div>

        <p className="mt-4 text-[0.94rem] leading-[1.55] text-white/72">
          {copy.payments.payoutInfoIntro}
        </p>

        <div className="mt-5 rounded-[20px] border border-white/10 bg-black/15 p-4">
          <div className="text-[0.74rem] font-extrabold uppercase tracking-[0.15em] text-white/58">
            {copy.payments.payoutBreakdownTitle}
          </div>
          <div className="mt-3 space-y-3">
            {rows.map((row) => (
              <div key={row.label} className="flex items-start justify-between gap-4 text-[0.92rem]">
                <span className="text-white/68">{row.label}</span>
                <span className="shrink-0 font-bold text-white">
                  {row.sign}{formatMoney(row.value)}
                </span>
              </div>
            ))}
            <div className="h-px bg-white/12" />
            <div className="flex items-start justify-between gap-4">
              <span className="text-[0.94rem] font-extrabold text-white">
                {copy.payments.payoutEstimatedTotal}
              </span>
              <span className="shrink-0 text-[1.25rem] font-extrabold text-[#85e6ff]">
                {formatMoney(estimatedPayout)}
              </span>
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-[18px] border border-[#75d9ff]/20 bg-[#75d9ff]/[0.08] px-4 py-3 text-[0.84rem] leading-[1.5] text-white/70">
          <p>{copy.payments.payoutStripeFeeNote}</p>
          {buyerFees > 0 ? (
            <p className="mt-2">{copy.payments.payoutHistoricalFeeNote.replace("{{amount}}", formatMoney(buyerFees))}</p>
          ) : null}
        </div>

        <div className="mt-5">
          <div className="text-[0.74rem] font-extrabold uppercase tracking-[0.15em] text-white/58">
            {copy.payments.payoutTimelineTitle}
          </div>
          <div className="mt-3 space-y-3">
            {copy.payments.payoutTimeline.map((item, index) => (
              <div key={item} className="flex gap-3">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#75d9ff]/35 bg-[#75d9ff]/10 text-[0.78rem] font-extrabold text-[#8ee8ff]">
                  {index + 1}
                </div>
                <div className="pt-0.5 text-[0.88rem] leading-[1.5] text-white/72">{item}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-5 rounded-[18px] border border-amber-200/20 bg-amber-200/[0.07] px-4 py-3 text-[0.84rem] leading-[1.5] text-white/72">
          {copy.payments.payoutTimingNote}
        </div>
    </AppModalShell>
  );
}

function StripeCountrySelectionModal({
  open,
  onClose,
  copy,
  countryOptions,
  value,
  reviewedCountry,
  onChange,
  onContinue,
  busy,
  error,
  showNewAccountNotice,
}) {
  const selectedCountry = reviewedCountry || normalizeStripeCountryCode(value) || DEFAULT_STRIPE_CONNECT_COUNTRY;
  const displayedCountryOptions = reviewedCountry && !countryOptions.some((option) => option.code === reviewedCountry)
    ? [...countryOptions, { code: reviewedCountry, name: reviewedCountry }]
    : countryOptions;

  return (
    <AppModalShell
      open={open}
      onClose={onClose}
      title={copy.payments.stripeCountryModalTitle}
      description={copy.payments.stripeCountryModalBody}
      closeAriaLabel={copy.closeAria}
      maxWidthClassName="max-w-[520px]"
      footer={
        <div className="flex flex-col gap-3">
          <AppModalButton loading={busy} onClick={onContinue}>
            {busy ? copy.payments.opening : copy.payments.stripeCountryContinue}
          </AppModalButton>
          <AppModalButton variant="secondary" disabled={busy} onClick={onClose}>
            {copy.payments.stripeCountryCancel}
          </AppModalButton>
        </div>
      }
    >
      {showNewAccountNotice ? (
        <p className="mb-4 rounded-[16px] border border-cyan-300/20 bg-cyan-300/[0.07] px-4 py-3 text-[0.87rem] leading-[1.5] text-white/80">
          {copy.payments.stripeNewAccountNotice}
        </p>
      ) : null}
      <label className="block">
        <div className="mb-2 text-[0.82rem] font-semibold text-white/82">
          {copy.payments.stripeCountryLabel}
        </div>
        <select
          value={selectedCountry}
          onChange={(event) => onChange(normalizeStripeCountryCode(event.target.value))}
          disabled={Boolean(reviewedCountry)}
          className="h-12 w-full rounded-[16px] border border-white/12 bg-white/[0.06] px-4 text-[0.96rem] font-semibold text-white outline-none"
        >
          {displayedCountryOptions.map((option) => (
            <option key={option.code} value={option.code} className="text-black">
              {option.name}
            </option>
          ))}
        </select>
      </label>

      <div className="mt-4 rounded-[18px] border border-[#75d9ff]/20 bg-[#75d9ff]/[0.08] px-4 py-3 text-[0.86rem] leading-[1.5] text-white/72">
        {reviewedCountry ? copy.payments.stripeCountryReviewedHint : copy.payments.stripeCountryLockedHint}
      </div>

      {error ? (
        <div className="mt-4 rounded-[18px] border border-red-300/24 bg-red-500/[0.12] px-4 py-3 text-[0.86rem] leading-[1.5] text-red-50">
          {error}
        </div>
      ) : null}
    </AppModalShell>
  );
}

function PaymentUserRow({ payment, copy, settlementMode = "event_hold" }) {
  const buyer = payment?.buyer || {};
  const displayName = buyer.full_name || buyer.username || buyer.code || `#${buyer.user_id || ""}`;
  const avatarSrc = resolveUserAvatarSrc(buyer, "/assets/events/default_avatar.png");
  const grossPaidAmount = payment?.gross_paid_amount ?? payment?.amount;
  const refundedAmount = numberValue(payment?.refunded_amount);
  const hostBalanceAmount = numberValue(payment?.host_balance_amount);
  const grossPaidAmountValue = numberValue(grossPaidAmount);
  const statusLabel =
    copy.payments.rowStatuses?.[payment?.status] ||
    String(payment?.status || "").trim() ||
    copy.payments.rowStatuses?.paid ||
    "";
  const shouldShowHostBalance = settlementMode !== "direct"
    && Math.abs(hostBalanceAmount - grossPaidAmountValue) >= 0.01;
  return (
    <div className="relative overflow-visible flex items-center gap-3 rounded-[18px] border border-white/10 bg-white/[0.04] px-3 py-3">
      <CountBadge
        count={payment?.is_recent_deposit ? 1 : 0}
        className="right-0 top-0 translate-x-[28%] -translate-y-[28%]"
      />
      <img
        src={avatarSrc}
        alt=""
        className="h-11 w-11 shrink-0 rounded-full object-cover ring-1 ring-white/30"
      />
      <div className="min-w-0 flex-1">
        <div className="truncate text-[0.96rem] font-extrabold text-white">{displayName}</div>
        <div className="mt-0.5 flex flex-wrap gap-x-2 gap-y-1 text-[0.78rem] text-white/52">
          {buyer.code ? <span>{buyer.code}</span> : null}
          {payment?.paid_at ? <span>{formatPaymentDate(payment.paid_at)}</span> : null}
        </div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-[1rem] font-extrabold text-white">
          {formatMoney(grossPaidAmount, payment?.currency)}
        </div>
        <div className="text-[0.72rem] font-semibold uppercase tracking-[0.08em] text-white/66">
          {statusLabel}
        </div>
        {refundedAmount > 0 ? (
          <div className="mt-1 text-[0.72rem] font-semibold text-[#9BE8FF]">
            {copy.payments.refundedValueLabel.replace(
              "{{value}}",
              formatMoney(refundedAmount, payment?.currency)
            )}
          </div>
        ) : null}
        {shouldShowHostBalance ? (
          <div className="mt-1 text-[0.72rem] font-semibold text-white/56">
            {copy.payments.hostBalanceValueLabel.replace(
              "{{value}}",
              formatMoney(hostBalanceAmount, payment?.currency)
            )}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function SectionTitle({ children, className = "mt-6" }) {
  return (
    <div className={`${className} mb-4 flex w-full items-center gap-4`}>
      <div className="h-px min-w-[32px] flex-1 bg-white/38 shadow-[0_0_0_1px_rgba(255,255,255,0.08)]" />
      <span className="shrink-0 px-1 text-[0.78rem] font-semibold uppercase tracking-[0.18em] text-white">
        {children}
      </span>
      <div className="h-px min-w-[32px] flex-1 bg-white/38 shadow-[0_0_0_1px_rgba(255,255,255,0.08)]" />
    </div>
  );
}

function StarRow({ value }) {
  const stars = buildStars(value);
  return (
    <div className="flex items-center gap-1">
      {stars.map((star, index) => (
        <span
          key={index}
          className={`text-[1.25rem] leading-none ${
            star.filled || star.half ? "text-[#FFD86B]" : "text-white/22"
          }`}
        >
          ★
        </span>
      ))}
    </div>
  );
}

function OrganizerUpsellModal({
  copy,
  open,
  onClose,
  onOpenPremium,
  title,
  body,
}) {
  const isDesktop = useIsDesktop();
  useBodyLock(open);
  if (!open) return null;
  const card = (
    <div
      className="pointer-events-auto relative max-h-[calc(100dvh-env(safe-area-inset-top,0px)-var(--bottom-nav-clearance,112px)-16px)] w-full max-w-[560px] overflow-y-auto rounded-[30px] border border-white/10 bg-[linear-gradient(180deg,rgba(19,27,36,0.88),rgba(10,16,24,0.76))] p-5 text-white shadow-[0_24px_80px_rgba(0,0,0,0.35)] backdrop-blur-xl sm:p-6"
      onClick={(event) => event.stopPropagation()}
    >
      <div className="pointer-events-none absolute -top-16 left-1/2 h-40 w-40 -translate-x-1/2 rounded-full bg-[#57B6FF]/12 blur-3xl" />
      <div className="relative flex items-start justify-between gap-4">
        <div>
          <div className="text-[0.76rem] font-semibold uppercase tracking-[0.16em] text-[#6FD3FF]">
            VaultGrid Premium
          </div>
          <h3 className="mt-2 text-[1.45rem] font-extrabold leading-[1.1] text-white">
            {title}
          </h3>
        </div>
        <VaultModalCloseButton onClick={onClose} aria-label={copy.closeAria} />
      </div>

      <p className="mt-4 text-[0.98rem] leading-[1.55] text-white/76">{body}</p>

      <div className="mt-5 rounded-[22px] border border-[#4ec9ff]/20 bg-[linear-gradient(180deg,rgba(75,145,255,0.18)_0%,rgba(24,197,224,0.12)_100%)] px-4 py-4">
        <div className="text-[0.74rem] font-semibold uppercase tracking-[0.14em] text-[#9BE8FF]">
          {copy.limits.modalCapLabel}
        </div>
        <div className="mt-2 text-[0.96rem] leading-[1.45] text-white/88">
          {copy.limits.modalParallelLimit}
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3">
        <button
          type="button"
          onClick={onOpenPremium}
          className="inline-flex h-12 w-full items-center justify-center overflow-hidden rounded-[18px] bg-[linear-gradient(90deg,#2F67E7_0%,#2FD8F6_100%)] px-5 text-[0.98rem] font-semibold text-white shadow-[0_18px_32px_rgba(47,116,232,0.34)]"
        >
          {copy.buttons.openPremium}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-12 w-full items-center justify-center rounded-[18px] border border-white/12 bg-white/[0.05] px-5 text-[0.98rem] font-semibold text-white/76"
        >
          {copy.buttons.maybeLater}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <div className="fixed inset-0 z-[40] bg-black/70 backdrop-blur-sm" aria-hidden="true" />
      {!isDesktop ? (
        <>
          <div className="fixed inset-0 z-[55] bg-transparent" onClick={onClose} aria-hidden="true" />
          <div className="fixed inset-0 z-[56] flex items-center justify-center px-4 pb-[calc(var(--bottom-nav-clearance,112px)+16px)] pt-[calc(env(safe-area-inset-top,0px)+16px)] pointer-events-none">
            {card}
          </div>
        </>
      ) : (
        <>
          <div className="fixed inset-0 z-[55] bg-transparent" onClick={onClose} aria-hidden="true" />
          <main className="fixed inset-0 z-[56] flex items-center justify-center p-6 pointer-events-none">
            {card}
          </main>
        </>
      )}
    </>
  );
}

function HostPaidAccessRequestModal({
  copy,
  open,
  onClose,
  form,
  onChange,
  onSubmit,
  submitting,
  error,
  countryOptions,
  onOpenVerificationTerms,
  onOpenCommercialConditions,
}) {
  const socialValue = String(form.social_profile || "").trim();
  const ageValue = String(form.age || "").trim();
  const phoneValue = String(form.phone_number || "").trim();
  const eventTypesValue = String(form.requested_event_types || "").trim();
  const acceptedVerificationTerms = Boolean(form.accept_verification_terms);
  const submitDisabled =
    submitting ||
    socialValue.length < 3 ||
    !/^\d+$/.test(ageValue) ||
    Number(ageValue) < 1 ||
    Number(ageValue) > 120 ||
    phoneValue.length < 5 ||
    eventTypesValue.length < 3 ||
    !form.business_country ||
    !form.legal_form ||
    !String(form.business_legal_name || "").trim() ||
    !acceptedVerificationTerms;

  return (
    <AppModalShell
      open={open}
      onClose={onClose}
      title={copy.payments.formTitle}
      description={copy.payments.formBody}
      closeAriaLabel={copy.closeAria}
      maxWidthClassName="max-w-[560px]"
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <span className="mb-2 block text-[0.82rem] font-semibold text-white/82">
              {copy.payments.businessCountryLabel}
            </span>
            <select
              value={form.business_country}
              onChange={(event) => onChange("business_country", event.target.value)}
              className="h-12 w-full rounded-[16px] border border-white/12 bg-white/[0.06] px-4 text-[0.96rem] text-white outline-none"
            >
              <option value="" className="text-black">{copy.payments.businessCountryPlaceholder}</option>
              {countryOptions.map((option) => (
                <option key={option.code} value={option.code} className="text-black">{option.name}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-2 block text-[0.82rem] font-semibold text-white/82">
              {copy.payments.businessTypeLabel}
            </span>
            <select
              value={form.legal_form}
              onChange={(event) => onChange("legal_form", event.target.value)}
              className="h-12 w-full rounded-[16px] border border-white/12 bg-white/[0.06] px-4 text-[0.96rem] text-white outline-none"
            >
              <option value="" className="text-black">{copy.payments.businessTypePlaceholder}</option>
              <option value="sole_trader" className="text-black">{copy.payments.businessTypeSoleTrader}</option>
              <option value="company" className="text-black">{copy.payments.businessTypeCompany}</option>
            </select>
          </label>
        </div>
        <label className="block">
          <span className="mb-2 block text-[0.82rem] font-semibold text-white/82">
            {copy.payments.businessLegalNameLabel}
          </span>
          <input
            type="text"
            value={form.business_legal_name}
            onChange={(event) => onChange("business_legal_name", event.target.value)}
            maxLength={255}
            placeholder={copy.payments.businessLegalNamePlaceholder}
            autoComplete="organization"
            className="h-12 w-full rounded-[16px] border border-white/12 bg-white/[0.06] px-4 text-[0.96rem] text-white outline-none placeholder:text-white/34"
          />
        </label>
        <label className="block">
          <div className="mb-2 text-[0.82rem] font-semibold text-white/82">
            {copy.payments.socialLabel}
          </div>
          <input
            type="text"
            value={form.social_profile}
            onChange={(event) => onChange("social_profile", event.target.value)}
            placeholder={copy.payments.socialPlaceholder}
            className="h-12 w-full rounded-[16px] border border-white/12 bg-white/[0.06] px-4 text-[0.96rem] text-white outline-none placeholder:text-white/34"
          />
        </label>

        <label className="block">
          <div className="mb-2 text-[0.82rem] font-semibold text-white/82">
            {copy.payments.businessSocialLabel}
          </div>
          <input
            type="text"
            value={form.business_social_profile}
            onChange={(event) => onChange("business_social_profile", event.target.value)}
            placeholder={copy.payments.businessSocialPlaceholder}
            className="h-12 w-full rounded-[16px] border border-white/12 bg-white/[0.06] px-4 text-[0.96rem] text-white outline-none placeholder:text-white/34"
          />
        </label>

        <div className="grid gap-4 sm:grid-cols-2">
          <label className="block">
            <div className="mb-2 text-[0.82rem] font-semibold text-white/82">
              {copy.payments.ageLabel}
            </div>
            <input
              type="number"
              min="1"
              max="120"
              value={form.age}
              onChange={(event) => onChange("age", event.target.value)}
              placeholder={copy.payments.agePlaceholder}
              className="h-12 w-full rounded-[16px] border border-white/12 bg-white/[0.06] px-4 text-[0.96rem] text-white outline-none placeholder:text-white/34"
            />
          </label>

          <label className="block">
            <div className="mb-2 text-[0.82rem] font-semibold text-white/82">
              {copy.payments.phoneLabel}
            </div>
            <input
              type="tel"
              value={form.phone_number}
              onChange={(event) => onChange("phone_number", event.target.value)}
              placeholder={copy.payments.phonePlaceholder}
              className="h-12 w-full rounded-[16px] border border-white/12 bg-white/[0.06] px-4 text-[0.96rem] text-white outline-none placeholder:text-white/34"
            />
          </label>
        </div>

        <label className="block">
          <div className="mb-2 text-[0.82rem] font-semibold text-white/82">
            {copy.payments.eventTypesLabel}
          </div>
          <textarea
            value={form.requested_event_types}
            onChange={(event) => onChange("requested_event_types", event.target.value)}
            placeholder={copy.payments.eventTypesPlaceholder}
            className="min-h-[112px] w-full rounded-[16px] border border-white/12 bg-white/[0.06] px-4 py-3 text-[0.96rem] leading-[1.45] text-white outline-none placeholder:text-white/34"
          />
        </label>

        <label className="block">
          <div className="mb-2 text-[0.82rem] font-semibold text-white/82">
            {copy.payments.paymentMethodLabel}
          </div>
          <select
            value={form.requested_payment_method}
            onChange={(event) => onChange("requested_payment_method", event.target.value)}
            className="h-12 w-full rounded-[16px] border border-white/12 bg-white/[0.06] px-4 text-[0.96rem] text-white outline-none"
          >
            <option value="cash" className="text-black">
              {copy.payments.paymentMethodCash}
            </option>
            <option value="card" className="text-black">
              {copy.payments.paymentMethodCard}
            </option>
            <option value="both" className="text-black">
              {copy.payments.paymentMethodBoth}
            </option>
          </select>
        </label>

        <label className="block">
          <div className="mb-2 text-[0.82rem] font-semibold text-white/82">
            {copy.payments.notesLabel}
          </div>
          <textarea
            value={form.business_notes}
            onChange={(event) => onChange("business_notes", event.target.value)}
            placeholder={copy.payments.notesPlaceholder}
            className="min-h-[96px] w-full rounded-[16px] border border-white/12 bg-white/[0.06] px-4 py-3 text-[0.96rem] leading-[1.45] text-white outline-none placeholder:text-white/34"
          />
        </label>

        <div className="rounded-[18px] border border-white/10 bg-white/[0.04] px-4 py-4">
          <div className="text-[0.88rem] leading-[1.5] text-white/74">
            {copy.payments.formLegalHint}
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={onOpenVerificationTerms}
              className="inline-flex min-h-10 items-center justify-center rounded-[14px] border border-white/12 bg-white/[0.05] px-4 py-2 text-[0.88rem] font-semibold text-white/84"
            >
              {copy.payments.verificationTermsCta}
            </button>
            <button
              type="button"
              onClick={onOpenCommercialConditions}
              className="inline-flex min-h-10 items-center justify-center rounded-[14px] border border-white/12 bg-white/[0.05] px-4 py-2 text-[0.88rem] font-semibold text-white/84"
            >
              {copy.payments.commercialCta}
            </button>
          </div>
          <label className="mt-4 flex items-start gap-3">
            <input
              type="checkbox"
              checked={acceptedVerificationTerms}
              onChange={(event) => onChange("accept_verification_terms", event.target.checked)}
              className="mt-1 h-4 w-4 rounded border-white/20 bg-white/[0.06]"
            />
            <span className="text-[0.88rem] leading-[1.5] text-white/78">
              {copy.payments.requestConsentLabel}
            </span>
          </label>
        </div>
      </div>

      <div className="mt-6 flex flex-col gap-3">
        {error ? (
          <div role="alert" className="rounded-[16px] border border-red-300/30 bg-red-500/15 px-4 py-3 text-[0.9rem] leading-[1.45] text-red-100">
            {error}
          </div>
        ) : null}
        <button
          type="button"
          disabled={submitDisabled}
          onClick={onSubmit}
          className="inline-flex h-12 w-full items-center justify-center overflow-hidden rounded-[18px] bg-[linear-gradient(90deg,#2F67E7_0%,#2FD8F6_100%)] px-5 text-[0.98rem] font-semibold text-white shadow-[0_18px_32px_rgba(47,116,232,0.34)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {submitting ? copy.payments.requestSubmitting : copy.payments.submitRequestCta}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="inline-flex h-12 w-full items-center justify-center rounded-[18px] border border-white/12 bg-white/[0.05] px-5 text-[0.98rem] font-semibold text-white/76"
        >
          {copy.payments.closeRequestCta}
        </button>
      </div>
    </AppModalShell>
  );
}

const SANDBOX_SUMMARY_OVERRIDES = {
  slots_full: {
    host_slots_unlimited: false,
    host_created: 9,
    user_events_count: 2,
    user_concepts_count: 1,
    user_total_creations_count: 3,
    can_create: false,
    can_create_event: false,
    can_create_concept: false,
    available_slots: 0,
    available_event_slots: 0,
    available_concept_slots: 0,
    total_available_slots: 0,
    max_events: 3,
    max_concepts: 3,
    max_total_creations: 3,
    host_creation_block_reason: "slots_exhausted",
  },
};

function resolveScenarioSummary(liveSummary, scenarioId) {
  if (!scenarioId || scenarioId === "live") return liveSummary;
  const override = SANDBOX_SUMMARY_OVERRIDES[scenarioId];
  if (!override) return liveSummary;
  return { ...(liveSummary || {}), ...override };
}

function resolveCreationBlockReason(summary) {
  const explicitReason = String(summary?.host_creation_block_reason || "").trim().toLowerCase();
  if (explicitReason) return explicitReason;
  if (!summary) return "";

  const totalAvailableSlots = numberValue(summary.total_available_slots ?? summary.available_slots);
  const canCreate = Boolean(summary.can_create);
  if (!canCreate && totalAvailableSlots === 0) {
    return "slots_exhausted";
  }
  return "";
}

function resolveUpsellPresentation(copy, reason) {
  switch (reason) {
    case "slots_exhausted":
    default:
      return {
        title: copy.limits.modalTitleSlots,
        body: copy.limits.modalBodySlots,
      };
  }
}

const EventCreateStep0 = ({
  hostRating,
  onClose,
  onProceed,
  onContinueCreation,
  onReadRules,
  onReadHostGuide,
  selectedType = "event",
  onSelectType,
  progressCurrent = 1,
  progressTotal = 6,
  progressInitialWidth = null,
  showHostlessToggle = false,
  hostlessConcept = false,
  onToggleHostlessConcept,
  showSandboxToggle = false,
  sandboxEvent = false,
  onToggleSandboxEvent,
  showRevolutToggle = false,
  useRevolutPayment = false,
  onToggleRevolutPayment,
  autoOpenSlotLimitModal = false,
  sandboxScenarioId = "live",
  hideProceedButton = false,
  initialTab = "overview",
  organizerSection = "panel",
  initialFocusEventId = null,
  initialFeedbackEventId = null,
  initialPanelOpen = false,
  unreadByEventId = {},
  showCreateEventBanner = false,
  onPaymentsCountChange,
}) => {
  const { t, i18n } = useTranslation("events");
  const navigate = useNavigate();
  const location = useLocation();
  const copy = useMemo(() => resolveCopy(i18n), [i18n]);
  const settlementCopy = useMemo(() => resolveSettlementCopy(i18n), [i18n]);
  const manualBankLanguage = String(i18n?.resolvedLanguage || i18n?.language || "en").slice(0, 2).toLowerCase();
  const manualBankCopy = {
    en: { title: "Bank withdrawal", available: "Available in Stripe", pending: "Still processing", fee: "Card processing costs", withdrawable: "You can withdraw", button: "Withdraw to bank account", working: "Starting withdrawal...", refresh: "Refresh balance", paused: "Bank withdrawals are temporarily unavailable.", detail: "You can withdraw settled ticket funds to your linked bank account. Card processing costs are kept aside. VaultGrid commission is 0%. Bank arrival follows Stripe and your bank.", success: "Bank withdrawal started.", error: "Could not load the Stripe balance." },
    ru: { title: "Вывод на банковский счёт", available: "Доступно в Stripe", pending: "Ещё обрабатывается", fee: "Расходы на обработку карт", withdrawable: "Можно вывести", button: "Вывести на счёт", working: "Запускаем вывод...", refresh: "Обновить баланс", paused: "Вывод на банковский счёт временно недоступен.", detail: "Вы можете вывести обработанные платежи за билеты на привязанный банковский счёт. Расходы на обработку карт зарезервированы. Комиссия VaultGrid — 0%. Срок зачисления зависит от Stripe и банка.", success: "Вывод на счёт запущен.", error: "Не удалось загрузить баланс Stripe." },
    sk: { title: "Výplata na bankový účet", available: "Dostupné v Stripe", pending: "Ešte sa spracúva", fee: "Náklady na spracovanie kariet", withdrawable: "Môžete vyplatiť", button: "Vyplatiť na účet", working: "Spúšťa sa výplata...", refresh: "Obnoviť zostatok", paused: "Výplaty na bankový účet sú dočasne nedostupné.", detail: "Spracované platby za lístky si môžete vyplatiť na prepojený bankový účet. Náklady na spracovanie kariet sú rezervované. Provízia VaultGrid je 0 %. Pripísanie závisí od Stripe a banky.", success: "Výplata na účet bola spustená.", error: "Nepodarilo sa načítať zostatok Stripe." },
    cs: { title: "Výplata na bankovní účet", available: "Dostupné ve Stripe", pending: "Ještě se zpracovává", fee: "Náklady na zpracování karet", withdrawable: "Lze vyplatit", button: "Vyplatit na účet", working: "Spouští se výplata...", refresh: "Obnovit zůstatek", paused: "Výplaty na bankovní účet jsou dočasně nedostupné.", detail: "Zpracované platby za vstupenky si můžete vyplatit na propojený bankovní účet. Náklady na zpracování karet jsou rezervované. Provize VaultGrid je 0 %. Připsání závisí na Stripe a bance.", success: "Výplata na účet byla zahájena.", error: "Nepodařilo se načíst zůstatek Stripe." },
  }[["ru", "sk", "cs"].includes(manualBankLanguage) ? manualBankLanguage : "en"];
  const hostFeedbackUnreadCtx = useHostFeedbackUnread();
  const hostFeedbackUnreadRefetch = hostFeedbackUnreadCtx?.refetch;
  const isLocalDebugAvailable = isLocalDevelopmentHost();
  const queryParams = useMemo(
    () => new URLSearchParams(location.search || ""),
    [location.search]
  );
  const debugPanelEnabled = isLocalDebugAvailable && queryParams.get("host_panel_debug") === "1";
  const [debugScenario, setDebugScenario] = useState(
    debugPanelEnabled ? queryParams.get("host_panel_scenario") || "live" : "live"
  );
  const effectiveScenarioId = sandboxScenarioId !== "live" ? sandboxScenarioId : debugScenario;

  const isTicketsSection = organizerSection === "tickets" || initialTab === "payments";
  const [activeTab, setActiveTab] = useState(
    isTicketsSection ? "payments" : HOST_PANEL_TAB_VALUES.includes(initialTab) ? initialTab : "overview"
  );
  const [eventFilter, setEventFilter] = useState(null);
  const [visibleEventsCount, setVisibleEventsCount] = useState(6);
  const [panelData, setPanelData] = useState(null);
  const [loadingPanel, setLoadingPanel] = useState(false);
  const [panelError, setPanelError] = useState("");
  const [limitUpsellOpen, setLimitUpsellOpen] = useState(false);
  const [paidHostRequestOpen, setPaidHostRequestOpen] = useState(false);
  const [showOrganizerPanel, setShowOrganizerPanel] = useState(Boolean(initialPanelOpen));
  const [payoutInfoEvent, setPayoutInfoEvent] = useState(null);
  const [paymentMetricsHelpOpen, setPaymentMetricsHelpOpen] = useState(false);
  const [paymentMetricsHelpMode, setPaymentMetricsHelpMode] = useState("event_hold");
  const [feedbackEvent, setFeedbackEvent] = useState(null);
  const [expandedSafetyReportEventIds, setExpandedSafetyReportEventIds] = useState(
    () => new Set()
  );
  const [paymentsActionError, setPaymentsActionError] = useState("");
  const [paymentsActionNotice, setPaymentsActionNotice] = useState("");
  const [stripeActionBusy, setStripeActionBusy] = useState("");
  const [manualBankSnapshot, setManualBankSnapshot] = useState(null);
  const [manualBankLoading, setManualBankLoading] = useState(false);
  const [invoiceCheckoutPendingId, setInvoiceCheckoutPendingId] = useState(null);
  const [tbankRecipientType, setTBankRecipientType] = useState("sole_proprietor");
  const [stripeCountryModalOpen, setStripeCountryModalOpen] = useState(false);
  const [stripeConnectCountry, setStripeConnectCountry] = useState(DEFAULT_STRIPE_CONNECT_COUNTRY);
  const [paidHostRequestSubmitting, setPaidHostRequestSubmitting] = useState(false);
  const [expandedPaidEventIds, setExpandedPaidEventIds] = useState(() => {
    const parsed = Number(initialFocusEventId);
    return Number.isFinite(parsed) && parsed > 0 ? new Set([parsed]) : new Set();
  });
  const [selectedAnalyticsEventId, setSelectedAnalyticsEventId] = useState(null);
  const [eventAnalytics, setEventAnalytics] = useState({});
  const [analyticsLoadingId, setAnalyticsLoadingId] = useState(null);
  const [analyticsErrorId, setAnalyticsErrorId] = useState(null);
  const [analyticsPreviews, setAnalyticsPreviews] = useState({});
  const [analyticsPreviewsLoading, setAnalyticsPreviewsLoading] = useState(false);
  const [analyticsPreviewsLoaded, setAnalyticsPreviewsLoaded] = useState(false);
  const [analyticsPreviewsError, setAnalyticsPreviewsError] = useState(false);
  const [analyticsPreviewsRetry, setAnalyticsPreviewsRetry] = useState(0);
  const paidEventRefs = useRef(new Map());
  const hostEventRefs = useRef(new Map());
  const initialFeedbackHandledRef = useRef(null);
  const embeddedOrganizerPanel = Boolean(hideProceedButton && initialPanelOpen);
  const [paidHostRequestForm, setPaidHostRequestForm] = useState({
    business_country: "",
    legal_form: "",
    business_legal_name: "",
    social_profile: "",
    business_social_profile: "",
    age: "",
    phone_number: "",
    requested_payment_method: "card",
    requested_event_types: "",
    business_notes: "",
    accept_verification_terms: false,
  });
  const autoPromptHandledRef = useRef(false);
  useEffect(() => {
    setVisibleEventsCount(6);
  }, [activeTab, eventFilter]);

  useEffect(() => {
    setActiveTab(
      isTicketsSection ? "payments" : HOST_PANEL_TAB_VALUES.includes(initialTab) ? initialTab : "overview"
    );
  }, [initialTab, isTicketsSection]);

  useEffect(() => {
    setShowOrganizerPanel(Boolean(initialPanelOpen));
  }, [initialPanelOpen]);

  useEffect(() => {
    const parsed = Number(initialFocusEventId);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    if (isTicketsSection) setActiveTab("payments");
    setExpandedPaidEventIds(new Set([parsed]));
  }, [initialFocusEventId, isTicketsSection]);

  useEffect(() => {
    initialFeedbackHandledRef.current = null;
  }, [initialFeedbackEventId]);

  useEffect(() => {
    if (!debugPanelEnabled) {
      setDebugScenario("live");
      return;
    }
    setDebugScenario(queryParams.get("host_panel_scenario") || "live");
  }, [debugPanelEnabled, queryParams]);

  useEffect(() => {
    let cancelled = false;
    setLoadingPanel(true);
    setPanelError("");

    api
      .get("/events/organizer-panel/", {
        meta: { suppressGlobalError: true },
        suppressUnauthorizedRedirect: true,
      })
      .then(({ data }) => {
        if (!cancelled) setPanelData(data || null);
      })
      .catch((error) => {
        if (cancelled) return;
        console.error("[EventCreateStep0] organizer panel load failed", error);
        setPanelError(copy.loadError);
      })
      .finally(() => {
        if (!cancelled) setLoadingPanel(false);
      });

    return () => {
      cancelled = true;
    };
  }, [copy.loadError]);

  const liveSummary = panelData?.summary || hostRating || null;
  const summary = resolveScenarioSummary(
    liveSummary,
    debugPanelEnabled || sandboxScenarioId !== "live" ? effectiveScenarioId : "live"
  );
  const recentEvents = useMemo(
    () => (Array.isArray(panelData?.recent_events) ? panelData.recent_events : []),
    [panelData?.recent_events]
  );
  const analyticsEvents = useMemo(
    () => recentEvents.filter((event) => event?.type !== "concept"),
    [recentEvents]
  );
  useEffect(() => {
    if (activeTab !== "analytics" || !panelData || analyticsEvents.length === 0) return undefined;
    let cancelled = false;
    setAnalyticsPreviewsLoading(true);
    setAnalyticsPreviewsLoaded(false);
    setAnalyticsPreviewsError(false);
    api.get("/events/host-analytics/my-events/", {
      meta: { suppressGlobalError: true },
      suppressUnauthorizedRedirect: true,
    }).then(({ data }) => {
      if (cancelled) return;
      const next = {};
      (Array.isArray(data?.rows) ? data.rows : []).forEach((row) => {
        next[Number(row.event_id)] = row;
      });
      setAnalyticsPreviews(next);
      setAnalyticsPreviewsLoaded(true);
    }).catch(() => {
      if (!cancelled) setAnalyticsPreviewsError(true);
    }).finally(() => {
      if (!cancelled) setAnalyticsPreviewsLoading(false);
    });
    return () => { cancelled = true; };
  }, [activeTab, analyticsEvents.length, analyticsPreviewsRetry, panelData]);
  const loadEventAnalytics = useCallback(async (eventId, { force = false } = {}) => {
    if (analyticsLoadingId !== null) {
      if (analyticsLoadingId === eventId) {
        setSelectedAnalyticsEventId((current) => current === eventId ? null : eventId);
      }
      return;
    }
    if (!force && eventAnalytics[eventId]) {
      setSelectedAnalyticsEventId((current) => current === eventId ? null : eventId);
      return;
    }
    setSelectedAnalyticsEventId(eventId);
    setAnalyticsLoadingId(eventId);
    setAnalyticsErrorId(null);
    try {
      const { data } = await api.get(`/events/${eventId}/host-analytics/summary/`, {
        meta: { suppressGlobalError: true },
        suppressUnauthorizedRedirect: true,
      });
      setEventAnalytics((current) => ({ ...current, [eventId]: data?.total || {} }));
      setAnalyticsPreviews((current) => ({
        ...current,
        [eventId]: { ...(current[eventId] || {}), card_opens: data?.total?.card_opens || 0 },
      }));
    } catch {
      setAnalyticsErrorId(eventId);
    } finally {
      setAnalyticsLoadingId(null);
    }
  }, [analyticsLoadingId, eventAnalytics]);
  const eventAttentionTotal = useMemo(
    () =>
      Object.values(unreadByEventId || {}).reduce(
        (total, count) => total + Math.max(0, Number(count) || 0),
        0
      ),
    [unreadByEventId]
  );
  const paidEvents = recentEvents.filter(
    (event) => String(event.pricing_model || "").toLowerCase() === "host_paid"
  );
  const hasHeldPaidEvents = paidEvents.some((event) =>
    String(event.payment_summary?.settlement_mode || event.settlement_mode || "event_hold").toLowerCase() !== "direct"
  );
  const paymentsSummary = panelData?.payments_summary || {};
  const manualBankEnabled = Boolean(panelData?.manual_bank_payouts_enabled);
  const manualBankPaused = Boolean(panelData?.manual_bank_payouts_paused || manualBankSnapshot?.paused);
  useEffect(() => {
    if (!manualBankEnabled || activeTab !== "payments") return;
    let cancelled = false;
    setManualBankLoading(true);
    api.get("/events/organizer-panel/stripe/manual-bank-payout/")
      .then(({ data }) => { if (!cancelled) setManualBankSnapshot(data); })
      .catch(() => { if (!cancelled) setPaymentsActionError(manualBankCopy.error); })
      .finally(() => { if (!cancelled) setManualBankLoading(false); });
    return () => { cancelled = true; };
  }, [manualBankEnabled, activeTab, manualBankCopy.error]);
  useEffect(() => {
    onPaymentsCountChange?.(numberValue(paymentsSummary.recent_deposits_count));
  }, [onPaymentsCountChange, paymentsSummary.recent_deposits_count]);
  const paidHostAgreement = panelData?.paid_host_agreement || null;
  const paymentProvider = String(panelData?.payment_provider || "stripe").toLowerCase();
  const isTBankPayments = paymentProvider === "tbank";
  const tbankCheckout = panelData?.tbank_checkout || {};
  const tbankDirectAcquiringReady = Boolean(tbankCheckout?.direct_acquiring_ready);
  const tbankRequiresRecipient = Boolean(tbankCheckout?.multisplit_enabled);
  const tbankRecipient = panelData?.tbank_recipient || null;
  const stripe = panelData?.stripe || null;
  const stripeCountryOptions = useMemo(() => buildStripeCountryOptions(stripe), [stripe]);
  const paidHostAccessRequest = panelData?.paid_host_access_request || null;
  const reviewedStripeCountry = String(paidHostAccessRequest?.status || "").toLowerCase() === "approved"
    ? normalizeStripeCountryCode(paidHostAccessRequest?.business_country)
    : "";
  const approvedPaymentOffer = paidHostAgreement?.offer || paidHostAccessRequest || {};
  const approvedSettlementMode = String(approvedPaymentOffer.settlement_mode || "event_hold").toLowerCase();
  const waiverEnd = approvedPaymentOffer.commission_waiver_until
    ? new Date(`${String(approvedPaymentOffer.commission_waiver_until).slice(0, 10)}T12:00:00`).toLocaleDateString(
        String(i18n?.resolvedLanguage || i18n?.language || "en")
      )
    : "";
  const hostSlotsUnlimited = Boolean(summary?.host_slots_unlimited);
  const totalAvailableSlots = hostSlotsUnlimited
    ? null
    : numberValue(summary?.total_available_slots ?? summary?.available_slots ?? 0);
  const hostCreationBlockReason = resolveCreationBlockReason(summary);
  const maxTotalCreations = hostSlotsUnlimited
    ? null
    : numberValue(summary?.max_total_creations ?? summary?.max_concepts ?? 0);
  const usedCreationSlots = numberValue(
    summary?.user_total_creations_count ?? summary?.user_events_count + summary?.user_concepts_count
  );
  const shouldAutoPromptSlotLimit =
    Boolean(summary) &&
    !hostSlotsUnlimited &&
    Boolean(hostCreationBlockReason) &&
    (autoOpenSlotLimitModal || debugPanelEnabled || sandboxScenarioId !== "live");
  const modalPresentation = resolveUpsellPresentation(copy, hostCreationBlockReason);

  useEffect(() => {
    const payloadCountry = normalizeStripeCountryCode(stripe?.country);
    if (payloadCountry) {
      setStripeConnectCountry(payloadCountry);
      return;
    }
    if (reviewedStripeCountry) {
      setStripeConnectCountry(reviewedStripeCountry);
      return;
    }
    setStripeConnectCountry((current) => {
      if (stripeCountryOptions.some((option) => option.code === current)) return current;
      return stripeCountryOptions[0]?.code || DEFAULT_STRIPE_CONNECT_COUNTRY;
    });
  }, [stripe?.country, reviewedStripeCountry, stripeCountryOptions]);

  useEffect(() => {
    if (!shouldAutoPromptSlotLimit) {
      autoPromptHandledRef.current = false;
      return;
    }
    if (autoPromptHandledRef.current) return;
    autoPromptHandledRef.current = true;
    setLimitUpsellOpen(true);
  }, [shouldAutoPromptSlotLimit]);

  useEffect(() => {
    const parsed = Number(initialFocusEventId);
    if (!Number.isFinite(parsed) || parsed <= 0 || activeTab !== "payments") return undefined;
    const timer = window.setTimeout(() => {
      const node = paidEventRefs.current.get(parsed);
      node?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [activeTab, initialFocusEventId, paidEvents.length]);

  useEffect(() => {
    const parsed = Number(initialFeedbackEventId);
    if (!Number.isFinite(parsed) || parsed <= 0 || recentEvents.length === 0) return undefined;
    if (initialFeedbackHandledRef.current === parsed) return undefined;
    const targetEvent = recentEvents.find((event) => Number(event?.event_id) === parsed);
    if (!targetEvent || numberValue(targetEvent.anonymous_host_feedback_count) <= 0) {
      return undefined;
    }

    initialFeedbackHandledRef.current = parsed;
    setActiveTab("events");
    setEventFilter("finished");
    setFeedbackEvent(targetEvent);
    const timer = window.setTimeout(() => {
      hostEventRefs.current.get(parsed)?.scrollIntoView?.({
        behavior: "smooth",
        block: "center",
      });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [initialFeedbackEventId, recentEvents]);

  useEffect(() => {
    if (queryParams.get("stripe_return") !== "1") return undefined;

    let cancelled = false;
    const timers = [];
    const refreshStripeStatus = async () => {
      try {
        const { data } = await api.get("/events/organizer-panel/stripe/status/", {
          meta: { suppressGlobalError: true },
          suppressUnauthorizedRedirect: true,
        });
        if (!cancelled && data?.stripe) {
          setPanelData((current) => ({ ...(current || {}), stripe: data.stripe }));
        }
      } catch (error) {
        console.error("[EventCreateStep0] stripe status refresh after return failed", error);
      }
    };

    timers.push(window.setTimeout(refreshStripeStatus, 700));
    timers.push(window.setTimeout(refreshStripeStatus, 2200));

    return () => {
      cancelled = true;
      timers.forEach((timerId) => window.clearTimeout(timerId));
    };
  }, [queryParams]);

  useEffect(() => {
    const stripeConnectedForRefresh = Boolean(stripe?.connected_account_id);
    const stripeReadyForRefresh = Boolean(stripe?.can_accept_paid_events);
    if (activeTab !== "payments" || !stripeConnectedForRefresh || stripeReadyForRefresh) {
      return undefined;
    }

    const refreshStripeStatus = async () => {
      try {
        const { data } = await api.get("/events/organizer-panel/stripe/status/", {
          meta: { suppressGlobalError: true },
          suppressUnauthorizedRedirect: true,
        });
        if (data?.stripe) {
          setPanelData((current) => ({ ...(current || {}), stripe: data.stripe }));
        }
      } catch (error) {
        console.error("[EventCreateStep0] stripe status refresh after resume failed", error);
      }
    };
    const handleResume = () => {
      void refreshStripeStatus();
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") handleResume();
    };

    window.addEventListener("vg:native-resume", handleResume);
    window.addEventListener("focus", handleResume);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      window.removeEventListener("vg:native-resume", handleResume);
      window.removeEventListener("focus", handleResume);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [activeTab, stripe?.can_accept_paid_events, stripe?.connected_account_id]);

  useEffect(() => {
    if (activeTab !== "payments") return;
    if (!numberValue(paymentsSummary.recent_deposits_count)) return;
    api
      .post("/events/organizer-panel/payments/read/", {})
      .catch((error) => {
        console.error("[EventCreateStep0] failed to mark payments tab seen", error);
      });
    setPanelData((current) => {
      const next = { ...(current || {}) };
      const nextSummary = { ...(next.payments_summary || {}) };
      nextSummary.recent_deposits_count = 0;
      next.payments_summary = nextSummary;
      return next;
    });
  }, [activeTab, paymentsSummary.recent_deposits_count]);

  const handleFeedbackSeen = useCallback(
    (eventId) => {
      const parsed = Number(eventId);
      if (!Number.isFinite(parsed) || parsed <= 0) return;
      setPanelData((current) => {
        const next = { ...(current || {}) };
        next.recent_events = Array.isArray(next.recent_events)
          ? next.recent_events.map((event) =>
              Number(event?.event_id) === parsed
                ? { ...event, anonymous_host_feedback_unread_count: 0 }
                : event
            )
          : next.recent_events;
        return next;
      });
      setFeedbackEvent((current) =>
        Number(current?.event_id) === parsed
          ? { ...current, anonymous_host_feedback_unread_count: 0 }
          : current
      );
      void hostFeedbackUnreadRefetch?.();
    },
    [hostFeedbackUnreadRefetch]
  );

  const closeFeedbackModal = useCallback(() => {
    setFeedbackEvent(null);
    const params = new URLSearchParams(location.search || "");
    if (!params.has("host_comments_event")) return;
    params.delete("host_comments_event");
    const search = params.toString();
    navigate(
      {
        pathname: location.pathname,
        search: search ? `?${search}` : "",
      },
      {
        replace: true,
        state: location.state || null,
      }
    );
  }, [location.pathname, location.search, location.state, navigate]);

  const toggleSafetyReportDetails = useCallback((eventId) => {
    const parsed = Number(eventId);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    setExpandedSafetyReportEventIds((current) => {
      const next = new Set(current);
      if (next.has(parsed)) {
        next.delete(parsed);
      } else {
        next.add(parsed);
      }
      return next;
    });
  }, []);

  if (!showOrganizerPanel) {
    const normalizedSelectedType = selectedType === "concept" ? "concept" : "event";
    const showHostlessConceptToggle = Boolean(
      showHostlessToggle && normalizedSelectedType === "concept"
    );
    const safeProgressTotal = Math.max(1, Number(progressTotal) || 6);
    const safeProgressCurrent = Math.min(
      safeProgressTotal,
      Math.max(1, Number(progressCurrent) || 1)
    );
    const progressWidth = `${(safeProgressCurrent / safeProgressTotal) * 100}%`;
    const introOptions = EVENT_CREATE_INTRO_TYPES.map((type) => ({
      type,
      title: t(`event_create.format_step.${type}_title`),
      line1: t(`event_create.format_step.${type}_line1`),
      line2: t(`event_create.format_step.${type}_line2`),
      line3: t(`event_create.format_step.${type}_line3`),
      short: t(`event_create.format_step.${type}_short`),
      body: t(`event_create.format_step.${type}_body`),
    }));
    const activeInfo = introOptions.find((option) => option.type === normalizedSelectedType) || introOptions[0];

    return (
      <div
        data-testid="event-create-intro-shell"
        className="relative h-full min-h-0 w-full overflow-hidden text-white lg:h-full"
        style={EVENT_CREATE_INTRO_BACKGROUND_STYLE}
      >
        <div
          data-testid="event-create-intro-content"
          className="relative mx-auto flex h-full min-h-0 w-full max-w-[402px] flex-col px-[30px] lg:max-w-[520px] lg:px-[26px]"
        >
          <div className="absolute left-[25px] top-[calc(20px+var(--vg-safe-area-top))] z-40 h-9 w-9 rounded-full bg-white/10" />
          <motion.button
            type="button"
            onClick={onClose}
            whileHover={PRESS_HOVER}
            whileTap={SUBTLE_PRESS_TAP}
            transition={PRESS_TRANSITION}
            aria-label="Back"
            className="absolute left-[30px] top-[calc(26px+var(--vg-safe-area-top))] z-50 flex h-6 w-6 items-center justify-center text-white"
          >
            <svg aria-hidden="true" viewBox="0 0 24 24" className="h-6 w-6" fill="none">
              <path d="M15 6L9 12L15 18" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </motion.button>

          <div
            className="absolute left-1/2 top-[calc(36px+var(--vg-safe-area-top))] z-40 h-[5px] w-[230px] -translate-x-1/2 overflow-hidden rounded-[27px] bg-white/20"
            data-event-create-progress="track"
          >
            <motion.div
              className="h-full rounded-[27px] bg-white"
              data-event-create-progress="fill"
              initial={{ width: progressInitialWidth || progressWidth }}
              animate={{ width: progressWidth }}
              transition={{ duration: 0.42, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>

          <div
            data-testid="event-create-intro-scroll"
            className="-mx-6 min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 pb-[18px] [-webkit-overflow-scrolling:touch] custom-scrollbar"
            style={{ scrollPaddingBottom: "18px" }}
          >
          <h1 className="mx-auto mt-[calc(82px+var(--vg-safe-area-top))] w-[286px] text-center text-[32px] font-semibold leading-[1.1] tracking-[0] text-white max-[850px]:mt-[calc(66px+var(--vg-safe-area-top))] max-[850px]:text-[30px]">
            {t("event_create.format_step.title")}
          </h1>

          <div className="mt-[32px] grid grid-cols-2 gap-3 max-[850px]:mt-[12px]">
            {introOptions.map((option) => {
              const active = normalizedSelectedType === option.type;
              const outerBorder = active
                ? "rgba(255,255,255,0.92)"
                : "rgba(255,255,255,0.32)";
              const outerBorderFade = active
                ? "rgba(255,255,255,0)"
                : "rgba(255,255,255,0)";
              const innerBorder = active
                ? "rgba(255,255,255,0.9)"
                : "rgba(255,255,255,0.34)";
              const innerBorderFade = active
                ? "rgba(255,255,255,0)"
                : "rgba(255,255,255,0)";
              return (
                <motion.button
                  key={option.type}
                  type="button"
                  onClick={() => onSelectType?.(option.type)}
                  aria-pressed={active}
                  whileHover={PRESS_HOVER}
                  whileTap={SUBTLE_PRESS_TAP}
                  transition={PRESS_TRANSITION}
                  className="relative h-[232px] overflow-hidden rounded-[18px] bg-transparent px-3 text-center transition max-[850px]:h-[184px]"
                >
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 h-[310px] rounded-[18px] p-px"
                    style={{
                      background: `linear-gradient(180deg, ${outerBorder} 0%, ${outerBorder} 42%, ${outerBorderFade} 78%)`,
                      WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                    }}
                  />
                  <span className="absolute left-2 right-2 top-0 flex h-[54px] items-center justify-center text-[25px] font-semibold leading-[1.05] text-white max-[850px]:h-[50px]">
                    {option.title}
                  </span>
                  <span
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-[-1px] top-[54px] h-[292px] rounded-[18px] p-px max-[850px]:top-[50px]"
                    style={{
                      background: `linear-gradient(180deg, ${innerBorder} 0%, ${innerBorder} 44%, ${innerBorderFade} 76%)`,
                      WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)",
                      WebkitMaskComposite: "xor",
                      maskComposite: "exclude",
                    }}
                  />
                  <span
                    className="absolute left-[10px] right-[10px] top-[80px] z-[1] block font-semibold leading-[1.16] text-white max-[850px]:top-[66px]"
                    style={{ fontSize: "clamp(14px, 4.1vw, 17px)" }}
                  >
                    {option.line1}
                  </span>
                  <span
                    className="absolute left-[10px] right-[10px] top-[130px] z-[1] block font-semibold leading-[1.16] text-white max-[850px]:top-[104px]"
                    style={{ fontSize: "clamp(13px, 3.7vw, 16px)" }}
                  >
                    {option.line2}
                  </span>
                  <span
                    className="absolute bottom-[20px] left-[8px] right-[8px] z-[1] block font-semibold leading-[1.16] text-white max-[850px]:bottom-[10px]"
                    style={{ fontSize: "clamp(13px, 3.7vw, 16px)" }}
                  >
                    {option.line3}
                  </span>
                </motion.button>
              );
            })}
          </div>

          <div className="mt-[36px] text-center text-[15px] font-normal leading-4 text-white max-[850px]:mt-[14px]">
            {t("event_create.format_step.choose_type")}
          </div>

          <div className="mt-2 grid grid-cols-2 gap-[7px]">
            {introOptions.map((option) => {
              const active = normalizedSelectedType === option.type;
              return (
                <motion.button
                  key={`${option.type}-pill`}
                  type="button"
                  onClick={() => onSelectType?.(option.type)}
                  whileHover={PRESS_HOVER}
                  whileTap={SUBTLE_PRESS_TAP}
                  transition={PRESS_TRANSITION}
                  className={`relative flex h-[50px] items-center justify-between rounded-[42px] border bg-black/10 px-[15px] text-[15px] font-medium leading-[22px] text-white transition ${
                    active
                      ? "border-white/45 shadow-[0_0_18px_rgba(255,255,255,0.32)]"
                      : "border-white/20 opacity-70"
                  }`}
                >
                  <span>{option.title}</span>
                  <span className="relative flex h-5 w-5 items-center justify-center rounded-full border border-white bg-transparent">
                    {active ? <span className="h-3 w-3 rounded-full bg-white" /> : null}
                  </span>
                </motion.button>
              );
            })}
          </div>

          {(showHostlessConceptToggle || showSandboxToggle || showRevolutToggle) && (
            <div className="mt-[10px] grid grid-cols-[repeat(auto-fit,minmax(96px,1fr))] gap-2">
              {showHostlessConceptToggle ? (
                <label
                  className="flex h-9 items-center gap-2 rounded-[18px] border border-white/12 bg-black/10 px-3 text-[11px] font-semibold leading-none text-white/64"
                  title={t("event_create.format_step.hostless_toggle")}
                >
                  <input
                    type="checkbox"
                    checked={Boolean(hostlessConcept)}
                    onChange={(event) => onToggleHostlessConcept?.(event.target.checked)}
                    className="h-3.5 w-3.5 shrink-0 accent-[#3e6ffb]"
                  />
                  <span className="truncate">
                    {t("event_create.format_step.hostless_short", { defaultValue: "Hostless" })}
                  </span>
                </label>
              ) : null}
              {showSandboxToggle ? (
                <label className="flex h-9 items-center gap-2 rounded-[18px] border border-white/12 bg-black/10 px-3 text-[11px] font-semibold leading-none text-white/64">
                  <input
                    type="checkbox"
                    checked={Boolean(sandboxEvent)}
                    onChange={(event) => onToggleSandboxEvent?.(event.target.checked)}
                    className="h-3.5 w-3.5 shrink-0 accent-[#3e6ffb]"
                  />
                  <span className="truncate">
                    {t("event_create.format_step.sandbox_short", { defaultValue: "Sandbox" })}
                  </span>
                </label>
              ) : null}
              {showRevolutToggle ? (
                <label className="flex h-9 items-center gap-2 rounded-[18px] border border-white/12 bg-black/10 px-3 text-[11px] font-semibold leading-none text-white/64">
                  <input
                    type="checkbox"
                    checked={Boolean(useRevolutPayment)}
                    onChange={(event) => onToggleRevolutPayment?.(event.target.checked)}
                    className="h-3.5 w-3.5 shrink-0 accent-[#3e6ffb]"
                  />
                  <span className="truncate">
                    {t("event_create.format_step.revolut_short", { defaultValue: "Revolut payments" })}
                  </span>
                </label>
              ) : null}
            </div>
          )}

          <div className="mt-[24px] text-center max-[850px]:mt-[12px]">
            <motion.button
              type="button"
              whileHover={PRESS_HOVER}
              whileTap={SUBTLE_PRESS_TAP}
              transition={PRESS_TRANSITION}
              className="inline-flex items-center gap-1 text-[12px] font-normal leading-4 text-white/50 underline"
            >
              <svg aria-hidden="true" viewBox="0 0 24 24" className="h-[15px] w-[15px] rotate-180" fill="none">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
                <path d="M12 8v4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                <path d="M12 16h.01" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
              </svg>
              <span>
                <span className="font-semibold italic">{activeInfo.title}</span>
                {activeInfo.short}
              </span>
            </motion.button>
            <p className="mx-auto mt-[7px] max-w-[316px] text-[12px] font-normal leading-4 text-white/50">
              {activeInfo.body}
            </p>
          </div>
          </div>

          <div
            data-testid="event-create-persistent-actions"
            className="-mx-[30px] mt-auto shrink-0 space-y-[10px] px-[30px] pb-[max(env(safe-area-inset-bottom,0px),14px)] pt-[24px]"
          >
            <div className="grid grid-cols-2 gap-[10px]">
              <motion.button
                type="button"
                onClick={onReadRules}
                whileHover={PRESS_HOVER}
                whileTap={PRESS_TAP}
                transition={PRESS_TRANSITION}
                className="min-h-[55px] whitespace-nowrap rounded-[100px] border border-white/25 bg-white/10 px-3 text-[14px] font-semibold leading-4 text-white"
              >
                {copy.rulesCta}
              </motion.button>
              <motion.button
                type="button"
                onClick={onReadHostGuide}
                whileHover={PRESS_HOVER}
                whileTap={PRESS_TAP}
                transition={PRESS_TRANSITION}
                className="min-h-[55px] whitespace-nowrap rounded-[100px] border border-white/25 bg-white/10 px-3 text-[14px] font-semibold leading-4 text-white"
              >
                {copy.guideCta}
              </motion.button>
            </div>
            <motion.button
              type="button"
              onClick={() => (onContinueCreation || onProceed)?.(summary?.host_created)}
              whileHover={PRESS_HOVER}
              whileTap={PRESS_TAP}
              transition={PRESS_TRANSITION}
              className="h-[55px] w-full rounded-[100px] border border-white/20 bg-[#3e6ffb] text-[15px] font-semibold leading-4 text-white"
            >
              {t("event_create.format_step.continue")}
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  if (!summary) {
    return (
      <div
        className={`relative w-full text-white ${
          embeddedOrganizerPanel
            ? "min-h-full"
            : "min-h-[calc(100dvh-var(--bottom-nav-height,72px))] lg:min-h-screen"
        }`}
        style={embeddedOrganizerPanel ? undefined : EVENT_CREATE_INTRO_BACKGROUND_STYLE}
      >
        <div
          className={`mx-auto w-full max-w-[560px] px-4 md:px-5 lg:max-w-[780px] ${
            embeddedOrganizerPanel
              ? "pb-[calc(var(--bottom-nav-clearance,112px)+40px)] pt-0 lg:px-0"
              : "pb-[calc(var(--bottom-nav-clearance,112px)+148px)] pt-[calc(16px+var(--vg-safe-area-top))] lg:pb-28 lg:px-6 lg:pt-6"
          }`}
        >
          {!embeddedOrganizerPanel ? (
            <div className="relative pr-12">
              <h1 className="text-[2rem] font-extrabold leading-[1.02] text-white">
                {copy.title}
              </h1>
              <div className="absolute right-0 top-0">
                <VaultModalCloseButton onClick={onClose} aria-label={copy.closeAria} />
              </div>
            </div>
          ) : null}

          {showCreateEventBanner ? (
            <div
              className={`mx-auto mb-3 ${
                embeddedOrganizerPanel ? EMBEDDED_ORGANIZER_CONTROL_WIDTH_CLASS : "w-full"
              }`}
            >
              <CreateEventBanner onClick={() => onProceed?.(summary?.host_created)} disabled={!onProceed} mode="host-create" />
            </div>
          ) : null}

          {!isTicketsSection ? (
            <HostPanelTabs
              copy={copy}
              activeTab={activeTab}
              onChange={setActiveTab}
              eventsCount={eventAttentionTotal}
              compact={embeddedOrganizerPanel}
            />
          ) : null}
          <HostPanelSkeleton activeTab={activeTab} />
        </div>
      </div>
    );
  }

  const openPremiumPage = () => {
    navigate("/menu/subscription?src=host_creation_limit");
  };
  const organizerPaymentsReturn = {
    returnTo: ORGANIZER_PAYMENTS_RETURN_TO,
    returnState: ORGANIZER_PAYMENTS_RETURN_STATE,
  };
  const openCommercialConditions = () => {
    const lang = String(i18n?.resolvedLanguage || i18n?.language || "").toLowerCase();
    if (lang.startsWith("ru")) {
      navigate("/commercial-conditions/ru", { state: organizerPaymentsReturn });
      return;
    }
    if (lang.startsWith("sk") || lang.startsWith("cs")) {
      navigate("/commercial-conditions/sk", { state: organizerPaymentsReturn });
      return;
    }
    if (lang.startsWith("en")) {
      navigate("/commercial-conditions/en", { state: organizerPaymentsReturn });
      return;
    }
    navigate("/commercial-conditions", { state: organizerPaymentsReturn });
  };
  const openVerificationTerms = () => {
    const lang = String(i18n?.resolvedLanguage || i18n?.language || "").toLowerCase();
    if (lang.startsWith("ru")) {
      navigate("/paid-host-verification-terms/ru", { state: organizerPaymentsReturn });
      return;
    }
    if (lang.startsWith("sk") || lang.startsWith("cs")) {
      navigate("/paid-host-verification-terms/sk", { state: organizerPaymentsReturn });
      return;
    }
    if (lang.startsWith("en")) {
      navigate("/paid-host-verification-terms/en", { state: organizerPaymentsReturn });
      return;
    }
    navigate("/paid-host-verification-terms", { state: organizerPaymentsReturn });
  };
  const openPaidHostAgreement = () => {
    const lang = String(i18n?.resolvedLanguage || i18n?.language || "").toLowerCase();
    if (lang.startsWith("ru")) {
      navigate("/paid-host-agreement/ru", { state: organizerPaymentsReturn });
      return;
    }
    if (lang.startsWith("sk") || lang.startsWith("cs")) {
      navigate("/paid-host-agreement/sk", { state: organizerPaymentsReturn });
      return;
    }
    if (lang.startsWith("en")) {
      navigate("/paid-host-agreement/en", { state: organizerPaymentsReturn });
      return;
    }
    navigate("/paid-host-agreement", { state: organizerPaymentsReturn });
  };
  const openPaidHostBilling = () => {
    const lang = String(i18n?.resolvedLanguage || i18n?.language || "").toLowerCase();
    const path = lang.startsWith("ru") ? "/paid-host-agreement/ru"
      : lang.startsWith("sk") || lang.startsWith("cs") ? "/paid-host-agreement/sk"
      : "/paid-host-agreement/en";
    navigate(`${path}?mode=billing`, { state: organizerPaymentsReturn });
  };
  const performanceCards = [
    {
      label: copy.metrics.totalCreated,
      value: formatCompactMetricNumber(summary.host_created),
    },
    {
      label: copy.metrics.activeEvents,
      value: formatCompactMetricNumber(summary.user_events_count),
    },
    {
      label: copy.metrics.activeConcepts,
      value: formatCompactMetricNumber(summary.user_concepts_count),
    },
    {
      label: copy.metrics.finished,
      value: formatCompactMetricNumber(summary.host_successfully_finished),
    },
    {
      label: copy.metrics.canceled,
      value: formatCompactMetricNumber(summary.host_canceled),
    },
    {
      label: copy.metrics.postponed,
      value: formatCompactMetricNumber(summary.host_postponed),
    },
  ];
  const attendanceCards = [
    {
      label: copy.metrics.registered,
      value: formatCompactMetricNumber(summary.registered_at_start_total),
    },
    {
      label: copy.metrics.confirmed,
      value: formatCompactMetricNumber(summary.confirmed_attendance_total),
    },
    {
      label: copy.metrics.manualConfirmed,
      value: formatCompactMetricNumber(summary.host_manual_attendance_confirmed),
    },
    { label: copy.metrics.attendanceRate, value: formatPercent(summary.attendance_rate_percent) },
  ];
  const analyticsCards = [
    {
      label: copy.metrics.views,
      value: formatCompactMetricNumber(summary.analytics?.card_impressions),
    },
    {
      label: copy.metrics.opens,
      value: formatCompactMetricNumber(summary.analytics?.card_opens),
    },
    {
      label: copy.metrics.uniqueViewers,
      value: formatCompactMetricNumber(summary.analytics?.unique_viewers_lifetime),
    },
    {
      label: copy.metrics.uniqueOpeners,
      value: formatCompactMetricNumber(summary.analytics?.unique_openers_lifetime),
    },
  ];

  const averageEventRating = numberValue(summary.average_event_rating);
  const togglePaidEventDetails = (eventId) => {
    const parsed = Number(eventId);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    setExpandedPaidEventIds((current) => current.has(parsed) ? new Set() : new Set([parsed]));
  };

  const summaryCurrency = paymentsSummary.currency || (isTBankPayments ? "RUB" : "EUR");
  const paymentCards = [
    {
      label: copy.metrics.paidEventsCount,
      value: formatCompactMetricNumber(
        paymentsSummary.paid_events_count ?? paidEvents.length
      ),
    },
    {
      label: copy.metrics.paidTicketsSold,
      value: formatCompactMetricNumber(paymentsSummary.paid_tickets_sold),
    },
    {
      label: copy.metrics.depositPayments,
      value: formatCompactMetricNumber(paymentsSummary.deposit_payments_count),
    },
    {
      label: copy.metrics.refundedTickets,
      value: formatCompactMetricNumber(paymentsSummary.refunded_tickets_count),
    },
    { label: copy.metrics.grossRevenue, value: formatMoney(paymentsSummary.gross_revenue, summaryCurrency) },
    { label: copy.metrics.refundedAmount, value: formatMoney(paymentsSummary.refunded_amount, summaryCurrency) },
    ...(approvedSettlementMode === "direct" && !hasHeldPaidEvents ? [] : [
      { label: copy.metrics.platformFeeRetained, value: formatMoney(paymentsSummary.platform_fee_retained, summaryCurrency) },
    ]),
    ...(approvedSettlementMode === "direct" && !hasHeldPaidEvents ? [] : [
      { label: copy.metrics.heldBalance, value: formatMoney(paymentsSummary.held_balance, summaryCurrency) },
      { label: copy.metrics.releasedBalance, value: formatMoney(paymentsSummary.released_payouts, summaryCurrency) },
      { label: copy.metrics.pendingPayout, value: formatMoney(paymentsSummary.pending_payout, summaryCurrency) },
    ]),
    { label: copy.metrics.cashDepositReserved, value: formatMoney(paymentsSummary.cash_deposit_reserved, summaryCurrency) },
    { label: copy.metrics.cashCommissionSettled, value: formatMoney(paymentsSummary.cash_commission_settled, summaryCurrency) },
    { label: copy.metrics.cashDepositReleased, value: formatMoney(paymentsSummary.cash_deposit_released, summaryCurrency) },
    { label: copy.metrics.cashOutstanding, value: formatMoney(paymentsSummary.cash_outstanding_amount, summaryCurrency) },
  ];
  const filteredEvents = recentEvents.filter((event) => {
    if (eventFilter === "concepts") {
      return event?.type === "concept";
    }
    if (eventFilter === "active") {
      return event?.type !== "concept" && ["active", "pending"].includes(String(event.status || "").toLowerCase());
    }
    if (eventFilter === "finished") {
      return event?.type !== "concept" && ["finished", "cancelled", "postponed"].includes(String(event.status || "").toLowerCase());
    }
    return true;
  });
  const sortedEvents = [...filteredEvents].sort((a, b) => {
    const attentionA = Math.max(0, Number(unreadByEventId?.[String(a?.event_id)]) || 0);
    const attentionB = Math.max(0, Number(unreadByEventId?.[String(b?.event_id)]) || 0);
    if (attentionA !== attentionB) return attentionB - attentionA;
    const conceptA = a?.type === "concept";
    const conceptB = b?.type === "concept";
    if (conceptA !== conceptB) return Number(conceptA) - Number(conceptB);
    if (conceptA) {
      return String(a?.name || "").localeCompare(String(b?.name || ""), undefined, { sensitivity: "base" });
    }
    const dateA = Date.parse(a?.date_time || "");
    const dateB = Date.parse(b?.date_time || "");
    const futureA = Number.isFinite(dateA) && dateA >= Date.now();
    const futureB = Number.isFinite(dateB) && dateB >= Date.now();
    if (futureA !== futureB) return Number(futureB) - Number(futureA);
    if (Number.isFinite(dateA) && Number.isFinite(dateB)) {
      return futureA ? dateA - dateB : dateB - dateA;
    }
    if (Number.isFinite(dateA) !== Number.isFinite(dateB)) return Number.isFinite(dateB) - Number.isFinite(dateA);
    return String(a?.name || "").localeCompare(String(b?.name || ""), undefined, { sensitivity: "base" });
  });
  const visibleEvents = sortedEvents.slice(0, visibleEventsCount);
  const canShowMoreEvents = visibleEventsCount < sortedEvents.length;
  const stripeConnected = Boolean(stripe?.connected_account_id);
  const stripeReadyForOnlineTickets = Boolean(stripe?.can_accept_paid_events);
  const billingReady = Boolean(panelData?.billing_details?.complete);
  const stripeReviewPending = stripeConnected && !stripeReadyForOnlineTickets &&
    ["listed", "under_review"].includes(String(stripe?.disabled_reason || "").toLowerCase()) &&
    !(stripe?.requirements_currently_due || []).length &&
    !(stripe?.requirements_past_due || []).length;
  const tbankReadyForOnlineTickets = Boolean(
    tbankDirectAcquiringReady || (tbankRequiresRecipient && tbankRecipient?.ready_for_payouts)
  );
  const paymentSetupReady = isTBankPayments
    ? tbankReadyForOnlineTickets
    : stripeReadyForOnlineTickets;
  const paidHostRequestStatus = String(paidHostAccessRequest?.status || "").trim().toLowerCase();
  const paymentsFlow = resolvePaidHostPaymentsFlow({
    requestStatus: paidHostRequestStatus,
    accessEntitled: panelData?.summary?.has_paid_host_events_access,
    agreementAccepted: paidHostAgreement?.accepted,
    stripeReadyForOnlineTickets,
    paymentSetupReady,
    billingRequired: !isTBankPayments,
    billingReady,
    paidEvents,
  });
  const hasOpenPaidHostRequest = paymentsFlow.hasOpenRequest;
  const paidHostAccessApproved = paymentsFlow.accessApproved;
  const verificationStepComplete = paymentsFlow.verificationComplete;
  const agreementStepComplete = paymentsFlow.agreementComplete;
  const paymentSetupStepComplete = paymentsFlow.paymentSetupComplete;
  const billingStepComplete = paymentsFlow.billingComplete;
  const paidEventsStepComplete = paymentsFlow.paidEventsComplete;
  const paymentsFlowComplete = paymentsFlow.complete;
  const stripeStepComplete = stripeReadyForOnlineTickets;
  const agreementNeedsRenewal = Boolean(
    paidHostAgreement?.requires_reaccept ||
      (paidHostAgreement?.accepted_at && !paidHostAgreement?.accepted)
  );
  const canViewPaymentDetails = paidHostAccessApproved;
  const canManageStripe = !isTBankPayments && paidHostAccessApproved && agreementStepComplete;
  const anyOverlayOpen =
    limitUpsellOpen ||
    paidHostRequestOpen ||
    stripeCountryModalOpen ||
    Boolean(payoutInfoEvent) ||
    paymentMetricsHelpOpen ||
    Boolean(feedbackEvent);

  const updatePanelData = (updater) => {
    setPanelData((current) => {
      const next = typeof updater === "function" ? updater(current || {}) : updater;
      return next || current;
    });
  };

  const markPaymentsSeen = async (eventId = null) => {
    try {
      await api.post("/events/organizer-panel/payments/read/", eventId ? { event_id: eventId } : {});
    } catch (error) {
      console.error("[EventCreateStep0] failed to mark payments seen", error);
    }
  };

  const refreshTBankRecipient = async () => {
    setPaymentsActionError("");
    setPaymentsActionNotice("");
    setStripeActionBusy("tbank");
    try {
      const { data } = await api.get("/payments/tbank/recipients/current/", {
        meta: { suppressGlobalError: true },
      });
      updatePanelData((current) => ({
        ...(current || {}),
        tbank_recipient: data?.recipient || null,
      }));
    } catch (error) {
      const backendError = error?.response?.data?.error || error?.response?.data?.detail;
      setPaymentsActionError(String(backendError || copy.loadError));
    } finally {
      setStripeActionBusy("");
    }
  };

  const createTBankRecipient = async () => {
    setPaymentsActionError("");
    setPaymentsActionNotice("");
    setStripeActionBusy("tbank-create");
    try {
      const { data } = await api.post("/payments/tbank/recipients/current/", {
        recipient_type: tbankRecipientType,
      });
      updatePanelData((current) => ({
        ...(current || {}),
        tbank_recipient: data?.recipient || null,
      }));
      setPaymentsActionNotice(copy.payments.tbankProfileCreated);
    } catch (error) {
      const backendError = error?.response?.data?.error || error?.response?.data?.detail;
      setPaymentsActionError(String(backendError || copy.loadError));
    } finally {
      setStripeActionBusy("");
    }
  };

  const handleTogglePaidEventDetails = (eventId) => {
    const parsed = Number(eventId);
    if (!Number.isFinite(parsed) || parsed <= 0) return;
    const willOpen = !expandedPaidEventIds.has(parsed);
    togglePaidEventDetails(parsed);
    if (!willOpen) return;
    markPaymentsSeen(parsed);
    updatePanelData((current) => {
      const next = { ...(current || {}) };
      next.recent_events = Array.isArray(next.recent_events)
        ? next.recent_events.map((event) => {
            if (Number(event?.event_id) !== parsed) return event;
            const paymentSummary = { ...(event.payment_summary || {}) };
            paymentSummary.recent_deposits_count = 0;
            paymentSummary.payments = Array.isArray(paymentSummary.payments)
              ? paymentSummary.payments.map((payment) => ({
                  ...payment,
                  is_recent_deposit: false,
                }))
              : [];
            return { ...event, payment_summary: paymentSummary };
          })
        : next.recent_events;
      return next;
    });
  };
  const openPaidEvent = (event) => {
    navigate(buildEventPath(event.event_id, event.name), {
      state: {
        returnTo: ORGANIZER_PAYMENTS_RETURN_TO,
        returnState: {
          ...ORGANIZER_PAYMENTS_RETURN_STATE,
          hostPanelFocusEventId: Number(event.event_id),
        },
      },
    });
  };

  const payCommissionInvoice = async (invoiceId) => {
    const id = Number(invoiceId);
    if (!Number.isSafeInteger(id) || id <= 0 || invoiceCheckoutPendingId != null) return;
    setPaymentsActionError("");
    setInvoiceCheckoutPendingId(id);
    try {
      const { data } = await api.post(`/events/organizer-panel/invoices/${id}/checkout/`);
      const target = new URL(String(data?.checkout_url || ""), window.location.origin);
      if (target.protocol !== "https:" && target.origin !== window.location.origin) {
        throw new Error("invalid_invoice_checkout_url");
      }
      if (isNativeApp()) await openExternalUrl(target.toString());
      else window.location.assign(target.toString());
    } catch (error) {
      setPaymentsActionError(String(error?.response?.data?.error || settlementCopy.invoicePaymentError));
    } finally {
      setInvoiceCheckoutPendingId(null);
    }
  };


  const startStripeConnect = async ({ country } = {}) => {
    setPaymentsActionError("");
    setPaymentsActionNotice("");
    setStripeActionBusy("connect");
    const selectedCountry = normalizeStripeCountryCode(reviewedStripeCountry || country || stripeConnectCountry);
    const popup = prepareExternalWindow();
    try {
      const { data } = await api.post(
        "/events/organizer-panel/stripe/connect/",
        selectedCountry ? { country: selectedCountry } : {},
        { meta: { suppressGlobalError: true } }
      );
      if (data?.stripe) {
        updatePanelData((current) => ({ ...(current || {}), stripe: data.stripe }));
      }
      if (data?.url) {
        setStripeCountryModalOpen(false);
        openExternalStripeUrl(data.url, popup);
        return;
      }
      closePreparedExternalWindow(popup);
      setPaymentsActionError(copy.loadError);
    } catch (error) {
      closePreparedExternalWindow(popup);
      const backendError = error?.response?.data?.error;
      setPaymentsActionError(String(backendError || copy.loadError));
    } finally {
      setStripeActionBusy("");
    }
  };

  const openStripeCountrySelection = () => {
    setPaymentsActionError("");
    setPaymentsActionNotice("");
    if (reviewedStripeCountry) setStripeConnectCountry(reviewedStripeCountry);
    setStripeCountryModalOpen(true);
  };

  const continueStripeCountrySelection = () => {
    startStripeConnect({ country: stripeConnectCountry });
  };

  const openStripeDashboard = async () => {
    setPaymentsActionError("");
    setPaymentsActionNotice("");
    setStripeActionBusy("dashboard");
    const popup = prepareExternalWindow();
    try {
      const { data } = await api.post("/events/organizer-panel/stripe/dashboard/");
      if (data?.url) {
        openExternalStripeUrl(data.url, popup);
        return;
      }
      closePreparedExternalWindow(popup);
      setPaymentsActionError(copy.loadError);
    } catch (error) {
      closePreparedExternalWindow(popup);
      const recoveryUrl = error?.response?.data?.url;
      if (recoveryUrl) {
        openExternalStripeUrl(recoveryUrl);
        return;
      }
      const backendError = error?.response?.data?.error;
      setPaymentsActionError(String(backendError || copy.loadError));
    } finally {
      setStripeActionBusy("");
    }
  };

  const refreshManualBankBalance = async () => {
    setManualBankLoading(true);
    setPaymentsActionError("");
    try {
      const { data } = await api.get("/events/organizer-panel/stripe/manual-bank-payout/");
      setManualBankSnapshot(data);
    } catch (error) {
      setPaymentsActionError(String(error?.response?.data?.error || manualBankCopy.error));
    } finally {
      setManualBankLoading(false);
    }
  };

  const startManualBankPayout = async () => {
    setStripeActionBusy("manual-bank-payout");
    setPaymentsActionError("");
    setPaymentsActionNotice("");
    try {
      await api.post("/events/organizer-panel/stripe/manual-bank-payout/");
      setPaymentsActionNotice(manualBankCopy.success);
      await refreshManualBankBalance();
    } catch (error) {
      setPaymentsActionError(String(error?.response?.data?.error || manualBankCopy.error));
    } finally {
      setStripeActionBusy("");
    }
  };

  const submitPaidHostAccessRequest = async () => {
    setPaymentsActionError("");
    setPaymentsActionNotice("");
    setPaidHostRequestSubmitting(true);
    try {
      const payload = {
        business_country: paidHostRequestForm.business_country,
        legal_form: paidHostRequestForm.legal_form,
        business_legal_name: paidHostRequestForm.business_legal_name,
        social_profile: paidHostRequestForm.social_profile,
        business_social_profile: paidHostRequestForm.business_social_profile,
        age: paidHostRequestForm.age,
        phone_number: paidHostRequestForm.phone_number,
        requested_payment_method: paidHostRequestForm.requested_payment_method,
        requested_event_types: paidHostRequestForm.requested_event_types,
        business_notes: paidHostRequestForm.business_notes,
      };
      const { data } = await api.post("/events/organizer-panel/paid-host-access-request/", payload);
      updatePanelData((current) => ({
        ...(current || {}),
        paid_host_access_request: data?.request || current?.paid_host_access_request || null,
      }));
      setPaidHostRequestOpen(false);
      setPaymentsActionNotice(copy.payments.requestSent);
    } catch (error) {
      const backendError = error?.response?.data?.error;
      setPaymentsActionError(String(backendError || copy.loadError));
    } finally {
      setPaidHostRequestSubmitting(false);
    }
  };

  const paymentsFlowSteps = [
    {
      label: copy.payments.steps[0],
      complete: verificationStepComplete,
      locked: false,
    },
    {
      label: copy.payments.steps[1],
      complete: agreementStepComplete,
      locked: !paidHostAccessApproved,
    },
    {
      label: copy.payments.steps[2],
      complete: paymentSetupStepComplete,
      locked: !agreementStepComplete,
    },
    ...(!isTBankPayments ? [{
      label: copy.payments.steps[3],
      complete: billingReady,
      locked: !paymentSetupStepComplete && !billingReady,
    }] : []),
    {
      label: isTBankPayments ? copy.payments.steps[4].replace(/^5\./, "4.") : copy.payments.steps[4],
      complete: paidEventsStepComplete,
      locked: !billingStepComplete,
    },
  ];
  const showPanelSkeleton = (loadingPanel && !panelData) || (!panelData && Boolean(panelError));

  return (
    <div
      className={`relative w-full text-white ${
        embeddedOrganizerPanel
          ? "min-h-full"
          : "min-h-[calc(100dvh-var(--bottom-nav-height,72px))] lg:min-h-screen"
      }`}
      style={embeddedOrganizerPanel ? undefined : EVENT_CREATE_INTRO_BACKGROUND_STYLE}
    >
      <div
        className={`mx-auto w-full px-4 md:px-5 ${
          embeddedOrganizerPanel
            ? `${EMBEDDED_ORGANIZER_CONTROL_WIDTH_CLASS} pb-[calc(var(--bottom-nav-clearance,112px)+40px)] pt-0 lg:px-0`
            : "max-w-[560px] pb-[calc(var(--bottom-nav-clearance,112px)+148px)] pt-[calc(16px+var(--vg-safe-area-top))] lg:max-w-[780px] lg:pb-28 lg:px-6 lg:pt-6"
        }`}
      >
        {!embeddedOrganizerPanel ? (
          <div className="relative pr-12">
            <h1 className="text-[2rem] font-extrabold leading-[1.02] text-white">
              {copy.title}
            </h1>
            <div className="absolute right-0 top-0">
              <VaultModalCloseButton onClick={onClose} aria-label={copy.closeAria} />
            </div>
          </div>
        ) : null}

        {showCreateEventBanner ? (
          <div
            className={`mx-auto mb-3 ${
              embeddedOrganizerPanel ? EMBEDDED_ORGANIZER_CONTROL_WIDTH_CLASS : "w-full"
            }`}
          >
            <CreateEventBanner onClick={() => onProceed?.(summary?.host_created)} disabled={!onProceed} mode="host-create" />
          </div>
        ) : null}

        {!isTicketsSection ? (
          <HostPanelTabs
            copy={copy}
            activeTab={activeTab}
            onChange={setActiveTab}
            eventsCount={eventAttentionTotal}
            compact={embeddedOrganizerPanel}
          />
        ) : null}

        {showPanelSkeleton ? (
          <HostPanelSkeleton activeTab={activeTab} />
        ) : (
          <>
        {activeTab === "overview" && (
          <>
            <div className="mt-5 grid gap-4 xl:grid-cols-[1.15fr_0.85fr]">
              <div className="rounded-[26px] border border-white/12 bg-white/[0.05] px-5 py-5 shadow-[0_20px_40px_rgba(0,0,0,0.24)]">
                <div className="text-[0.76rem] font-semibold uppercase tracking-[0.16em] text-white/56">
                  {copy.hostScore.label}
                </div>
                <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
                  <div>
                    <div className="flex items-end gap-3">
                      <div className="text-[3.1rem] font-extrabold leading-none text-white">
                        {averageEventRating > 0 ? averageEventRating.toFixed(1) : "—"}
                      </div>
                      <div className="pb-2">
                        <StarRow value={averageEventRating} />
                      </div>
                    </div>
                    <div className="mt-2 max-w-xl text-[0.92rem] leading-[1.45] text-white/68">
                      {numberValue(summary.total_event_ratings) > 0
                        ? copy.hostScore.basedOn
                            .replace("{{count}}", formatNumber(summary.total_event_ratings))
                            .replace("{{events}}", formatNumber(summary.rated_events_count))
                        : copy.hostScore.unrated}
                    </div>
                  </div>

                  <div className="rounded-[20px] border border-white/12 bg-[#071420]/55 px-4 py-4 text-right">
                    <div className="text-[0.76rem] font-semibold uppercase tracking-[0.14em] text-white/54">
                      {copy.hostScore.attendance}
                    </div>
                    <div className="mt-2 text-[2rem] font-extrabold leading-none text-white">
                      {formatPercent(summary.attendance_rate_percent)}
                    </div>
                    <div className="mt-2 max-w-[240px] text-[0.82rem] leading-[1.35] text-white/58">
                      {copy.hostScore.attendanceHint
                        .replace("{{confirmed}}", formatNumber(summary.confirmed_attendance_total))
                        .replace("{{registered}}", formatNumber(summary.registered_at_start_total))}
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-[26px] border border-white/12 bg-white/[0.05] px-5 py-5 shadow-[0_20px_40px_rgba(0,0,0,0.24)]">
                <div className="text-[0.76rem] font-semibold uppercase tracking-[0.16em] text-white/56">
                  {copy.sections.limits}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-3 xl:grid-cols-1">
                  <MetricCard
                    label={copy.metrics.slotsUsed}
                    value={formatMetricValue(usedCreationSlots, {
                      unlimited: hostSlotsUnlimited,
                    })}
                  />
                  <MetricCard
                    label={copy.metrics.slotsAvailable}
                    value={formatMetricValue(totalAvailableSlots, {
                      unlimited: hostSlotsUnlimited,
                    })}
                  />
                </div>
                {!hostSlotsUnlimited ? (
                  <div className="mt-4 rounded-[20px] border border-[#59c8ff]/14 bg-[linear-gradient(180deg,rgba(14,30,49,0.88)_0%,rgba(7,20,32,0.88)_100%)] px-4 py-4 text-[0.92rem] leading-[1.5] text-white/76">
                    <div className="text-[0.74rem] font-semibold uppercase tracking-[0.14em] text-[#7FD9FF]">
                      {copy.limits.cardTitle}
                    </div>
                    <p className="mt-2">{copy.limits.cardBody}</p>
                    <button
                      type="button"
                      onClick={() => setLimitUpsellOpen(true)}
                      className="mt-4 inline-flex h-11 items-center justify-center overflow-hidden rounded-[16px] bg-[linear-gradient(90deg,#2F67E7_0%,#2FD8F6_100%)] px-5 text-[0.92rem] font-semibold text-white shadow-[0_14px_28px_rgba(47,116,232,0.28)]"
                    >
                      {copy.limits.cardButtonLabel}
                    </button>
                  </div>
                ) : null}
                {debugPanelEnabled ? (
                  <div className="mt-4 rounded-[18px] border border-amber-300/14 bg-amber-400/8 px-4 py-3">
                    <div className="text-[0.72rem] font-semibold uppercase tracking-[0.14em] text-amber-100/82">
                      {copy.limits.debugTitle}
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button
                        type="button"
                        onClick={() => setDebugScenario("live")}
                        className={`rounded-full px-3 py-2 text-[0.78rem] font-semibold ${
                          debugScenario === "live"
                            ? "bg-white text-[#0A64FF]"
                            : "border border-white/12 bg-white/[0.05] text-white/76"
                        }`}
                      >
                        {copy.limits.debugLive}
                      </button>
                      <button
                        type="button"
                        onClick={() => setDebugScenario("slots_full")}
                        className={`rounded-full px-3 py-2 text-[0.78rem] font-semibold ${
                          debugScenario === "slots_full"
                            ? "bg-white text-[#0A64FF]"
                            : "border border-white/12 bg-white/[0.05] text-white/76"
                        }`}
                      >
                        {copy.limits.debugSlotsFull}
                      </button>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>

            <SectionTitle>{copy.sections.performance}</SectionTitle>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {performanceCards.map((card) => (
                <MetricCard key={card.label} label={card.label} value={card.value} />
              ))}
            </div>

            <SectionTitle>{copy.sections.attendance}</SectionTitle>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
              {attendanceCards.map((card) => (
                <MetricCard key={card.label} label={card.label} value={card.value} />
              ))}
            </div>

          </>
        )}

        {activeTab === "analytics" && (
          <>
            <SectionTitle className="mt-5">{copy.analyticsPanel.eventList}</SectionTitle>
            {analyticsPreviewsError ? (
              <div className="mb-4 flex items-center justify-between gap-3 rounded-[18px] border border-white/12 bg-white/[0.05] px-4 py-3 text-sm text-white/70">
                <span>{copy.analyticsPanel.previewError}</span>
                <button type="button" onClick={() => setAnalyticsPreviewsRetry((current) => current + 1)} className="shrink-0 font-semibold text-white underline">
                  {copy.analyticsPanel.retry}
                </button>
              </div>
            ) : null}
            {analyticsEvents.length === 0 ? (
              <div className="rounded-[24px] border border-white/12 bg-white/[0.05] px-5 py-5 text-[0.95rem] text-white/70">
                {copy.analyticsPanel.noEvents}
              </div>
            ) : (
              <div className="space-y-3">
                {analyticsEvents.slice(0, visibleEventsCount).map((event) => {
                  const eventId = Number(event.event_id);
                  const isSelected = selectedAnalyticsEventId === eventId;
                  const totals = eventAnalytics[eventId];
                  const fullDate = formatAnalyticsEventDate(event.date_time, i18n.language) || `#${eventId}`;
                  const previewOpens = analyticsPreviews[eventId]?.card_opens;
                  const previewLabel = (analyticsPreviewsLoading || !analyticsPreviewsLoaded) && previewOpens === undefined && !analyticsPreviewsError
                    ? "…"
                    : analyticsPreviewsError && previewOpens === undefined
                      ? "—"
                      : formatCompactMetricNumber(previewOpens);
                  const toggleLabel = isSelected ? copy.analyticsPanel.close : copy.analyticsPanel.open;
                  const cardBorderClass = isSelected ? "border-sky-300/70" : "border-white/16";
                  return (
                    <div key={eventId} className="pb-4">
                      <div className={`relative overflow-visible rounded-2xl border bg-[#13233b] shadow-[0_10px_24px_rgba(0,0,0,0.18)] transition ${cardBorderClass} ${isSelected ? "ring-2 ring-sky-300/25" : ""}`}>
                        <span
                          className={`absolute right-3 top-0 z-20 inline-flex -translate-y-1/2 items-center gap-1.5 rounded-full border bg-[#13233b] px-3 py-1 text-[0.8rem] font-extrabold text-white shadow-[0_6px_14px_rgba(0,0,0,0.18)] ${cardBorderClass}`}
                          aria-label={`${copy.metrics.opens}: ${previewLabel}`}
                        >
                          {previewLabel}
                          <img src="/assets/events/analytics_eye.svg" alt="" className="h-4 w-[22px] invert" />
                        </span>
                        <button
                          type="button"
                          onClick={() => void loadEventAnalytics(eventId)}
                          aria-expanded={isSelected}
                          aria-controls={`event-analytics-${eventId}`}
                          aria-label={`${toggleLabel}: ${event.name} · ${fullDate}`}
                          disabled={analyticsLoadingId !== null && analyticsLoadingId !== eventId}
                          className="block w-full text-left disabled:opacity-70"
                        >
                          <EventCard
                            title={event.name}
                            avatar={null}
                            type={event.type || "event"}
                            isOnline={Number(event.event_format) === 0}
                            isPersonal={Number(event.host_format) === 0}
                            isPaid={Boolean(event.is_paid)}
                            isByRequest={Boolean(event.join_by_request)}
                            minVaultLevel={event.min_vault_level}
                            datetime={event.date_time}
                            isVerified={Boolean(event.is_verified)}
                            isValidated={Boolean(event.is_validated)}
                            isFavourite={false}
                            onToggleFavourite={() => {}}
                            status={event.status || "active"}
                            badgeLabel={event.badge_label || null}
                            iconImageUrl={event.icon_image_url}
                            showFavouriteButton={false}
                            suppressStatusOverlay
                            cardClassName="!border-transparent !bg-transparent shadow-none"
                            titleMaxWidthClass="max-w-[420px]"
                          />
                        </button>
                        <div id={`event-analytics-${eventId}`} className={`grid transition-all duration-200 ease-out ${isSelected ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                          <div className="overflow-hidden">
                            {isSelected ? (
                              <div className="border-t border-white/10 px-4 pb-5 pt-4" aria-live="polite">
                                {analyticsLoadingId === eventId || analyticsErrorId === eventId || !totals ? (
                                  <div className="relative" role="status" aria-label={analyticsErrorId === eventId ? copy.analyticsPanel.error : copy.analyticsPanel.loading}>
                                    <div className={`grid grid-cols-2 gap-3 md:grid-cols-4 ${analyticsErrorId === eventId ? "opacity-35" : ""}`}>
                                      {Array.from({ length: 4 }).map((_, index) => <AnalyticsMetricSkeleton key={index} />)}
                                    </div>
                                    {analyticsErrorId === eventId ? (
                                      <button type="button" onClick={() => void loadEventAnalytics(eventId, { force: true })} className="absolute inset-0 m-auto h-fit w-fit rounded-xl bg-[#13233b] px-4 py-2 text-sm font-semibold text-white underline">
                                        {copy.analyticsPanel.error} {copy.analyticsPanel.retry}
                                      </button>
                                    ) : null}
                                  </div>
                                ) : (
                                  <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                                    <MetricCard label={copy.metrics.views} value={formatCompactMetricNumber(totals.card_impressions)} />
                                    <MetricCard label={copy.metrics.opens} value={formatCompactMetricNumber(totals.card_opens)} />
                                    <MetricCard label={copy.metrics.uniqueViewers} value={formatCompactMetricNumber(totals.unique_viewers_lifetime)} />
                                    <MetricCard label={copy.metrics.uniqueOpeners} value={formatCompactMetricNumber(totals.unique_openers_lifetime)} />
                                  </div>
                                )}
                              </div>
                            ) : null}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => void loadEventAnalytics(eventId)}
                          aria-expanded={isSelected}
                          disabled={analyticsLoadingId !== null && analyticsLoadingId !== eventId}
                          aria-controls={`event-analytics-${eventId}`}
                          aria-label={`${toggleLabel}: ${event.name}`}
                          className="absolute bottom-0 left-1/2 z-20 flex h-5 min-w-[40px] -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full border border-white/75 bg-[#13233b] px-3 text-white shadow-[0_10px_20px_rgba(0,0,0,0.2)] transition disabled:opacity-60"
                        >
                          <svg width="12" height="6" viewBox="0 0 12 6" fill="none" aria-hidden="true" className={`transition-transform duration-200 ${isSelected ? "rotate-180" : ""}`}>
                            <path d="M1 1L6 5L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  );
                })}
                {visibleEventsCount < analyticsEvents.length ? (
                  <AutoLoadMoreSentinel
                    onIntersect={() =>
                      setVisibleEventsCount((current) => Math.min(current + 6, analyticsEvents.length))
                    }
                  />
                ) : null}
              </div>
            )}
            <SectionTitle>{copy.sections.analytics}</SectionTitle>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {analyticsCards.map((card) => (
                <MetricCard key={card.label} label={card.label} value={card.value} />
              ))}
            </div>
          </>
        )}

        {activeTab === "events" && (
          <>
            <div className="mt-5 mb-4 flex flex-wrap justify-center gap-2">
              <TabButton active={eventFilter === "active"} onClick={() => setEventFilter((current) => current === "active" ? null : "active")}>
                {copy.eventCard.activeOnly}
              </TabButton>
              <TabButton active={eventFilter === "finished"} onClick={() => setEventFilter((current) => current === "finished" ? null : "finished")}>
                {copy.eventCard.finishedOnly}
              </TabButton>
              <TabButton active={eventFilter === "concepts"} onClick={() => setEventFilter((current) => current === "concepts" ? null : "concepts")}>
                {copy.eventCard.conceptsOnly}
              </TabButton>
            </div>
            {loadingPanel && recentEvents.length === 0 ? (
              <HostPanelEventListSkeleton rows={3} />
            ) : recentEvents.length === 0 ? (
              <div className="rounded-[24px] border border-white/12 bg-white/[0.05] px-5 py-5 text-[0.95rem] text-white/70">
                {copy.eventCard.noEvents}
              </div>
            ) : filteredEvents.length === 0 ? (
              <div className="rounded-[24px] border border-white/12 bg-white/[0.05] px-5 py-5 text-[0.95rem] text-white/70">
                {copy.eventCard.noEventsFiltered}
              </div>
            ) : (
              <div className="space-y-3">
                {visibleEvents.map((event) => {
                  const normalizedStatus = String(event.status || "").toLowerCase();
                  const isTerminalEvent = ["finished", "cancelled", "postponed"].includes(normalizedStatus);
                  const eventId = Number(event.event_id);
                  const safetyReportCount = numberValue(
                    event.unregistered_attendee_report_count
                  );
                  const safetyReportExpanded =
                    expandedSafetyReportEventIds.has(eventId);
                  return (
                    <div
                      key={event.event_id}
                      ref={(node) => {
                        const eventId = Number(event.event_id);
                        if (!node) {
                          hostEventRefs.current.delete(eventId);
                          return;
                        }
                        hostEventRefs.current.set(eventId, node);
                      }}
                      className="rounded-[24px] border border-white/12 bg-white/[0.05] px-5 py-5 shadow-[0_18px_34px_rgba(0,0,0,0.18)]"
                    >
                      <button
                        type="button"
                        className="block w-full text-left"
                        onClick={() => navigate(buildEventPath(event.event_id, event.name))}
                      >
                        <EventCard
                          title={event.name}
                          avatar={null}
                          type={event.type || "event"}
                          isOnline={Number(event.event_format) === 0}
                          isPersonal={Number(event.host_format) === 0}
                          isPaid={Boolean(event.is_paid)}
                          isByRequest={Boolean(event.join_by_request)}
                          minVaultLevel={event.min_vault_level}
                          datetime={event.date_time}
                          datetimeDisplayMode="date_only"
                          isVerified={Boolean(event.is_verified)}
                          isValidated={Boolean(event.is_validated)}
                          isFavourite={false}
                          onToggleFavourite={() => {}}
                          status={event.status || "active"}
                          badgeLabel={event.badge_label || null}
                          iconImageUrl={event.icon_image_url}
                          showFavouriteButton={false}
                          suppressStatusOverlay={false}
                          titleMaxWidthClass="max-w-[190px] md:max-w-[300px]"
                          unreadCount={
                            Number(unreadByEventId?.[String(event.event_id)] || 0) || 0
                          }
                        />
                      </button>

                      {isTerminalEvent ? (
                        <div className="mt-4 rounded-[18px] border border-white/12 bg-[#071420]/55 px-4 py-3">
                          <div className="text-[0.72rem] font-semibold uppercase tracking-[0.12em] text-white/54">
                            {copy.eventCard.eventRating}
                          </div>
                          <div className="mt-2 flex items-center gap-3">
                            <div className="text-[1.4rem] font-extrabold leading-none text-white">
                              {numberValue(event.event_rating_count) > 0
                                ? numberValue(event.average_event_rating).toFixed(1)
                                : "—"}
                            </div>
                            <StarRow value={event.average_event_rating} />
                          </div>
                          <div className="mt-2 text-[0.8rem] text-white/58">
                            {`${formatNumber(event.event_rating_count)} ${copy.eventCard.ratingsSuffix}`}
                          </div>
                        </div>
                      ) : null}

                      <div className={`mt-4 grid gap-3 ${normalizedStatus === "finished" ? "grid-cols-2 md:grid-cols-3" : "grid-cols-1"}`}>
                        {normalizedStatus !== "finished" ? (
                          <MetricCard
                            label={copy.eventCard.currentParticipants}
                            value={formatNumber(event.current_participants_count)}
                          />
                        ) : (
                          <>
                            <MetricCard
                              label={copy.eventCard.registeredAtStart}
                              value={formatNumber(event.registered_at_start)}
                            />
                            <MetricCard
                              label={copy.eventCard.confirmedAttendance}
                              value={formatNumber(event.confirmed_attendance)}
                            />
                            <MetricCard
                              label={copy.eventCard.attendanceRate}
                              value={formatPercent(event.attendance_rate_percent)}
                              className="col-span-2 md:col-span-1"
                            />
                          </>
                        )}
                      </div>
                      {normalizedStatus === "finished" && safetyReportCount > 0 ? (
                        <div className="mt-4 overflow-hidden rounded-[18px] border border-amber-200/24 bg-[linear-gradient(180deg,rgba(245,158,11,0.12),rgba(239,68,68,0.08))] shadow-[inset_0_1px_0_rgba(255,255,255,0.05)]">
                          <button
                            type="button"
                            onClick={() => toggleSafetyReportDetails(eventId)}
                            aria-expanded={safetyReportExpanded}
                            aria-controls={`event-safety-report-${eventId}`}
                            className="flex min-h-14 w-full items-center gap-3 px-4 py-3 text-left text-white transition hover:bg-white/[0.05] active:bg-white/[0.08]"
                          >
                            <span
                              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-amber-100/24 bg-amber-300/12 text-amber-100"
                              aria-hidden="true"
                            >
                              <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none">
                                <path
                                  d="M12 3.2 20 6.5v5.7c0 4.8-3.1 8.8-8 10.6-4.9-1.8-8-5.8-8-10.6V6.5L12 3.2Z"
                                  stroke="currentColor"
                                  strokeWidth="1.8"
                                  strokeLinejoin="round"
                                />
                                <path
                                  d="M12 8v5M12 16.8h.01"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                />
                              </svg>
                            </span>
                            <span className="min-w-0 flex-1">
                              <span className="block text-[14px] font-extrabold leading-5">
                                {copy.safetyReports.title}
                              </span>
                              <span className="mt-0.5 block text-[12px] font-semibold text-amber-50/68">
                                {copy.safetyReports.countLabel.replace(
                                  "{{count}}",
                                  formatNumber(safetyReportCount)
                                )}
                              </span>
                            </span>
                            <svg
                              viewBox="0 0 24 24"
                              className={`h-5 w-5 shrink-0 text-white/62 transition-transform ${
                                safetyReportExpanded ? "rotate-180" : ""
                              }`}
                              fill="none"
                              aria-hidden="true"
                            >
                              <path
                                d="m6 9 6 6 6-6"
                                stroke="currentColor"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                          {safetyReportExpanded ? (
                            <div
                              id={`event-safety-report-${eventId}`}
                              className="border-t border-white/10 px-4 pb-4 pt-4"
                            >
                              <p className="text-[0.9rem] font-medium leading-[1.55] text-white/82">
                                {copy.safetyReports.body}
                              </p>
                              <p className="mt-3 text-[0.84rem] leading-[1.5] text-white/62">
                                {copy.safetyReports.guidance}
                              </p>
                              <button
                                type="button"
                                onClick={() => navigate("/events/hosting-rules", {
                                  state: {
                                    mode: "review",
                                    returnTo: `${location.pathname}${location.search || ""}${location.hash || ""}`,
                                    returnState: location.pathname === "/events/search"
                                      ? { ...(location.state || {}), audienceMode: "host", hostPanelTab: "events" }
                                      : { ...(location.state || {}), openOrganizerPanel: true, organizerPanelInitialTab: "events" },
                                  },
                                })}
                                className="mt-4 inline-flex min-h-11 w-full items-center justify-center rounded-[15px] border border-amber-100/24 bg-white/[0.08] px-4 py-2.5 text-center text-[0.9rem] font-extrabold text-white transition hover:bg-white/[0.13] active:scale-[0.99]"
                              >
                                {copy.safetyReports.viewRules}
                              </button>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      {normalizedStatus === "finished" &&
                      numberValue(event.anonymous_host_feedback_count) > 0 ? (
                        <button
                          type="button"
                          onClick={() => setFeedbackEvent(event)}
                          className="relative mt-4 flex min-h-12 w-full items-center gap-3 rounded-[18px] border border-white/16 bg-white/[0.08] px-4 py-3 pr-14 text-left text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.06)] transition hover:bg-white/[0.12] active:scale-[0.99]"
                        >
                          <span
                            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/10 text-white"
                            aria-hidden="true"
                          >
                            <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none">
                              <path
                                d="M5 5.5h14v10H9.5L6 18.5v-3H5v-10Z"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M8.5 9h7M8.5 12h4.5"
                                stroke="currentColor"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                              />
                            </svg>
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className="block text-[14px] font-extrabold leading-5">
                              {copy.eventCard.participantFeedback}
                            </span>
                            <span className="mt-0.5 block text-[12px] font-medium text-white/52">
                              {formatNumber(event.anonymous_host_feedback_count)}
                            </span>
                          </span>
                          <svg
                            viewBox="0 0 24 24"
                            className="h-5 w-5 shrink-0 text-white/62"
                            fill="none"
                            aria-hidden="true"
                          >
                            <path
                              d="m9 6 6 6-6 6"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                          <CountBadge
                            count={event.anonymous_host_feedback_unread_count}
                            className="right-10 top-1/2 -translate-y-1/2"
                          />
                        </button>
                      ) : null}
                    </div>
                  );
                })}
                {canShowMoreEvents ? (
                  <AutoLoadMoreSentinel
                    onIntersect={() =>
                      setVisibleEventsCount((current) =>
                        Math.min(current + 6, sortedEvents.length)
                      )
                    }
                  />
                ) : null}
              </div>
            )}
          </>
        )}

        {activeTab === "payments" && (
          <>
            <SectionTitle className="mt-5">{copy.sections.payments}</SectionTitle>
            <div className="grid gap-4">
              <button
                type="button"
                onClick={() => navigate("/host-guide")}
                className="flex w-full items-center justify-between gap-4 rounded-[22px] border border-cyan-300/30 bg-cyan-300/[0.08] px-5 py-4 text-left text-white transition hover:bg-cyan-300/[0.14]"
              >
                <span>
                  <span className="block text-base font-bold">{copy.payments.hostGuideTitle}</span>
                  <span className="mt-1 block text-sm leading-5 text-white/70">{copy.payments.hostGuideBody}</span>
                  <span className="mt-2 block text-sm font-semibold text-cyan-200">{copy.payments.hostGuideCta} →</span>
                </span>
              </button>
              <div className="rounded-[26px] border border-white/12 bg-white/[0.05] px-5 py-5 shadow-[0_20px_40px_rgba(0,0,0,0.24)]">
                <p className="max-w-2xl text-[0.95rem] leading-[1.5] text-white/70">
                  {copy.payments.body}
                </p>

                {paidHostAccessApproved ? (
                  <div className="mt-4 rounded-[18px] border border-cyan-300/25 bg-cyan-300/[0.07] px-4 py-4">
                    <div className="text-[0.75rem] font-bold uppercase tracking-[0.14em] text-cyan-200/80">
                      {settlementCopy.offerTitle}
                    </div>
                    <div className="mt-1 text-base font-semibold text-white">
                      {approvedSettlementMode === "direct" ? settlementCopy.direct : settlementCopy.hold}
                    </div>
                    <p className="mt-2 text-sm leading-6 text-white/74">
                      {manualBankEnabled ? manualBankCopy.detail : approvedSettlementMode === "direct" ? settlementCopy.directDetail : settlementCopy.holdDetail}
                    </p>
                    <p className="mt-2 text-sm leading-6 text-white/74">
                      {approvedPaymentOffer.platform_commission_waived
                        ? waiverEnd
                          ? settlementCopy.freeUntil.replace("{{date}}", waiverEnd)
                          : settlementCopy.freeOpen
                        : settlementCopy.commission}
                    </p>
                    {approvedSettlementMode === "direct" && !manualBankEnabled && stripeConnected && stripe?.payout_schedule_interval === "manual" ? (
                      <p className="mt-3 rounded-[14px] border border-amber-300/22 bg-amber-300/[0.08] px-3 py-2 text-sm leading-6 text-amber-50/90">
                        {settlementCopy.manualScheduleNotice}
                      </p>
                    ) : null}
                  </div>
                ) : null}

                {!paymentsFlowComplete ? (
                  <div className="mt-4">
                    <div className="text-[0.78rem] font-semibold uppercase tracking-[0.14em] text-white/56">
                      {copy.payments.stepsTitle}
                    </div>
                    <div className="mt-3 space-y-2">
                      {paymentsFlowSteps.map((step, index) => (
                        <div
                          key={`${step.label}-${index}`}
                          className={`flex items-start justify-between gap-3 rounded-[16px] px-4 py-3 text-[0.92rem] leading-[1.5] transition ${
                            step.complete
                              ? "border-transparent bg-white/[0.02] text-white/42"
                              : step.locked
                                ? "border border-white/[0.06] bg-white/[0.02] text-white/42"
                              : "border border-white/10 bg-white/[0.04] text-white/82"
                          }`}
                        >
                          <span>{step.label}</span>
                          {step.complete ? (
                            <span className="shrink-0 text-[1rem] leading-none opacity-90">✅</span>
                          ) : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}

                {paymentsActionError ? (
                  <div className="mt-4 rounded-[18px] border border-amber-300/18 bg-amber-400/10 px-4 py-3 text-[0.9rem] leading-[1.45] text-amber-50/90">
                    {paymentsActionError}
                  </div>
                ) : null}
                {paymentsActionNotice ? (
                  <div className="mt-4 rounded-[18px] border border-[#69d6ff]/18 bg-[#69d6ff]/10 px-4 py-3 text-[0.9rem] leading-[1.45] text-[#dff8ff]">
                    {paymentsActionNotice}
                  </div>
                ) : null}

                <div className="mt-5 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={openCommercialConditions}
                    className="inline-flex h-11 items-center justify-center rounded-[16px] border border-white/12 bg-white/[0.05] px-5 text-[0.94rem] font-semibold text-white/82 transition hover:bg-white/[0.08]"
                  >
                    {copy.payments.commercialCta}
                  </button>

                  {!verificationStepComplete ? (
                    <button
                      type="button"
                      disabled={hasOpenPaidHostRequest}
                      onClick={() => {
                        setPaymentsActionError("");
                        setPaidHostRequestOpen(true);
                      }}
                      className={`inline-flex h-11 items-center justify-center rounded-[16px] px-5 text-[0.94rem] font-semibold transition ${
                        hasOpenPaidHostRequest
                          ? "cursor-not-allowed border border-white/12 bg-white/[0.05] text-white/42 opacity-70"
                          : "bg-[linear-gradient(90deg,#2F67E7_0%,#2FD8F6_100%)] text-white shadow-[0_14px_26px_rgba(47,116,232,0.28)]"
                      }`}
                    >
                      {hasOpenPaidHostRequest
                        ? copy.payments.requestPendingCta
                        : copy.payments.requestAccessCta}
                    </button>
                  ) : null}

                  {paidHostAccessApproved ? (
                    <button
                      type="button"
                      onClick={openPaidHostAgreement}
                      className={`inline-flex h-11 items-center justify-center rounded-[16px] px-5 text-[0.94rem] font-semibold text-white ${agreementStepComplete
                        ? "border border-white/20 bg-white/[0.08]"
                        : "bg-[linear-gradient(90deg,#2F67E7_0%,#2FD8F6_100%)] shadow-[0_14px_26px_rgba(47,116,232,0.28)]"}`}
                    >
                      {agreementStepComplete ? copy.payments.reviewAgreementCta : copy.payments.agreementCta}
                    </button>
                  ) : null}

                  {isTBankPayments && tbankRequiresRecipient && paidHostAccessApproved && agreementStepComplete && !tbankRecipient ? (
                    <div className="flex flex-wrap gap-3">
                      <label className="sr-only" htmlFor="tbank-recipient-type">
                        {copy.payments.tbankRecipientTypeLabel}
                      </label>
                      <select
                        id="tbank-recipient-type"
                        value={tbankRecipientType}
                        onChange={(event) => setTBankRecipientType(event.target.value)}
                        className="h-11 rounded-[16px] border border-white/12 bg-[#121827] px-4 text-[0.94rem] font-semibold text-white/88 outline-none"
                      >
                        {Object.entries(copy.payments.tbankRecipientTypes).map(([value, label]) => (
                          <option key={value} value={value}>{label}</option>
                        ))}
                      </select>
                      <button
                        type="button"
                        disabled={stripeActionBusy === "tbank-create"}
                        onClick={createTBankRecipient}
                        className="inline-flex h-11 items-center justify-center rounded-[16px] bg-[linear-gradient(90deg,#2F67E7_0%,#2FD8F6_100%)] px-5 text-[0.94rem] font-semibold text-white shadow-[0_14px_26px_rgba(47,116,232,0.28)] disabled:cursor-wait disabled:opacity-70"
                      >
                        {stripeActionBusy === "tbank-create"
                          ? copy.payments.refreshing
                          : copy.payments.tbankCreateProfileCta}
                      </button>
                    </div>
                  ) : null}

                  {isTBankPayments && tbankRequiresRecipient && paidHostAccessApproved && agreementStepComplete ? (
                    <button
                      type="button"
                      disabled={stripeActionBusy === "tbank"}
                      onClick={refreshTBankRecipient}
                      className="inline-flex h-11 items-center justify-center rounded-[16px] border border-white/12 bg-white/[0.05] px-5 text-[0.94rem] font-semibold text-white/88 transition hover:bg-white/[0.08] disabled:cursor-wait disabled:opacity-70"
                    >
                      {stripeActionBusy === "tbank"
                        ? copy.payments.refreshing
                        : copy.payments.refreshCta}
                    </button>
                  ) : null}

                  {canManageStripe && !stripeConnected ? (
                    <button
                      type="button"
                      disabled={stripeActionBusy === "connect"}
                      onClick={openStripeCountrySelection}
                      className="inline-flex h-11 items-center justify-center gap-2 rounded-[16px] bg-[linear-gradient(90deg,#2F67E7_0%,#2FD8F6_100%)] px-5 text-[0.94rem] font-semibold text-white shadow-[0_14px_26px_rgba(47,116,232,0.28)] disabled:cursor-wait disabled:opacity-70"
                    >
                      {stripeActionBusy === "connect" ? (
                        copy.payments.opening
                      ) : (
                        copy.payments.connectCta
                      )}
                    </button>
                  ) : null}

                  {canManageStripe && stripeConnected && !stripeStepComplete && !stripeReviewPending ? (
                    <button
                      type="button"
                      disabled={stripeActionBusy === "connect"}
                      onClick={() => startStripeConnect({ country: stripe?.country || stripeConnectCountry })}
                      className="inline-flex h-11 items-center justify-center gap-2 rounded-[16px] bg-[linear-gradient(90deg,#2F67E7_0%,#2FD8F6_100%)] px-5 text-[0.94rem] font-semibold text-white shadow-[0_14px_26px_rgba(47,116,232,0.28)] disabled:cursor-wait disabled:opacity-70"
                    >
                      {stripeActionBusy === "connect" ? (
                        copy.payments.opening
                      ) : (
                        copy.payments.continueCta
                      )}
                    </button>
                  ) : null}

                  {canManageStripe && stripeStepComplete && !billingReady ? (
                    <button
                      type="button"
                      onClick={openPaidHostBilling}
                      className="inline-flex h-11 items-center justify-center rounded-[16px] bg-[linear-gradient(90deg,#2F67E7_0%,#2FD8F6_100%)] px-5 text-[0.94rem] font-semibold text-white shadow-[0_14px_26px_rgba(47,116,232,0.28)]"
                    >
                      {copy.payments.billingCta}
                    </button>
                  ) : null}

                  {canManageStripe && (stripeStepComplete || stripeReviewPending) && !paymentsFlowComplete ? (
                    <button
                      type="button"
                      disabled={stripeActionBusy === "dashboard"}
                      onClick={openStripeDashboard}
                      className="inline-flex h-11 items-center justify-center rounded-[16px] border border-white/12 bg-white/[0.05] px-5 text-[0.94rem] font-semibold text-white/88 transition hover:bg-white/[0.08] disabled:opacity-70"
                    >
                      {stripeActionBusy === "dashboard" ? copy.payments.opening : copy.payments.dashboardCta}
                    </button>
                  ) : null}

                  {paymentsFlowComplete && !isTBankPayments ? (
                    <button
                      type="button"
                      disabled={stripeActionBusy === "dashboard"}
                      onClick={openStripeDashboard}
                      className="inline-flex h-11 items-center justify-center rounded-[16px] border border-white/12 bg-white/[0.05] px-5 text-[0.94rem] font-semibold text-white/88 transition hover:bg-white/[0.08] disabled:opacity-70"
                    >
                      {stripeActionBusy === "dashboard" ? copy.payments.opening : copy.payments.dashboardCta}
                    </button>
                  ) : null}
                </div>

                <p className="mt-4 max-w-2xl text-[0.9rem] leading-[1.45] text-white/52">
                  {isTBankPayments && agreementStepComplete
                    ? tbankReadyForOnlineTickets
                      ? tbankDirectAcquiringReady
                        ? copy.payments.tbankDirectReadyHelp
                        : copy.payments.tbankReadyHelp
                      : tbankRecipient
                        ? copy.payments.tbankPendingHelp
                        : copy.payments.tbankMissingHelp
                  : paymentsFlowComplete
                    ? manualBankEnabled
                      ? manualBankCopy.detail
                      : approvedSettlementMode === "direct"
                      ? settlementCopy.directDetail
                      : copy.payments.managementHelp
                    : stripeReviewPending
                      ? `${billingReady ? `${copy.payments.billingSavedHelp} ` : ""}${copy.payments.stripeReviewHelp}`
                    : canManageStripe && stripeStepComplete && !billingReady
                      ? copy.payments.billingHelp
                    : stripeStepComplete
                      ? copy.payments.stripeConnectedHelp
                    : stripeConnected
                      ? copy.payments.stripeOnboardingHelp
                    : paidHostAccessApproved && !agreementStepComplete
                      ? agreementNeedsRenewal
                        ? copy.payments.agreementUpdatedHelp
                        : copy.payments.approvedHelp
                    : paidHostAccessApproved && agreementStepComplete
                      ? copy.payments.agreementHelp
                    : paidHostAccessApproved
                      ? copy.payments.approvedHelp
                    : copy.payments.requestHelp}
                </p>
              </div>

              {canViewPaymentDetails ? (
                <>
                  {manualBankEnabled ? (
                    <div className="mb-5 rounded-[22px] border border-[#69d6ff]/25 bg-[#69d6ff]/[0.07] px-5 py-5">
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <h3 className="text-[1rem] font-semibold text-white">{manualBankCopy.title}</h3>
                        <button type="button" onClick={refreshManualBankBalance} disabled={manualBankLoading || stripeActionBusy === "manual-bank-payout"} className="text-[0.86rem] font-semibold text-[#a5ecff] disabled:opacity-50">
                          {manualBankCopy.refresh}
                        </button>
                      </div>
                      <p className="mt-2 text-[0.88rem] leading-6 text-white/67">{manualBankCopy.detail}</p>
                      {manualBankPaused ? <p className="mt-2 text-[0.88rem] text-amber-100">{manualBankCopy.paused}</p> : null}
                      <div className="mt-4 grid grid-cols-2 gap-3 md:grid-cols-4">
                        <MetricCard label={manualBankCopy.available} value={formatMoney(manualBankSnapshot?.available, "EUR")} />
                        <MetricCard label={manualBankCopy.pending} value={formatMoney(manualBankSnapshot?.pending, "EUR")} />
                        <MetricCard label={manualBankCopy.fee} value={formatMoney(manualBankSnapshot?.reserved_for_fees, "EUR")} />
                        <MetricCard label={manualBankCopy.withdrawable} value={formatMoney(manualBankSnapshot?.withdrawable, "EUR")} />
                      </div>
                      <button type="button" onClick={startManualBankPayout} disabled={manualBankLoading || stripeActionBusy === "manual-bank-payout" || manualBankPaused || Boolean(manualBankSnapshot?.blocked) || numberValue(manualBankSnapshot?.withdrawable) <= 0} className="mt-4 inline-flex h-11 items-center justify-center rounded-[16px] bg-[linear-gradient(90deg,#2F67E7_0%,#2FD8F6_100%)] px-5 text-[0.94rem] font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">
                        {stripeActionBusy === "manual-bank-payout" ? manualBankCopy.working : `${manualBankCopy.button} · ${formatMoney(manualBankSnapshot?.withdrawable, "EUR")}`}
                      </button>
                    </div>
                  ) : null}
                  {billingStepComplete ? (
                    <>
                      <SectionTitle>{copy.sections.payouts}</SectionTitle>
                      <div className="grid grid-cols-2 gap-3">
                        {paymentCards.map((card) => (
                          <MetricCard key={card.label} label={card.label} value={card.value} />
                        ))}
                      </div>
                    </>
                  ) : null}
                  <SectionTitle>{copy.sections.paidEvents}</SectionTitle>
                  {loadingPanel && paidEvents.length === 0 ? (
                    <HostPanelEventListSkeleton rows={2} />
                  ) : paidEvents.length === 0 ? (
                    <div className="rounded-[22px] border border-white/10 bg-white/[0.03] px-6 py-5 shadow-[0_16px_28px_rgba(0,0,0,0.14)]">
                      <p className="text-[0.92rem] leading-[1.5] text-white/58">
                        {copy.payments.paidEventsEmpty}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {paidEvents.map((event) => {
                        const paymentSummary = event.payment_summary || {};
                        const eventCurrency = paymentSummary.currency || summaryCurrency;
                        const payoutProviderReference =
                          paymentSummary.payout_provider_reference || paymentSummary.payout_stripe_id;
                        const payoutProvider = String(
                          paymentSummary.payout_provider || (paymentSummary.payout_stripe_id ? "stripe" : "")
                        ).toLowerCase();
                        const eventId = Number(event.event_id);
                        const isExpanded = expandedPaidEventIds.has(eventId);
                        const recentDepositsCount = numberValue(paymentSummary.recent_deposits_count);
                        const payments = Array.isArray(paymentSummary.payments)
                          ? paymentSummary.payments
                          : [];
                        const cancellationPolicy = String(
                          paymentSummary.cancellation_policy || event.host_paid_cancellation_policy || ""
                        ).toLowerCase();
                        const cancellationLabel =
                          copy.payments.cancellationPolicies[cancellationPolicy] ||
                          cancellationPolicy ||
                          "-";
                        const balanceDueDays = paymentSummary.balance_due_days_before;
                        const payoutStatus = String(paymentSummary.payout_status || "").toLowerCase();
                        const eventSettlementMode = String(
                          paymentSummary.settlement_mode || event.settlement_mode || "event_hold"
                        ).toLowerCase();
                        const commissionInvoice = paymentSummary.commission_invoice || null;
                        const displayedCommissionAmount = eventSettlementMode === "direct" && (commissionInvoice?.invoice_voided || commissionInvoice?.adjustment_required)
                          ? commissionInvoice.amount_due ?? 0
                          : paymentSummary.platform_commission_amount;
                        const payoutStatusLabel =
                          copy.payments.payoutStatuses?.[payoutStatus] || payoutStatus;
                        const payoutStatusDetail = payoutStatus === "released"
                          ? (payoutProvider === "stripe"
                              ? copy.payments.payoutReleasedAt
                              : copy.payments.payoutReleasedGeneric).replace(
                              "{{date}}",
                              formatPaymentDate(paymentSummary.payout_released_at) || "-"
                            )
                          : payoutStatus === "processing"
                            ? payoutProvider === "stripe"
                              ? copy.payments.payoutProcessingDetail
                              : copy.payments.payoutProcessingGeneric
                          : paymentSummary.payout_release_after_at
                            ? copy.payments.payoutReleaseAfter.replace(
                                "{{date}}",
                                formatPaymentDate(paymentSummary.payout_release_after_at) || "-"
                              )
                            : "";
                        const stripeFundsAvailableDetail =
                          payoutStatus === "scheduled" && paymentSummary.stripe_funds_available_at
                          ? copy.payments.payoutStripeFundsAvailable.replace(
                              "{{date}}",
                              formatPaymentDate(paymentSummary.stripe_funds_available_at) || "-"
                            )
                          : "";
                        return (
                          <div
                            key={`paid-${event.event_id}`}
                            ref={(node) => {
                              if (!node) {
                                paidEventRefs.current.delete(eventId);
                                return;
                              }
                              paidEventRefs.current.set(eventId, node);
                            }}
                            className={`relative mb-5 rounded-2xl border bg-[#13233b] shadow-[0_10px_24px_rgba(0,0,0,0.18)] transition ${
                              Number(initialFocusEventId) === eventId
                                ? "border-[#69d6ff]/70"
                                : "border-white/75"
                            }`}
                          >
                            <CountBadge count={recentDepositsCount} className="right-4 top-2 z-20" />
                            <button
                              type="button"
                              className="block w-full text-left"
                              onClick={() => isExpanded ? openPaidEvent(event) : handleTogglePaidEventDetails(eventId)}
                              aria-expanded={isExpanded}
                              aria-controls={`paid-event-details-${eventId}`}
                              aria-label={`${isExpanded ? copy.payments.openEvent : copy.payments.viewDetails}: ${event.name}`}
                            >
                              <EventCard
                                title={event.name}
                                avatar={null}
                                type={event.type || "event"}
                                isOnline={Number(event.event_format) === 0}
                                isPersonal={Number(event.host_format) === 0}
                                isPaid={Boolean(event.is_paid)}
                                isByRequest={Boolean(event.join_by_request)}
                                minVaultLevel={event.min_vault_level}
                                datetime={event.date_time}
                                datetimeDisplayMode="date_only"
                                isVerified={Boolean(event.is_verified)}
                                isValidated={Boolean(event.is_validated)}
                                isFavourite={false}
                                onToggleFavourite={() => {}}
                                status={event.status || "active"}
                                badgeLabel={event.badge_label || null}
                                iconImageUrl={event.icon_image_url}
                                showFavouriteButton={false}
                                suppressStatusOverlay={false}
                                cardClassName="!border-transparent !bg-transparent shadow-none"
                                titleMaxWidthClass="max-w-[190px] md:max-w-[300px]"
                              />
                            </button>
                            <div id={`paid-event-details-${eventId}`} className={`grid transition-all duration-200 ease-out ${isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                              <div className="overflow-hidden">
                                {isExpanded ? (
                                  <div className="border-t border-white/10 px-4 pb-5 pt-4">
                                    <p className="mt-3 rounded-[16px] border border-white/10 bg-white/[0.04] px-4 py-3 text-sm leading-6 text-white/76">
                                      {manualBankEnabled ? manualBankCopy.detail : eventSettlementMode === "direct" ? settlementCopy.eventDirect : settlementCopy.eventHold}
                                    </p>
                                    {eventSettlementMode === "direct" && commissionInvoice ? (
                                      <div className="mt-3 rounded-[16px] border border-cyan-300/20 bg-cyan-300/[0.06] px-4 py-3 text-sm leading-6 text-white/80">
                                        <div className="font-semibold text-white">
                                          {commissionInvoice.adjustment_required
                                            ? settlementCopy.invoiceAdjusting
                                            : commissionInvoice.invoice_voided
                                            ? settlementCopy.invoiceVoided
                                            : commissionInvoice.payment_status === "paid"
                                              ? settlementCopy.invoicePaid
                                              : settlementCopy.invoiceUnpaid}
                                          {commissionInvoice.invoice_number ? ` · ${commissionInvoice.invoice_number}` : ""}
                                        </div>
                                        <div className="mt-1">
                                          {formatMoney(commissionInvoice.invoice_voided || commissionInvoice.adjustment_required
                                            ? commissionInvoice.amount_due ?? 0
                                            : commissionInvoice.total_amount, commissionInvoice.currency || eventCurrency)}
                                          {commissionInvoice.due_at && commissionInvoice.payment_status !== "paid" && !commissionInvoice.invoice_voided && !commissionInvoice.adjustment_required
                                            ? ` · ${settlementCopy.invoiceDue.replace("{{date}}", formatPaymentDate(commissionInvoice.due_at))}`
                                            : ""}
                                        </div>
                                        {commissionInvoice.adjustment_required ? (
                                          <p className="mt-2 text-white/70">{settlementCopy.invoiceAdjustingDetail}</p>
                                        ) : null}
                                        {commissionInvoice.payment_status !== "paid" && !commissionInvoice.invoice_voided && !commissionInvoice.adjustment_required ? (
                                          <button
                                            type="button"
                                            onClick={() => payCommissionInvoice(commissionInvoice.id)}
                                            disabled={invoiceCheckoutPendingId != null}
                                            className="mt-3 rounded-xl bg-cyan-300 px-4 py-2 font-semibold text-slate-950 disabled:opacity-60"
                                          >
                                            {invoiceCheckoutPendingId === commissionInvoice.id ? settlementCopy.payingInvoice : settlementCopy.payInvoice}
                                          </button>
                                        ) : null}
                                      </div>
                                    ) : null}
                                    {payoutStatus && eventSettlementMode !== "direct" ? (
                                      <div className="mt-4 rounded-[18px] border border-[#69d6ff]/25 bg-[#69d6ff]/[0.08] px-4 py-3">
                                        <div className="flex flex-wrap items-center justify-between gap-2">
                                          <div className="flex items-center gap-2">
                                            <span className="text-[0.72rem] font-extrabold uppercase tracking-[0.16em] text-white/60">
                                              {copy.payments.payoutStatusTitle}
                                            </span>
                                            <button
                                              type="button"
                                              onClick={(clickEvent) => {
                                                clickEvent.stopPropagation();
                                                setPayoutInfoEvent({
                                                  eventId,
                                                  paymentSummary,
                                                });
                                              }}
                                              className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-[#69d6ff]/45 bg-[#69d6ff]/10 text-[0.78rem] font-extrabold text-[#a5ecff] transition hover:bg-[#69d6ff]/20"
                                              aria-label={copy.payments.payoutInfoAria}
                                            >
                                              ?
                                            </button>
                                          </div>
                                          <span className="rounded-full border border-[#69d6ff]/40 bg-[#69d6ff]/15 px-3 py-1 text-[0.78rem] font-bold text-white">
                                            {payoutStatusLabel}
                                          </span>
                                        </div>
                                        {payoutStatusDetail ? (
                                          <div className="mt-2 text-[0.86rem] text-white/78">{payoutStatusDetail}</div>
                                        ) : null}
                                        {stripeFundsAvailableDetail ? (
                                          <div className="mt-1 text-[0.8rem] leading-[1.45] text-white/56">
                                            {stripeFundsAvailableDetail}
                                          </div>
                                        ) : null}
                                        {payoutStatus === "blocked" || payoutStatus === "failed" ? (
                                          <div className="mt-2 text-[0.82rem] leading-[1.45] text-amber-100/85">
                                            {copy.payments.payoutBlockedHint}
                                          </div>
                                        ) : null}
                                        {payoutProviderReference ? (
                                          <div className="mt-2 text-[0.76rem] text-white/48">
                                            {(payoutProvider === "stripe"
                                              ? copy.payments.payoutStripeReference
                                              : copy.payments.payoutProviderReference).replace(
                                              "{{id}}",
                                              payoutProviderReference
                                            )}
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : null}
                                    <div className="mt-4 flex items-center justify-between gap-3">
                                      <div className="text-[0.78rem] font-extrabold uppercase tracking-[0.16em] text-white/72">
                                        {copy.payments.metricsTitle}
                                      </div>
                                      <button
                                        type="button"
                                        onClick={() => {
                                          setPaymentMetricsHelpMode(eventSettlementMode);
                                          setPaymentMetricsHelpOpen(true);
                                        }}
                                        className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-[#69d6ff]/45 bg-[#69d6ff]/10 text-base font-extrabold text-[#a5ecff] transition hover:bg-[#69d6ff]/20"
                                        aria-label={copy.payments.metricHelpAria}
                                      >
                                        ?
                                      </button>
                                    </div>
                                    <div className="mt-3 grid grid-cols-2 gap-3">
                                      <MetricCard label={copy.metrics.paidTicketsSold} value={formatNumber(paymentSummary.paid_tickets_sold)} />
                                      <MetricCard label={copy.metrics.grossRevenue} value={formatMoney(paymentSummary.gross_revenue, eventCurrency)} />
                                      <MetricCard label={copy.metrics.depositPayments} value={formatNumber(paymentSummary.deposit_payments_count)} />
                                      <MetricCard label={copy.metrics.depositRevenue} value={formatMoney(paymentSummary.deposit_revenue, eventCurrency)} />
                                      <MetricCard label={copy.metrics.refundedTickets} value={formatNumber(paymentSummary.refunded_tickets_count)} />
                                      <MetricCard label={copy.metrics.refundedAmount} value={formatMoney(paymentSummary.refunded_amount, eventCurrency)} />
                                      {eventSettlementMode !== "direct" ? (
                                        <>
                                          <MetricCard label={copy.metrics.pendingPayout} value={formatMoney(paymentSummary.pending_payout, eventCurrency)} />
                                          <MetricCard label={copy.metrics.releasedBalance} value={formatMoney(paymentSummary.released_payouts, eventCurrency)} />
                                        </>
                                      ) : null}
                                    </div>
                                    {(eventSettlementMode === "direct" && !commissionInvoice && !manualBankEnabled) ||
                                    numberValue(displayedCommissionAmount) > 0 ||
                                    (eventSettlementMode === "direct" && numberValue(manualBankEnabled ? paymentSummary.organizer_processing_fee_amount : paymentSummary.stripe_processing_fee_total) > 0) ||
                                    (eventSettlementMode !== "direct" && numberValue(paymentSummary.organizer_processing_fee_amount) > 0) ? (
                                      <div className="mt-3 rounded-[18px] border border-white/10 bg-black/10 px-4 py-3 text-[0.86rem] text-white/72">
                                        <div className="mb-2 text-[0.72rem] font-bold uppercase tracking-[0.12em] text-white/52">
                                          {copy.payments.chargesTitle}
                                        </div>
                                        {((eventSettlementMode === "direct" && !commissionInvoice && !manualBankEnabled) || numberValue(displayedCommissionAmount) > 0) ? (
                                          <div className="flex justify-between gap-3 py-1">
                                            <span>{eventSettlementMode === "direct" ? settlementCopy.commissionEstimate : copy.metrics.platformFeeRetained}</span>
                                            <span className="shrink-0 font-bold text-white">
                                              {eventSettlementMode === "direct" && !commissionInvoice
                                                ? settlementCopy.commissionPending
                                                : formatMoney(displayedCommissionAmount, eventCurrency)}
                                            </span>
                                          </div>
                                        ) : null}
                                        {eventSettlementMode !== "direct" && numberValue(paymentSummary.organizer_processing_fee_amount) > 0 ? (
                                          <div className="flex justify-between gap-3 py-1">
                                            <span>{copy.metrics.organizerProcessingTariff}</span>
                                            <span className="shrink-0 font-bold text-white">{formatMoney(paymentSummary.organizer_processing_fee_amount, eventCurrency)}</span>
                                          </div>
                                        ) : null}
                                        {eventSettlementMode === "direct" && numberValue(manualBankEnabled ? paymentSummary.organizer_processing_fee_amount : paymentSummary.stripe_processing_fee_total) > 0 ? (
                                          <div className="flex justify-between gap-3 py-1">
                                            <span>{manualBankEnabled ? manualBankCopy.fee : settlementCopy.stripeFee}</span>
                                            <span className="shrink-0 font-bold text-white">{formatMoney(manualBankEnabled ? paymentSummary.organizer_processing_fee_amount : paymentSummary.stripe_processing_fee_total, eventCurrency)}</span>
                                          </div>
                                        ) : null}
                                      </div>
                                    ) : null}
                                    {String(event.host_payment_collection_method || "").toLowerCase() === "cash" ||
                                    numberValue(paymentSummary.cash_deposit_reserved) > 0 ||
                                    numberValue(paymentSummary.cash_commission_settled) > 0 ? (
                                      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-4">
                                        <MetricCard
                                          label={copy.metrics.cashDepositReserved}
                                          value={formatMoney(paymentSummary.cash_deposit_reserved, eventCurrency)}
                                        />
                                        <MetricCard
                                          label={copy.metrics.cashCommissionSettled}
                                          value={formatMoney(paymentSummary.cash_commission_settled, eventCurrency)}
                                        />
                                        <MetricCard
                                          label={copy.metrics.cashDepositReleased}
                                          value={formatMoney(paymentSummary.cash_deposit_released, eventCurrency)}
                                        />
                                        <MetricCard
                                          label={copy.metrics.cashOutstanding}
                                          value={formatMoney(paymentSummary.cash_outstanding_amount, eventCurrency)}
                                        />
                                      </div>
                                    ) : null}
                                    <div className="mt-4 rounded-[20px] border border-white/10 bg-black/10 px-4 py-4">
                                      <div className="mb-3 text-[0.78rem] font-extrabold uppercase tracking-[0.16em] text-white/72">
                                        {copy.payments.paymentTermsTitle}
                                      </div>
                                      <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                                        <PaymentInfoPill
                                          label={copy.payments.ticketPrice}
                                          value={formatMoney(paymentSummary.ticket_price, eventCurrency)}
                                        />
                                        <PaymentInfoPill
                                          label={copy.payments.upfrontPayment}
                                          value={`${formatMoney(paymentSummary.deposit_amount_per_ticket, eventCurrency)} · ${formatNumber(paymentSummary.join_payment_percent)}%`}
                                        />
                                        <PaymentInfoPill
                                          label={copy.payments.remainingPayment}
                                          value={formatMoney(paymentSummary.remaining_amount_per_ticket, eventCurrency)}
                                        />
                                        <PaymentInfoPill
                                          label={copy.payments.balanceDue}
                                          value={
                                            balanceDueDays
                                              ? copy.payments.balanceDueDays.replace("{{days}}", formatNumber(balanceDueDays))
                                              : copy.payments.noBalanceDue
                                          }
                                        />
                                        <PaymentInfoPill
                                          label={copy.payments.cancellationPolicy}
                                          value={cancellationLabel}
                                        />
                                        <PaymentInfoPill
                                          label={copy.payments.lastPayment}
                                          value={formatPaymentDate(paymentSummary.last_paid_at) || "-"}
                                        />
                                      </div>
                                    </div>
                                      <div className="mt-4">
                                        <div className="mb-3 text-[0.78rem] font-extrabold uppercase tracking-[0.16em] text-white/72">
                                          {copy.payments.paidByTitle}
                                        </div>
                                        {payments.length > 0 ? (
                                          <div className="space-y-2">
                                            {payments.map((payment) => (
                                              <PaymentUserRow
                                                key={payment.ticket_key || payment.payment_id || `${eventId}-${payment.user_id}`}
                                                payment={payment}
                                                copy={copy}
                                                settlementMode={eventSettlementMode}
                                              />
                                            ))}
                                          </div>
                                        ) : (
                                          <div className="rounded-[18px] border border-white/10 bg-white/[0.04] px-4 py-4 text-[0.9rem] text-white/58">
                                            {copy.payments.noPaidParticipants}
                                          </div>
                                        )}
                                      </div>
                                  </div>
                                ) : null}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleTogglePaidEventDetails(eventId)}
                              aria-expanded={isExpanded}
                              aria-controls={`paid-event-details-${eventId}`}
                              aria-label={`${isExpanded ? copy.payments.hideDetails : copy.payments.viewDetails}: ${event.name}`}
                              className="absolute bottom-0 left-1/2 z-20 flex h-5 min-w-[40px] -translate-x-1/2 translate-y-1/2 items-center justify-center rounded-full border border-white/75 bg-[#13233b] px-3 text-white shadow-[0_10px_20px_rgba(0,0,0,0.2)] transition"
                            >
                              <svg width="12" height="6" viewBox="0 0 12 6" fill="none" aria-hidden="true" className={`transition-transform duration-200 ${isExpanded ? "rotate-180" : ""}`}>
                                <path d="M1 1L6 5L11 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                              </svg>
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </>
              ) : null}
            </div>
          </>
        )}
          </>
        )}
      </div>

      <OrganizerUpsellModal
        copy={copy}
        open={limitUpsellOpen}
        onClose={() => setLimitUpsellOpen(false)}
        onOpenPremium={openPremiumPage}
        title={modalPresentation.title}
        body={modalPresentation.body}
        usedSlots={usedCreationSlots}
        totalSlots={maxTotalCreations}
      />
      <HostPaidAccessRequestModal
        copy={copy}
        open={paidHostRequestOpen}
        onClose={() => setPaidHostRequestOpen(false)}
        form={paidHostRequestForm}
        onChange={(field, value) => {
          setPaymentsActionError("");
          setPaidHostRequestForm((current) => ({
            ...current,
            [field]: value,
          }));
        }}
        onSubmit={submitPaidHostAccessRequest}
        submitting={paidHostRequestSubmitting}
        error={paymentsActionError}
        countryOptions={stripeCountryOptions}
        onOpenVerificationTerms={openVerificationTerms}
        onOpenCommercialConditions={openCommercialConditions}
      />
      <StripeCountrySelectionModal
        copy={copy}
        open={stripeCountryModalOpen}
        onClose={() => setStripeCountryModalOpen(false)}
        countryOptions={stripeCountryOptions}
        value={stripeConnectCountry}
        reviewedCountry={reviewedStripeCountry}
        onChange={setStripeConnectCountry}
        onContinue={continueStripeCountrySelection}
        busy={stripeActionBusy === "connect"}
        error={paymentsActionError}
        showNewAccountNotice={approvedSettlementMode === "direct" && !stripeConnected}
      />
      <PaymentMetricsHelpModal
        open={paymentMetricsHelpOpen}
        onClose={() => setPaymentMetricsHelpOpen(false)}
        copy={copy}
        settlementCopy={manualBankEnabled ? {
          ...settlementCopy,
          stripeFee: manualBankCopy.fee,
          metricStripeFee: manualBankCopy.detail,
        } : settlementCopy}
        settlementMode={paymentMetricsHelpMode}
      />
      <PayoutInfoModal
        open={Boolean(payoutInfoEvent)}
        onClose={() => setPayoutInfoEvent(null)}
        copy={copy}
        paymentSummary={payoutInfoEvent?.paymentSummary || {}}
      />
      <HostFeedbackModal
        open={Boolean(feedbackEvent)}
        event={feedbackEvent}
        copy={copy}
        onClose={closeFeedbackModal}
        onSeen={handleFeedbackSeen}
      />
      {!hideProceedButton && !anyOverlayOpen ? (
        <div
          className="mt-8 w-full px-4 pb-[calc(24px+var(--vg-safe-area-bottom))] lg:flex lg:justify-center lg:px-8 xl:px-10"
        >
          <button
            onClick={() => onProceed?.(summary?.host_created)}
            className="w-full rounded-2xl border border-[#6ea7ff]/75 bg-[linear-gradient(180deg,#4a89ef_0%,#3b74dd_100%)] py-3 text-lg font-semibold text-white shadow-[0_12px_28px_rgba(46,112,238,0.42)] lg:w-[320px]"
          >
            {copy.buttons.backToCreation}
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default EventCreateStep0;
